import React, { useState, useEffect, useCallback, useRef } from "react";
import { GestionClienteModal } from './GestionCliente';

// Helper function to check if a record is assigned to the current user
const isRecordAssignedToUser = (record, user) => {
  if (!user || user.role === 'admin') return true;
  if (user.vendor_type === 'gerente') return true;

  if (user.role === 'vendedora') {
    // Build list of salesperson names this user can see
    const allowedNames = [];
    // supervised_salespeople applies to both supervisor AND individual (for multi-name matching)
    if (user.supervised_salespeople?.length > 0) {
      allowedNames.push(...user.supervised_salespeople.map(n => n.toUpperCase()));
    }
    if (user.zoho_name) {
      allowedNames.push(user.zoho_name.toUpperCase());
    }
    // Fallback: use user's display name
    if (allowedNames.length === 0) {
      allowedNames.push(user.name.toUpperCase());
    }

    // Remove duplicates
    const uniqueNames = [...new Set(allowedNames)];

    // Helper: check if value matches any allowed name
    const matchesAllowed = (value) => {
      if (!value) return false;
      const v = value.toUpperCase();
      return uniqueNames.some(name => v.includes(name));
    };

    // Check salesperson_name
    if (record.salesperson_name && matchesAllowed(record.salesperson_name)) return true;

    // Check custom_fields (cf_vendedor)
    if (record.custom_fields) {
      if (record.custom_fields.some(f => f.value && matchesAllowed(f.value))) return true;
    }

    // Check customer_data.custom_fields (enriched payments)
    if (record.customer_data?.custom_fields) {
      if (record.customer_data.custom_fields.some(f => f.value && matchesAllowed(f.value))) return true;
    }

    // Check contact_persons
    if (record.contact_persons) {
      if (record.contact_persons.some(p => matchesAllowed(`${p.salutation || ''} ${p.first_name || ''}`))) return true;
    }

    // Check customer_data.contact_persons
    if (record.customer_data?.contact_persons) {
      if (record.customer_data.contact_persons.some(p => matchesAllowed(`${p.salutation || ''} ${p.first_name || ''}`))) return true;
    }

    return false;
  }

  return true;
};

// Login Form Component
const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      if (data.success && data.user) {
        if (data.access_token) sessionStorage.setItem('access_token', data.access_token);
        onLogin(data.user);
      } else {
        setError(typeof data.detail === 'string' ? data.detail : 'Email o contraseña incorrectos.');
      }
    } catch (err) {
      setError('Error de conexion. Verifica tus credenciales.');
    }
    setLoading(false);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">VENEGE</h1>
          <p className="text-gray-600">Sistema de Ventas</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="usuario@venegeca.com" required data-testid="login-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="••••••••" required data-testid="login-password" />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50">
            {loading ? 'Iniciando sesion...' : 'Iniciar Sesion'}
          </button>
        </form>

        <div className="mt-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div>
            <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">o</span></div>
          </div>
          <button onClick={handleGoogleLogin} data-testid="google-login-btn"
            className="mt-4 w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-gray-700 font-medium">Iniciar con Google</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Icon Components
const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const MenuIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const HelpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const PackageIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const TruckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FileIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ShoppingCartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m-2.4 5l2 8h12M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6" />
  </svg>
);

const MapIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const CogIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const IntegrationIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const ActivityIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const FormIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const StoreIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const CarIcon = () => (
  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const AnalyticsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const TrendUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendDownIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </svg>
);

const StarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const CubeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const VenegeLogo = () => (
  <div className="flex items-center space-x-3">
    <div className="bg-red-600 rounded-lg p-2">
      <CarIcon />
    </div>
    <div>
      <h1 className="text-2xl font-bold text-white">VENEGE</h1>
      <p className="text-red-200 text-sm">Sistema de Ventas</p>
    </div>
  </div>
);

const LoadingSpinner = () => (
  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
);

const LoginPage = ({ onLogin }) => {
  return <LoginForm onLogin={onLogin} />;
};

// Sidebar Component
const Sidebar = ({ activeSection, setActiveSection, user, isOpen, onClose }) => {
  // All possible menu items
  const allMenuItems = [
    { id: 'dashboard', label: 'Panel Integral', icon: <ChartIcon /> },
    { id: 'analyticsEmbed', label: 'Panel', icon: <ChartIcon /> },
    { id: 'salesOrders', label: 'Ordenes de Venta', icon: <ShoppingCartIcon /> },
    { id: 'clients', label: 'Clientes', icon: <UserIcon /> },
    { id: 'inventory', label: 'Inventario', icon: <PackageIcon /> },
    { id: 'invoices', label: 'Facturas', icon: <FileIcon /> },
    { id: 'payments', label: 'Pagos', icon: <DollarIcon /> },
    { id: 'creditNotes', label: 'Notas de Credito', icon: <FormIcon /> },
    { id: 'logistics', label: 'Logistica', icon: <TruckIcon /> },
    { id: 'driverDeliveries', label: 'Mis Entregas', icon: <TruckIcon /> },
    { id: 'users', label: 'Usuarios', icon: <UserIcon /> }
  ];

  // Build menu based on permissions
  let menuItems;
  const perms = user.permissions || [];

  if (user.role === 'admin' && perms.length === 0) {
    // Admin with no explicit permissions = full access
    menuItems = allMenuItems;
  } else if (perms.length > 0) {
    // Use permissions to filter menu
    menuItems = allMenuItems.filter(item => perms.includes(item.id));
  } else {
    // Fallback for users without permissions configured - use role defaults
    const roleDefaults = {
      'vendedora': ['analyticsEmbed'],
      'aprobador': ['payments'],
      'logistica': ['logistics', 'driverDeliveries'],
      'chofer': ['driverDeliveries']
    };
    const defaults = roleDefaults[user.role] || ['dashboard'];
    menuItems = allMenuItems.filter(item => defaults.includes(item.id));
  }

  const handleMenuClick = (sectionId) => {
    setActiveSection(sectionId);
    // Close mobile menu when item is selected
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed md:relative inset-y-0 left-0 z-50 
        bg-gray-900 text-white w-64 min-h-screen p-4
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        {/* Mobile close button */}
        <div className="md:hidden flex justify-end mb-4">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-8">
          <VenegeLogo />
        </div>

        <nav className="space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${activeSection === item.id
                  ? 'bg-red-600 text-white'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </>
  );
};

const LogoutIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

// Header Component
const Header = ({ user, onLogout, onToggleSidebar, containerRef }) => {
  return (
    <div ref={containerRef} className="bg-white border-b border-gray-200 px-4 md:px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={onToggleSidebar}
            className="md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <MenuIcon />
          </button>
          <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Panel de Control</h1>
        </div>
        <div className="flex items-center space-x-2 md:space-x-4">
          <div className="flex items-center space-x-2">
            <UserIcon />
            <span className="hidden sm:inline text-gray-700">{user.name}</span>
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
              {user.vendor_type === 'supervisor' ? 'Supervisor' :
               user.vendor_type === 'gerente' ? 'Gerente de Ventas' :
               user.role === 'admin' ? 'Administrador' : 
               user.role === 'vendedora' ? 'Vendedora' : 
               user.role === 'aprobador' ? 'Aprobador' :
               user.role === 'logistica' ? 'Logistica' :
               user.role === 'chofer' ? 'Chofer' : 'Usuario'}
            </span>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center space-x-2 px-2 md:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
            title="Cerrar Sesión"
          >
            <LogoutIcon />
            <span className="hidden sm:inline">Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// UNIFIED DASHBOARD COMPONENT - COMBINES BOTH DASHBOARDS
// ============================================================================


const KPICard = ({ title, value, change, changeType, icon, color = "blue" }) => {
  const colorClasses = {
    blue: "from-blue-600 to-blue-800",
    green: "from-green-600 to-green-800", 
    red: "from-red-600 to-red-800",
    yellow: "from-yellow-600 to-yellow-800",
    purple: "from-purple-600 to-purple-800"
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
            {title}
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            {value}
          </p>
          {change !== undefined && (
            <div className="flex items-center mt-2">
              {changeType === 'increase' ? (
                <TrendUpIcon />
              ) : changeType === 'decrease' ? (
                <TrendDownIcon />
              ) : null}
              <span className={`ml-1 text-sm font-medium ${
                changeType === 'increase' ? 'text-green-600' : 
                changeType === 'decrease' ? 'text-red-600' : 'text-gray-600'
              }`}>
                {change}%
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full bg-gradient-to-r ${colorClasses[color]} text-white`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Chart Container Component
const ChartContainer = ({ title, children, className = "" }) => {
  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="h-64 flex items-center justify-center">
        {children || (
          <div className="text-gray-500">
            <AnalyticsIcon />
            <p className="mt-2 text-sm">Gráfico en desarrollo</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Executive Dashboard Component
const ExecutiveDashboard = ({ user }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSalesperson, setSelectedSalesperson] = useState('');
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, selectedSalesperson]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Build query parameters
      const params = new URLSearchParams({
        date_from: dateRange.from,
        date_to: dateRange.to,
        user_role: user.role,
        user_name: user.name
      });
      
      // Add salesperson filter only for admin users
      if (user.role === 'admin' && selectedSalesperson) {
        params.append('salesperson', selectedSalesperson);
      }
      
      const response = await fetch(
        `${backendUrl}/api/analytics/executive-dashboard?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      const data = await response.json();
      if (data.success) {
        setDashboardData(data.data);
      } else {
        console.error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-VE').format(num || 0);
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <LoadingSpinner />
              <p className="mt-4 text-gray-600">Cargando dashboard ejecutivo...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const kpis = dashboardData?.kpis?.data?.[0] || {};
  const trends = dashboardData?.sales_trends?.data || [];
  const growth = dashboardData?.growth_metrics?.data || [];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">📊 Dashboard Ejecutivo</h1>
            <p className="text-gray-600 mt-2">
              Análisis ejecutivo del {dateRange.from} al {dateRange.to}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            {/* Salesperson filter - only for admin users */}
            {user.role === 'admin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
                <select
                  value={selectedSalesperson}
                  onChange={(e) => setSelectedSalesperson(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                >
                  <option value="">Todos los vendedores</option>
                  <option value="adriana">Adriana Martinez</option>
                  <option value="roilan">Roilan Narvaez</option>
                  <option value="maria">Maria Figuera</option>
                  <option value="carlos">Carlos Abril</option>
                  <option value="andrea">Andrea Manrique</option>
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
              />
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="Ingresos Totales"
            value={formatCurrency(kpis.total_revenue)}
            icon={<DollarIcon />}
            color="green"
          />
          <KPICard
            title="Órdenes Totales"
            value={formatNumber(kpis.total_orders)}
            icon={<ShoppingCartIcon />}
            color="blue"
          />
          <KPICard
            title="Clientes Activos"
            value={formatNumber(kpis.active_customers)}
            icon={<UsersIcon />}
            color="purple"
          />
          <KPICard
            title="Ticket Promedio"
            value={formatCurrency(kpis.avg_order_value)}
            icon={<ChartIcon />}
            color="yellow"
          />
        </div>

        {/* Performance por Vendedores */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
          <KPICard
            title="Adriana Martinez"
            value={`${formatNumber(kpis.adriana_orders)} órdenes`}
            change={formatCurrency(kpis.adriana_revenue)}
            icon={<StarIcon />}
            color="red"
          />
          <KPICard
            title="Roilan Narvaez"
            value={`${formatNumber(kpis.roilan_orders)} órdenes`}
            change={formatCurrency(kpis.roilan_revenue)}
            icon={<StarIcon />}
            color="blue"
          />
          <KPICard
            title="Maria Figuera (Oriente)"
            value={`${formatNumber(kpis.maria_orders)} órdenes`}
            change={formatCurrency(kpis.maria_revenue)}
            icon={<StarIcon />}
            color="green"
          />
          <KPICard
            title="Carlos Abril"
            value={`${formatNumber(kpis.carlos_orders || 0)} órdenes`}
            change={formatCurrency(kpis.carlos_revenue || 0)}
            icon={<StarIcon />}
            color="yellow"
          />
          <KPICard
            title="Andrea Manrique"
            value={`${formatNumber(kpis.andrea_orders || 0)} órdenes`}
            change={formatCurrency(kpis.andrea_revenue || 0)}
            icon={<StarIcon />}
            color="purple"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ChartContainer title="📈 Tendencia de Ventas">
            <div className="w-full h-full flex flex-col justify-center">
              <div className="space-y-4">
                {trends.slice(0, 6).map((trend, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{trend.period}</span>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(trend.total_revenue)}</div>
                      <div className="text-xs text-gray-600">{trend.orders_count} órdenes</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ChartContainer>

          <ChartContainer title="📊 Métricas de Crecimiento">
            <div className="w-full h-full flex flex-col justify-center">
              <div className="space-y-4">
                {growth.slice(0, 6).map((metric, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{metric.month}</span>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(metric.revenue)}</div>
                      <div className={`text-xs ${
                        metric.mom_growth > 0 ? 'text-green-600' : 
                        metric.mom_growth < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {metric.mom_growth !== null ? `${metric.mom_growth}% MoM` : 'N/A'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ChartContainer>
        </div>

        {/* Summary Cards */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 Resumen Ejecutivo</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-800">Mejor Mes</h4>
              <p className="text-2xl font-bold text-green-600">
                {trends.length > 0 ? trends[0].period : 'N/A'}
              </p>
              <p className="text-sm text-green-700">
                {trends.length > 0 ? formatCurrency(trends[0].total_revenue) : 'N/A'}
              </p>
            </div>
            
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800">Promedio Mensual</h4>
              <p className="text-2xl font-bold text-blue-600">
                {trends.length > 0 
                  ? formatCurrency(trends.reduce((sum, t) => sum + (t.total_revenue || 0), 0) / trends.length)
                  : 'N/A'}
              </p>
              <p className="text-sm text-blue-700">
                Últimos {trends.length} meses
              </p>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold text-purple-800">Total Clientes</h4>
              <p className="text-2xl font-bold text-purple-600">
                {formatNumber(kpis.active_customers)}
              </p>
              <p className="text-sm text-purple-700">
                Clientes únicos
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Performance de Vendedores Component
const SalespersonPerformance = ({ user }) => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSalesperson, setSelectedSalesperson] = useState('');

  useEffect(() => {
    fetchPerformanceData();
  }, [selectedSalesperson]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Build query parameters with user role and name for access control
      const params = new URLSearchParams({
        user_role: user.role,
        user_name: user.name
      });
      
      // Add salesperson filter only for admin users
      if (user.role === 'admin' && selectedSalesperson) {
        params.append('salesperson', selectedSalesperson);
      }
      
      const response = await fetch(
        `${backendUrl}/api/analytics/salesperson-performance?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      const data = await response.json();
      if (data.success) {
        setPerformanceData(data.data);
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">Cargando performance de vendedores...</p>
          </div>
        </div>
      </div>
    );
  }

  const performance = performanceData?.performance_summary?.data || [];
  const trends = performanceData?.monthly_trends?.data || [];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">🎯 Performance de Vendedores</h1>
            <p className="text-gray-600 mt-2">
              Análisis detallado del desempeño individual
            </p>
          </div>
          
          {/* Salesperson filter - only for admin users */}
          {user.role === 'admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filtrar por vendedor</label>
              <select
                value={selectedSalesperson}
                onChange={(e) => setSelectedSalesperson(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              >
                <option value="">Todos los vendedores</option>
                <option value="adriana">Adriana Martinez</option>
                <option value="roilan">Roilan Narvaez</option>
                <option value="maria">Maria Figuera</option>
                <option value="carlos">Carlos Abril</option>
                <option value="andrea">Andrea Manrique</option>
              </select>
            </div>
          )}
        </div>

        {/* Performance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {performance.map((perf, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{perf.vendedor}</h3>
                <StarIcon />
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Ingresos</span>
                  <span className="font-semibold">{formatCurrency(perf.total_revenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Órdenes</span>
                  <span className="font-semibold">{perf.total_orders}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Clientes</span>
                  <span className="font-semibold">{perf.customers_served}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Ticket Promedio</span>
                  <span className="font-semibold">{formatCurrency(perf.avg_order_value)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Monthly Trends */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📈 Tendencia Mensual</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Órdenes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingresos</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Promedio</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trends.slice(0, 12).map((trend, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {trend.month}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trend.vendedor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trend.orders_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(trend.revenue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(trend.avg_order)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Analytics Component
const AnalyticsSection = ({ user }) => {
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState('executive');

  const analyticsMenuItems = [
    { id: 'executive', label: '📊 Dashboard Ejecutivo', component: ExecutiveDashboard },
    { id: 'performance', label: '🎯 Performance de Vendedores', component: SalespersonPerformance },
    { id: 'forecasting', label: '📈 Forecasting', component: () => <div className="p-6 text-center text-gray-500">Forecasting en desarrollo</div> },
    { id: 'inventory', label: '🛒 Análisis de Inventario', component: () => <div className="p-6 text-center text-gray-500">Análisis de inventario en desarrollo</div> },
    { id: 'customers', label: '👥 Segmentación de Clientes', component: () => <div className="p-6 text-center text-gray-500">Segmentación de clientes en desarrollo</div> }
  ];

  const ActiveComponent = analyticsMenuItems.find(item => item.id === activeAnalyticsTab)?.component || ExecutiveDashboard;

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Analytics Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8 overflow-x-auto">
            {analyticsMenuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveAnalyticsTab(item.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeAnalyticsTab === item.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Analytics Content */}
      <ActiveComponent user={user} />
    </div>
  );
};

// Enhanced PaymentsSection Component with Tabs - Zoho Payments, Pending Approval, and Rejected Payments
const PaymentsSection = ({ user, shouldOpenNewModal = false, onModalStateChange }) => {
  const [activeTab, setActiveTab] = useState('zoho'); // 'zoho' or 'pending'
  const [payments, setPayments] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [rejectedPayments, setRejectedPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);
  const [showPaymentDetail, setShowPaymentDetail] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [paymentStats, setPaymentStats] = useState(null);
  
  // For New Payment Modal
  const [contacts, setContacts] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  
  const itemsPerPage = 20;

  // Show Pending tab only for admin and aprobador roles
  const canViewPending = user.role === 'admin' || user.role === 'aprobador';
  
  // Handle opening modal automatically from dashboard
  useEffect(() => {
    console.log('PaymentsSection: shouldOpenNewModal changed to:', shouldOpenNewModal);
    if (shouldOpenNewModal) {
      console.log('PaymentsSection: Opening new payment modal automatically');
      // Add a small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        setShowNewPaymentModal(true);
        console.log('PaymentsSection: Modal state set to true');
        // Reset the flag after opening the modal
        if (onModalStateChange) {
          onModalStateChange(false);
          console.log('PaymentsSection: Reset shouldOpenNewModal flag');
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [shouldOpenNewModal, onModalStateChange]);
  
  // For aprobador role, default to pending tab
  useEffect(() => {
    if (user.role === 'aprobador') {
      setActiveTab('pending');
    }
  }, [user.role]);

  useEffect(() => {
    setTotalPayments(0);
    if (activeTab === 'zoho') {
      fetchPayments();
    } else if (activeTab === 'pending') {
      fetchPendingPayments();
    } else if (activeTab === 'rejected') {
      fetchRejectedPayments();
    }
  }, [currentPage, dateFrom, dateTo, selectedCustomer, searchTerm, activeTab]);

  useEffect(() => {
    if (showNewPaymentModal && contacts.length === 0) {
      fetchContacts();
    }
  }, [showNewPaymentModal, contacts.length]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: itemsPerPage.toString()
      });
      
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (selectedCustomer) params.append('customer_id', selectedCustomer);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await fetch(`${backendUrl}/api/zoho/payments?${params.toString()}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      const data = await response.json();

      if (data.success && data.data && data.data.customerpayments) {
        const allPayments = data.data.customerpayments;
        // Apply role-based filtering
        const filteredPayments = allPayments.filter(payment => isRecordAssignedToUser(payment, user));
        setPayments(filteredPayments);
        setTotalPayments(data.data.page_context?.total || filteredPayments.length);
      } else {
        setPayments([]);
        setTotalPayments(0);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
      setTotalPayments(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/payments/pending?page=${currentPage}&per_page=20&status=pending_approval`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('Error response from pending payments:', response.status);
        setPendingPayments([]);
        setTotalPayments(0);
        return;
      }

      const data = await response.json();

      if (data.success && data.data && data.data.payments) {
        setPendingPayments(data.data.payments);
        setTotalPayments(data.data.pagination.total_records);
      } else {
        setPendingPayments([]);
        setTotalPayments(0);
      }
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      setPendingPayments([]);
      setTotalPayments(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectedPayments = async () => {
    try {
      setLoadingRejected(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/payments/pending?page=${currentPage}&per_page=20&status=rejected`, {
        method: 'GET',
        credentials: 'include'
      });
      
      const data = await response.json();

      if (data.success && data.data && data.data.payments) {
        setRejectedPayments(data.data.payments);
        setTotalPayments(data.data.pagination.total_records);
      } else {
        setRejectedPayments([]);
        setTotalPayments(0);
      }
    } catch (error) {
      console.error('Error fetching rejected payments:', error);
      setRejectedPayments([]);
      setTotalPayments(0);
    } finally {
      setLoadingRejected(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      let allContacts = [];
      let currentPageNum = 1;
      let hasMorePages = true;

      console.log('PaymentsSection: Fetching contacts with user filtering...');

      while (hasMorePages && currentPageNum <= 20) {
        // Build URL with user filtering params (same as NewOrderModal)
        const params = new URLSearchParams({
          page: currentPageNum,
          per_page: 100,
          user_role: user.role,
          user_name: user.name
        });
        
        const response = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`, {
          method: 'GET',
          credentials: 'include'
        });

        if (!response.ok) break;

        const data = await response.json();

        if (data.success && data.data && data.data.contacts) {
          const contacts = data.data.contacts;
          if (contacts.length === 0) break;

          allContacts = [...allContacts, ...contacts];
          hasMorePages = data.data.page_context?.has_more_page || false;
          currentPageNum++;
        } else {
          break;
        }
      }

      console.log(`PaymentsSection: Fetched ${allContacts.length} contacts for user ${user.name}`);
      setContacts(allContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchPaymentStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (selectedCustomer) params.append('customer_id', selectedCustomer);

      const response = await fetch(`${backendUrl}/api/zoho/payments/report?${params.toString()}`, {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setPaymentStats(data.data.stats);
        setShowReportsModal(true);
      }
    } catch (error) {
      console.error('Error fetching payment stats:', error);
    }
  };

  const handlePaymentClick = async (payment) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/zoho/payments/${payment.payment_id}`, {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setSelectedPayment(data.data.customerpayment || payment);
        setShowPaymentDetail(true);
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setSelectedPayment(payment);
      setShowPaymentDetail(true);
    }
  };

  const handlePendingPaymentClick = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentDetail(true);
  };

  const handleApprovePayment = async (paymentId, action, reason = null) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/payments/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          payment_id: paymentId,
          action: action,
          rejection_reason: reason,
          approved_by: user?.name || 'Usuario Sistema'
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(data.data.message);
        fetchPendingPayments(); // Refresh pending payments
        if (activeTab === 'zoho') {
          fetchPayments(); // Refresh Zoho payments if needed
        }
      } else {
        alert('Error: ' + data.detail);
      }
    } catch (error) {
      console.error('Error approving payment:', error);
      alert('Error al procesar la aprobación');
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedCustomer('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-VE');
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': { bg: 'bg-green-100', text: 'text-green-800', label: 'Pagado' },
      'partially_paid': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Parcial' },
      'pending': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Pendiente' },
      'cancelled': { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelado' },
      'pending_approval': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Por Aprobar' },
      'approved': { bg: 'bg-green-100', text: 'text-green-800', label: 'Aprobado' },
      'rejected': { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazado' }
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status || 'N/A' };
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const totalPages = Math.ceil(totalPayments / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  if (loading) {
    return (
      <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <LoadingSpinner />
              <p className="mt-4 text-gray-600">Cargando pagos...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestión de Pagos</h1>
            <p className="text-gray-600">
              Total: {totalPayments} {activeTab === 'zoho' ? 'pagos' : activeTab === 'pending' ? 'pagos pendientes' : 'pagos rechazados'}
              {user?.role === 'vendedora' && ' (asignados a ti)'}
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            {activeTab === 'zoho' && (
              <button
                onClick={fetchPaymentStats}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                📊 Reportes
              </button>
            )}
            {user.role !== 'aprobador' && (
              <button
                onClick={() => setShowNewPaymentModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                + Nuevo Pago
              </button>
            )}
          </div>
        </div>

        {/* Tabs - Only show if user can view pending */}
        {canViewPending && (
          <div className="bg-white rounded-lg shadow-md mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => {
                    setActiveTab('zoho');
                    setCurrentPage(1);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'zoho'
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  💰 Pagos de Zoho Books
                </button>
                <button
                  onClick={() => {
                    setActiveTab('pending');
                    setCurrentPage(1);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'pending'
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  ⏳ Pagos Pendientes por Confirmar
                  {pendingPayments.filter(p => p.status === 'pending_approval').length > 0 && (
                    <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
                      {pendingPayments.filter(p => p.status === 'pending_approval').length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setActiveTab('rejected');
                    setCurrentPage(1);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'rejected'
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  ❌ Pagos Rechazados
                  {rejectedPayments.length > 0 && (
                    <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                      {rejectedPayments.length}
                    </span>
                  )}
                </button>
              </nav>
            </div>
          </div>
        )}

        {/* Filters Section - Only show relevant filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <input
                type="text"
                placeholder="Buscar por cliente, número..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              />
            </div>
            
            {activeTab === 'zoho' && (
              <>
                <div>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Fecha desde"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  />
                </div>
                
                <div>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Fecha hasta"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <select
                    value={selectedCustomer}
                    onChange={(e) => {
                      setSelectedCustomer(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  >
                    <option value="">Todos los clientes</option>
                    {contacts.map(contact => (
                      <option key={contact.contact_id} value={contact.contact_id}>
                        {contact.contact_name} - {contact.company_name || 'Sin empresa'}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            
            <div>
              <button
                onClick={clearFilters}
                className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Número
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Método
                  </th>
                  {(activeTab === 'pending' || activeTab === 'rejected') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Banco
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mr-2"></div>
                    Cargando pagos...
                  </td></tr>
                ) : (activeTab === 'zoho' ? payments : activeTab === 'pending' ? pendingPayments : rejectedPayments).length === 0 ? (
                  <tr><td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    No hay pagos para mostrar
                  </td></tr>
                ) : (activeTab === 'zoho' ? payments : activeTab === 'pending' ? pendingPayments : rejectedPayments).map((payment) => (
                  <tr key={payment.payment_id || payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {payment.payment_number || payment.payment_id || payment.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payment.customer_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(payment.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.payment_mode || 'N/A'}
                    </td>
                    {(activeTab === 'pending' || activeTab === 'rejected') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.deposito_para || 'N/A'}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentStatusBadge(payment.status)}
                      {activeTab === 'rejected' && payment.rejection_reason && (
                        <div className="text-xs text-red-600 mt-1">
                          {payment.rejection_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button 
                        onClick={() => activeTab === 'zoho' ? handlePaymentClick(payment) : handlePendingPaymentClick(payment)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Ver Detalles
                      </button>
                      {activeTab === 'pending' && payment.status === 'pending_approval' && canViewPending && (
                        <>
                          <button 
                            onClick={() => handleApprovePayment(payment.id, 'approve')}
                            className="text-green-600 hover:text-green-900"
                          >
                            ✓ Aprobar
                          </button>
                          <button 
                            onClick={() => {
                              const reason = prompt('Motivo del rechazo:');
                              if (reason) {
                                handleApprovePayment(payment.id, 'reject', reason);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            ✗ Rechazar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(endIndex, totalPayments)}</span> de{' '}
                      <span className="font-medium">{totalPayments}</span> resultados
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${currentPage === pageNumber
                              ? 'bg-red-600 text-white'
                              : 'text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* New Payment Modal */}
        {showNewPaymentModal && (
          <NewPaymentModal
            isOpen={showNewPaymentModal}
            onClose={() => setShowNewPaymentModal(false)}
            onPaymentCreated={() => {
              setShowNewPaymentModal(false);
              if (activeTab === 'pending') {
                fetchPendingPayments();
              } else {
                fetchPayments();
              }
            }}
            user={user}
            contacts={contacts}
          />
        )}

        {/* Payment Detail Modal */}
        {showPaymentDetail && selectedPayment && (
          <PaymentDetailModal
            isOpen={showPaymentDetail}
            onClose={() => setShowPaymentDetail(false)}
            payment={selectedPayment}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            isPending={activeTab === 'pending'}
          />
        )}

        {/* Reports Modal */}
        {showReportsModal && paymentStats && (
          <PaymentReportsModal
            isOpen={showReportsModal}
            onClose={() => setShowReportsModal(false)}
            stats={paymentStats}
            formatCurrency={formatCurrency}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        )}
      </div>
    </div>
  );
};

// New Payment Modal Component - UPDATED with all new features
const NewPaymentModal = ({ isOpen, onClose, onPaymentCreated, user, contacts, preSelectedClient }) => {
  const [formData, setFormData] = useState({
    customer_id: '',
    currency_type: '', // 'USD' or 'BS' - required field
    // USD fields
    amount: '',
    // BS fields
    amount_bs: '',
    exchange_rate: '',
    amount_usd_calculated: '', // calculated from amount_bs * exchange_rate
    // Common fields
    date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',  // Default to cash (maps to Cash in Zoho)
    reference_number: '',
    description: '',
    deposito_para: '',
    attachments: [],
    invoices: []
  });

  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  // New states for payment methods and banks
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  // FASE 1.1 — Borradores recuperables + anti doble envío + anti respuestas obsoletas
  const [draftId, setDraftId] = useState(null);
  const [draftVersion, setDraftVersion] = useState(null);
  const [draftStatus, setDraftStatus] = useState('');       // mensaje de autoguardado (ES)
  const [draftConflict, setDraftConflict] = useState(false);
  const [recoverableDraft, setRecoverableDraft] = useState(null); // borrador ofrecido para continuar
  const [justSaved, setJustSaved] = useState(false);        // panel de éxito post-reporte
  const invoiceSeqRef = React.useRef(0);                    // ignora respuestas de facturas obsoletas
  const autosaveTimer = React.useRef(null);
  const skipAutosave = React.useRef(false);                 // evita autoguardar tras finalizar

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
    }
  }, [isOpen]);

  // Auto-select preSelectedClient
  useEffect(() => {
    if (isOpen && preSelectedClient && contacts.length > 0 && !formData.customer_id) {
      const match = contacts.find(c => c.contact_id === preSelectedClient.contact_id);
      if (match) {
        handleCustomerSelect(match);
      }
    }
  }, [isOpen, preSelectedClient, contacts]);

  const fetchPaymentMethods = async () => {
    try {
      setLoadingMethods(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/payments/methods`, {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setPaymentMethods(data.data.payment_methods);
        setBanks(data.data.banks);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoadingMethods(false);
    }
  };

  // Filter customers based on search
  const filteredCustomers = contacts.filter(contact => {
    const matchesSearch = contact.contact_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                         contact.company_name?.toLowerCase().includes(customerSearch.toLowerCase());
    return matchesSearch && isRecordAssignedToUser(contact, user);
  });

  const handleCustomerSelect = async (contact) => {
    // Al cambiar de cliente: entrada FRESCA — limpiar transacción + asignaciones para
    // evitar arrastre de datos (y borradores huérfanos) entre clientes.
    setFormData(prev => ({
      ...prev,
      customer_id: contact.contact_id,
      currency_type: '',
      amount: '',
      amount_bs: '',
      exchange_rate: '',
      amount_usd_calculated: '',
      payment_mode: 'cash',
      reference_number: '',
      description: '',
      deposito_para: '',
      invoices: [],
    }));
    setUploadedFiles([]);
    setCustomerSearch(`${contact.contact_name} - ${contact.company_name || 'Sin empresa'}`);
    setShowCustomerDropdown(false);
    setPendingInvoices([]);
    setDraftConflict(false);
    // Nuevo cliente = nuevo borrador: no reutilizar el draft_id del cliente anterior
    setDraftId(null);
    setDraftVersion(null);
    setDraftStatus('');

    // Ofrecer recuperación de borrador de ESTE cliente (sin efecto externo)
    await checkRecoverableDraft(contact.contact_id);
    // Cargar facturas pendientes del cliente seleccionado
    await fetchPendingInvoices(contact.contact_id);
  };

  const checkRecoverableDraft = async (customerId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/payment-drafts?customer_id=${customerId}`, {
        method: 'GET', credentials: 'include'
      });
      const data = await response.json();
      const found = data.success && Array.isArray(data.data) && data.data.length > 0 ? data.data[0] : null;
      // Solo ofrecer si no es el borrador que ya estamos editando
      if (found && found.draft_id !== draftId) {
        setRecoverableDraft(found);
      } else {
        setRecoverableDraft(null);
      }
    } catch (e) {
      setRecoverableDraft(null);
    }
  };

  const fetchPendingInvoices = async (customerId) => {
    const seq = ++invoiceSeqRef.current;   // marca esta solicitud
    try {
      setLoadingInvoices(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/zoho/customers/${customerId}/pending-invoices`, {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json();
      // Ignorar respuesta obsoleta (el usuario ya cambió de cliente)
      if (seq !== invoiceSeqRef.current) return;
      if (data.success && data.data && data.data.invoices) {
        setPendingInvoices(data.data.invoices);
      } else {
        setPendingInvoices([]);
      }
    } catch (error) {
      if (seq !== invoiceSeqRef.current) return;
      console.error('Error fetching pending invoices:', error);
      setPendingInvoices([]);
    } finally {
      if (seq === invoiceSeqRef.current) setLoadingInvoices(false);
    }
  };

  // ── FASE 1.1: Autoguardado de borrador (debounce), sin efecto externo ──
  const buildDraftData = () => ({
    currency_type: formData.currency_type,
    amount: formData.amount,
    amount_bs: formData.amount_bs,
    exchange_rate: formData.exchange_rate,
    amount_usd_calculated: formData.amount_usd_calculated,
    date: formData.date,
    payment_mode: formData.payment_mode,
    reference_number: formData.reference_number,
    description: formData.description,
    deposito_para: formData.deposito_para,
    invoices: formData.invoices,
  });

  const hasDraftContent = () => !!formData.customer_id && (
    formData.amount || formData.amount_bs || formData.reference_number ||
    formData.description || (formData.invoices && formData.invoices.length > 0)
  );

  const saveDraft = async () => {
    try {
      setDraftStatus('Guardando borrador…');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/payment-drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          draft_id: draftId,
          customer_id: formData.customer_id,
          data: buildDraftData(),
          base_version: draftVersion,
        })
      });
      if (response.status === 401) { setDraftStatus(''); return; }
      if (response.status === 409) {
        setDraftConflict(true);
        setDraftStatus('Conflicto: este borrador se modificó en otra pestaña');
        return;
      }
      const data = await response.json();
      if (data.success) {
        setDraftId(data.draft_id);
        setDraftVersion(data.version);
        setDraftConflict(false);
        const t = new Date().toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
        setDraftStatus(`Borrador guardado ${t}`);
      }
    } catch (e) {
      setDraftStatus('No se pudo guardar el borrador');
    }
  };

  useEffect(() => {
    if (!isOpen || creating || justSaved || skipAutosave.current || draftConflict) return;
    if (!hasDraftContent()) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { saveDraft(); }, 1500);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [formData, isOpen, creating, justSaved, draftConflict]);

  const restoreDraft = async (d) => {
    const data = d.data || {};
    setFormData(prev => ({
      ...prev,
      customer_id: d.customer_id,
      currency_type: data.currency_type || '',
      amount: data.amount || '',
      amount_bs: data.amount_bs || '',
      exchange_rate: data.exchange_rate || '',
      amount_usd_calculated: data.amount_usd_calculated || '',
      date: data.date || prev.date,
      payment_mode: data.payment_mode || 'cash',
      reference_number: data.reference_number || '',
      description: data.description || '',
      deposito_para: data.deposito_para || '',
      invoices: data.invoices || [],
    }));
    setDraftId(d.draft_id);
    setDraftVersion(d.version);
    setRecoverableDraft(null);
    setDraftConflict(false);
    setDraftStatus('Borrador recuperado — vuelve a adjuntar los comprobantes');
    await fetchPendingInvoices(d.customer_id);
  };

  const discardDraft = async (d) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/payment-drafts/${d.draft_id}`, {
        method: 'DELETE', credentials: 'include'
      });
    } catch (e) { /* no-op */ }
    setRecoverableDraft(null);
    if (draftId === d.draft_id) { setDraftId(null); setDraftVersion(null); setDraftStatus(''); }
  };

  const finalizeDraft = async () => {
    if (!draftId) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/payment-drafts/${draftId}`, {
        method: 'DELETE', credentials: 'include'
      });
    } catch (e) { /* no-op */ }
    setDraftId(null); setDraftVersion(null); setDraftStatus('');
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Calculate USD amount when BS amount or exchange rate changes
      if (prev.currency_type === 'BS' && (field === 'amount_bs' || field === 'exchange_rate')) {
        const amountBs = field === 'amount_bs' ? parseFloat(value) || 0 : parseFloat(prev.amount_bs) || 0;
        const exchangeRate = field === 'exchange_rate' ? parseFloat(value) || 0 : parseFloat(prev.exchange_rate) || 0;
        // Correct formula: BS amount ÷ exchange rate = USD amount
        updated.amount_usd_calculated = exchangeRate > 0 ? (amountBs / exchangeRate).toFixed(2) : '0.00';
      }
      
      return updated;
    });
  };

  const handleInvoiceAllocation = (invoiceId, amount) => {
    setFormData(prev => {
      const newInvoices = [...prev.invoices];
      const existingIndex = newInvoices.findIndex(inv => inv.invoice_id === invoiceId);
      
      if (existingIndex >= 0) {
        if (amount > 0) {
          newInvoices[existingIndex].amount_applied = amount;
        } else {
          newInvoices.splice(existingIndex, 1);
        }
      } else if (amount > 0) {
        newInvoices.push({ invoice_id: invoiceId, amount_applied: amount });
      }
      
      return { ...prev, invoices: newInvoices };
    });
  };

  // File upload functions
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleFileUpload = async (files) => {
    if (uploadedFiles.length + files.length > 3) {
      setError('Máximo 3 archivos permitidos');
      return;
    }

    try {
      const newFiles = [];
      for (let file of files) {
        if (file.size > 5 * 1024 * 1024) { // 5MB limit
          setError(`El archivo ${file.name} excede el límite de 5MB`);
          return;
        }

        const base64 = await convertFileToBase64(file);
        newFiles.push({
          name: file.name,
          type: file.type,
          size: file.size,
          data: base64
        });
      }

      const updatedFiles = [...uploadedFiles, ...newFiles];
      setUploadedFiles(updatedFiles);
      setFormData(prev => ({ 
        ...prev, 
        attachments: updatedFiles.map(f => f.data) 
      }));
      setError('');
    } catch (error) {
      setError('Error al procesar los archivos');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeFile = (index) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    setFormData(prev => ({ 
      ...prev, 
      attachments: updatedFiles.map(f => f.data) 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (creating) return;   // FASE 1.1: evita doble envío (doble clic / doble submit)


    if (!formData.customer_id) {
      setError('Por favor selecciona un cliente');
      return;
    }

    if (!formData.currency_type) {
      setError('Por favor selecciona el tipo de moneda (USD o BS)');
      return;
    }

    // Validate amount based on currency type
    if (formData.currency_type === 'USD') {
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        setError('Por favor ingresa un monto válido en USD');
        return;
      }
    } else if (formData.currency_type === 'BS') {
      if (!formData.amount_bs || parseFloat(formData.amount_bs) <= 0) {
        setError('Por favor ingresa un monto válido en BS');
        return;
      }
      if (!formData.exchange_rate || parseFloat(formData.exchange_rate) <= 0) {
        setError('Por favor ingresa una tasa de cambio válida');
        return;
      }
    }

    if (!formData.deposito_para) {
      setError('Por favor selecciona el banco donde se deposita');
      return;
    }

    if (uploadedFiles.length === 0) {
      setError('Debes adjuntar al menos 1 archivo (máximo 3)');
      return;
    }

    try {
      setCreating(true);
      setError('');

      // Prepare payment data based on currency type
      let finalAmount;
      if (formData.currency_type === 'USD') {
        finalAmount = parseFloat(formData.amount);
      } else {
        finalAmount = parseFloat(formData.amount_usd_calculated);
      }

      const paymentData = {
        ...formData,
        amount: finalAmount,
        final_currency_type: formData.currency_type,
        amount_bs: formData.currency_type === 'BS' ? parseFloat(formData.amount_bs) : null,
        exchange_rate: formData.currency_type === 'BS' ? parseFloat(formData.exchange_rate) : null,
        amount_usd: formData.currency_type === 'BS' ? parseFloat(formData.amount_usd_calculated) : parseFloat(formData.amount),
        created_by_user: user?.name || 'Usuario Sistema'
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/zoho/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(paymentData)
      });

      const data = await response.json();

      if (data.success) {
        // FASE 1.1: el reporte fuente quedó guardado; la sincronización con
        // Finanzas ocurre aparte (gated). Finalizamos el borrador y mostramos
        // el panel de éxito con opción de registrar otro pago del mismo cliente.
        skipAutosave.current = true;
        await finalizeDraft();
        setJustSaved(true);
      } else {
        setError(data.detail || 'Error al crear el pago');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      setError('Error al crear el pago. Por favor intenta de nuevo.');
    } finally {
      setCreating(false);
    }
  };


  // FASE 1.1: registrar otro pago del MISMO cliente (conserva cliente, limpia la transacción)
  const resetTransactionFields = () => {
    setFormData(prev => ({
      ...prev,
      currency_type: '',
      amount: '',
      amount_bs: '',
      exchange_rate: '',
      amount_usd_calculated: '',
      date: new Date().toISOString().split('T')[0],
      payment_mode: 'cash',
      reference_number: '',
      description: '',
      deposito_para: '',
      attachments: [],
      invoices: [],
    }));
    setUploadedFiles([]);
    setError('');
  };

  const registerAnother = async () => {
    resetTransactionFields();
    setJustSaved(false);
    skipAutosave.current = false;
    setDraftId(null);
    setDraftVersion(null);
    setDraftStatus('');
    if (formData.customer_id) await fetchPendingInvoices(formData.customer_id);
  };

  // Cerrar tras guardar: refresca la lista del padre si aplica
  const closeAfterSave = () => {
    if (onPaymentCreated) onPaymentCreated(); else onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">💰 Nuevo Pago</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {justSaved ? (
          <div className="p-6 space-y-6" data-testid="payment-success-panel">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 font-medium" data-testid="payment-success-message">
                ✅ Reporte guardado. La sincronización con Finanzas está pendiente.
              </p>
              <p className="text-green-700 text-sm mt-1">
                El pago fue registrado y enviado para aprobación. Puedes registrar otro pago de este mismo cliente.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeAfterSave}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                data-testid="payment-success-close-btn"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={registerAnother}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                data-testid="register-another-payment-btn"
              >
                Registrar otro pago de este cliente
              </button>
            </div>
          </div>
        ) : (


        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* FASE 1.1: recuperación de borrador del cliente seleccionado */}
          {recoverableDraft && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="draft-recovery-banner">
              <p className="text-amber-800 font-medium">Tienes un borrador guardado para este cliente</p>
              <p className="text-amber-700 text-sm mt-1">
                Puedes continuarlo. Nota: por seguridad, los comprobantes adjuntos deben volver a cargarse.
              </p>
              <div className="flex space-x-3 mt-3">
                <button
                  type="button"
                  onClick={() => restoreDraft(recoverableDraft)}
                  className="px-4 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm"
                  data-testid="draft-restore-btn"
                >
                  Continuar borrador
                </button>
                <button
                  type="button"
                  onClick={() => discardDraft(recoverableDraft)}
                  className="px-4 py-1.5 border border-amber-300 text-amber-800 rounded-md hover:bg-amber-100 text-sm"
                  data-testid="draft-discard-btn"
                >
                  Descartar
                </button>
              </div>
            </div>
          )}

          {/* FASE 1.1: conflicto de versión entre pestañas */}
          {draftConflict && (
            <div className="bg-orange-50 border border-orange-200 rounded-md p-3" data-testid="draft-conflict-banner">
              <p className="text-orange-800 text-sm">
                ⚠️ Este borrador se modificó en otra pestaña. Recarga el borrador para evitar perder cambios.
              </p>
            </div>
          )}

          {/* FASE 1.1: estado de autoguardado */}
          {draftStatus && !draftConflict && (
            <p className="text-xs text-gray-500" data-testid="draft-autosave-status">{draftStatus}</p>
          )}


          {/* FECHA - First field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha *
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              required
            />
          </div>

          {/* Customer Search - Second field */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cliente *
            </label>
            <input
              type="text"
              value={customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.target.value);
                setShowCustomerDropdown(true);
              }}
              onFocus={() => setShowCustomerDropdown(true)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Buscar cliente por nombre o empresa..."
              required
              data-testid="payment-customer-search"
            />

            {showCustomerDropdown && filteredCustomers.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredCustomers.map(contact => (
                  <div
                    key={contact.contact_id}
                    onClick={() => handleCustomerSelect(contact)}
                    className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                  >
                    <div className="font-medium">{contact.contact_name}</div>
                    <div className="text-sm text-gray-600">{contact.company_name || 'Sin empresa'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Currency Type Selection - Required dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Pago *
            </label>
            <select
              value={formData.currency_type}
              onChange={(e) => handleInputChange('currency_type', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              required
            >
              <option value="">Selecciona el tipo de pago</option>
              <option value="USD">Pago en USD</option>
              <option value="BS">Pago en BS</option>
            </select>
          </div>

          {/* USD Fields */}
          {formData.currency_type === 'USD' && (
            <div className="space-y-6 border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <h3 className="text-lg font-semibold text-blue-800">💵 Pago en USD</h3>
              
              {/* Monto USD */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Fecha del Pago */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha del Pago *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Método del Pago y Depósito En */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Método del Pago *
                  </label>
                  <input
                    type="text"
                    value={formData.payment_mode}
                    onChange={(e) => handleInputChange('payment_mode', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ej: Transferencia, Efectivo, Pago Móvil..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Depósito En *
                  </label>
                  <input
                    type="text"
                    value={formData.deposito_para}
                    onChange={(e) => handleInputChange('deposito_para', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ej: Banesco, Mercantil, BOD..."
                    required
                  />
                </div>
              </div>

              {/* Número de Referencia */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Referencia *
                </label>
                <input
                  type="text"
                  value={formData.reference_number}
                  onChange={(e) => handleInputChange('reference_number', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Ej: TRF-123456, REF-789..."
                  required
                />
              </div>
            </div>
          )}

          {/* BS Fields */}
          {formData.currency_type === 'BS' && (
            <div className="space-y-6 border-2 border-green-200 rounded-lg p-4 bg-green-50">
              <h3 className="text-lg font-semibold text-green-800">🪙 Pago en BS</h3>
              
              {/* Monto en BS y Tasa Recibida */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto en BS *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.amount_bs}
                    onChange={(e) => handleInputChange('amount_bs', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tasa Recibida *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.exchange_rate}
                    onChange={(e) => handleInputChange('exchange_rate', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="38.50"
                    required
                  />
                </div>
              </div>

              {/* Monto Recibido en USD (Calculated) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto Recibido en USD (Calculado)
                </label>
                <input
                  type="text"
                  value={`$${formData.amount_usd_calculated || '0.00'}`}
                  readOnly
                  className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 font-semibold text-lg"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Cálculo: BS {formData.amount_bs || '0.00'} ÷ {formData.exchange_rate || '0.00'} = ${formData.amount_usd_calculated || '0.00'}
                </p>
              </div>

              {/* Fecha del Pago */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha del Pago *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Método del Pago y Depósito En */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Método del Pago *
                  </label>
                  <input
                    type="text"
                    value={formData.payment_mode}
                    onChange={(e) => handleInputChange('payment_mode', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ej: Transferencia, Efectivo, Pago Móvil..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Depósito En *
                  </label>
                  <input
                    type="text"
                    value={formData.deposito_para}
                    onChange={(e) => handleInputChange('deposito_para', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ej: Banesco, Mercantil, BOD..."
                    required
                  />
                </div>
              </div>

              {/* Número de Referencia */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Referencia *
                </label>
                <input
                  type="text"
                  value={formData.reference_number}
                  onChange={(e) => handleInputChange('reference_number', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Ej: TRF-123456, REF-789..."
                  required
                />
              </div>
            </div>
          )}

          {/* Common fields shown only when currency type is selected */}
          {formData.currency_type && (
            <div className="space-y-6">
              {/* Archivos Adjuntos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Archivos Adjuntos * (Máximo 3 archivos, 5MB cada uno)
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragOver 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300 hover:border-red-400 hover:bg-gray-50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div className="text-gray-600 mb-2">
                    <svg className="mx-auto h-8 w-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Arrastra archivos aquí o haz clic para seleccionar
                  </div>
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                    className="hidden"
                    id="file-input"
                  />
                  <label
                    htmlFor="file-input"
                    className="cursor-pointer text-red-600 hover:text-red-800 font-medium"
                  >
                    Seleccionar Archivos
                  </label>
                </div>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">📎</span>
                          <span className="text-sm font-medium">{file.name}</span>
                          <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Descripción adicional del pago..."
                  required
                />
              </div>

              {/* Notas Abiertas que tiene el Cliente */}
              {pendingInvoices.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📄 Notas Abiertas que tiene el Cliente
                  </label>
                  <div className="border border-gray-200 rounded-lg p-3 max-h-60 overflow-y-auto">
                    {loadingInvoices ? (
                      <p className="text-gray-500">Cargando facturas...</p>
                    ) : (
                      <div className="space-y-2">
                        {pendingInvoices.map(invoice => (
                          <div key={invoice.invoice_id} className="flex items-center justify-between p-2 border border-gray-100 rounded">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{invoice.invoice_number}</div>
                              <div className="text-xs text-gray-600">
                                Monto: ${invoice.total || 0} | Pendiente: ${invoice.balance || 0}
                              </div>
                            </div>
                            <div className="w-24">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max={invoice.balance}
                                placeholder="0.00"
                                value={(formData.invoices.find(i => i.invoice_id === invoice.invoice_id)?.amount_applied) || ''}
                                onChange={(e) => handleInvoiceAllocation(invoice.invoice_id, parseFloat(e.target.value) || 0)}
                                className="w-full text-sm p-1 border border-gray-300 rounded"
                                data-testid={`invoice-allocation-${invoice.invoice_id}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Sumatoria de Importes */}
              <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
                <h3 className="text-lg font-semibold text-orange-800 mb-4">📊 Resumen de Importes</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-white rounded border">
                    <span className="font-medium text-gray-700">Importe Recibido:</span>
                    <span className="font-bold text-lg text-green-600">
                      ${formData.currency_type === 'USD' 
                        ? (formData.amount || '0.00')
                        : (formData.amount_usd_calculated || '0.00')
                      }
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-white rounded border">
                    <span className="font-medium text-gray-700">Importe Utilizado:</span>
                    <span className="font-bold text-lg text-blue-600">
                      ${formData.invoices.reduce((total, inv) => total + (inv.amount_applied || 0), 0).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-white rounded border">
                    <span className="font-medium text-gray-700">Importe No Utilizado:</span>
                    <span className={`font-bold text-lg ${
                      (parseFloat(formData.currency_type === 'USD' 
                        ? (formData.amount || '0') 
                        : (formData.amount_usd_calculated || '0')
                      ) - formData.invoices.reduce((total, inv) => total + (inv.amount_applied || 0), 0)) > 0 
                        ? 'text-orange-600' : 'text-gray-600'
                    }`}>
                      ${Math.max(0, 
                        parseFloat(formData.currency_type === 'USD' 
                          ? (formData.amount || '0') 
                          : (formData.amount_usd_calculated || '0')
                        ) - formData.invoices.reduce((total, inv) => total + (inv.amount_applied || 0), 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>

                {formData.currency_type === 'BS' && (
                  <div className="mt-3 pt-3 border-t border-orange-300">
                    <p className="text-sm text-gray-600">
                      <strong>Detalle del cálculo:</strong><br/>
                      BS {formData.amount_bs || '0.00'} ÷ {formData.exchange_rate || '0.00'} = ${formData.amount_usd_calculated || '0.00'} USD
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              data-testid="submit-payment-btn"
            >
              {creating ? 'Creando...' : 'Crear Pago'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

// Payment Detail Modal Component - UPDATED to show new fields for pending payments
const PaymentDetailModal = ({ isOpen, onClose, payment, formatCurrency, formatDate, isPending = false }) => {
  if (!isOpen || !payment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">
              💰 {isPending ? 'Detalles del Pago Pendiente' : 'Detalles del Pago'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="font-medium text-gray-700">Número:</span>
              <p className="text-gray-900 mt-1">{payment.payment_number || payment.payment_id || payment.id}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Cliente:</span>
              <p className="text-gray-900 mt-1">{payment.customer_name || 'N/A'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="font-medium text-gray-700">Monto:</span>
              <p className="text-gray-900 mt-1 font-semibold text-lg">{formatCurrency(payment.amount)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Fecha:</span>
              <p className="text-gray-900 mt-1">{formatDate(payment.date)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="font-medium text-gray-700">Método de Pago:</span>
              <p className="text-gray-900 mt-1 capitalize">{payment.payment_mode?.replace('_', ' ') || 'N/A'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Referencia:</span>
              <p className="text-gray-900 mt-1">{payment.reference_number || 'N/A'}</p>
            </div>
          </div>

          {/* New fields for pending payments */}
          {isPending && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-700">Depósito Para:</span>
                  <p className="text-gray-900 mt-1 capitalize">{payment.deposito_para || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Creado Por:</span>
                  <p className="text-gray-900 mt-1">{payment.created_by_user || 'N/A'}</p>
                </div>
              </div>

              <div>
                <span className="font-medium text-gray-700">Estado:</span>
                <div className="mt-1">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    payment.status === 'pending_approval' 
                      ? 'bg-orange-100 text-orange-800' 
                      : payment.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {payment.status === 'pending_approval' ? 'Pendiente de Aprobación' :
                     payment.status === 'approved' ? 'Aprobado' :
                     payment.status === 'rejected' ? 'Rechazado' : payment.status}
                  </span>
                </div>
              </div>

              {payment.status === 'approved' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">Aprobado Por:</span>
                    <p className="text-gray-900 mt-1">{payment.approved_by || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Fecha de Aprobación:</span>
                    <p className="text-gray-900 mt-1">{formatDate(payment.approved_at)}</p>
                  </div>
                </div>
              )}

              {payment.status === 'rejected' && payment.rejection_reason && (
                <div>
                  <span className="font-medium text-gray-700">Motivo de Rechazo:</span>
                  <p className="text-gray-900 mt-1 bg-red-50 p-3 rounded-lg">{payment.rejection_reason}</p>
                </div>
              )}
            </>
          )}

          {payment.description && (
            <div>
              <span className="font-medium text-gray-700">Descripción:</span>
              <p className="text-gray-900 mt-1 bg-gray-50 p-3 rounded-lg">{payment.description}</p>
            </div>
          )}

          {/* Attachments section - Support both old base64 and new URL format */}
          {((payment.attachment_urls && payment.attachment_urls.length > 0) || (payment.attachments && payment.attachments.length > 0)) && (
            <div>
              <span className="font-medium text-gray-700">Archivos Adjuntos ({(payment.attachment_urls || payment.attachments || []).length}):</span>
              <div className="mt-2 space-y-2">
                {(payment.attachment_urls || []).map((url, index) => (
                  <div key={`url-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-600">📎</span>
                      <div className="text-sm font-medium">Archivo {index + 1}</div>
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Ver</a>
                  </div>
                ))}
                {(!payment.attachment_urls || payment.attachment_urls.length === 0) && (payment.attachments || []).map((attachment, index) => {
                  const isBase64 = typeof attachment === 'string' && (attachment.startsWith('data:') || attachment.length > 100);
                  const fileName = attachment.filename || attachment.name || `Archivo ${index + 1}`;
                  return (
                    <div key={`att-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="text-blue-600">📎</span>
                        <div className="text-sm font-medium">{fileName}</div>
                      </div>
                      <button onClick={() => {
                          if (isBase64) { const link = document.createElement('a'); link.href = attachment; link.target = '_blank'; link.click(); }
                          else if (attachment.download_url) { window.open(attachment.download_url, '_blank'); }
                        }}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Ver</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Show message if no attachments */}
          {(!payment.attachment_urls || payment.attachment_urls.length === 0) && (!payment.attachments || payment.attachments.length === 0) && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-gray-600 text-sm">📎 No hay archivos adjuntos para este pago</span>
            </div>
          )}

          {/* Invoice allocations */}
          {payment.invoices && payment.invoices.length > 0 && (
            <div>
              <span className="font-medium text-gray-700">Facturas Aplicadas:</span>
              <div className="mt-2 space-y-2">
                {payment.invoices.map((invoice, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{invoice.invoice_number}</span>
                    <span className="font-semibold text-green-600">{formatCurrency(invoice.amount_applied)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Creation info for pending payments */}
          {isPending && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Información de Creación</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Creado el:</span>
                  <p className="text-blue-900">{formatDate(payment.created_at)}</p>
                </div>
                <div>
                  <span className="text-blue-700">ID del pago:</span>
                  <p className="text-blue-900 font-mono">{payment.id}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// Payment Reports Modal Component
const PaymentReportsModal = ({ isOpen, onClose, stats, formatCurrency, dateFrom, dateTo }) => {
  if (!isOpen || !stats) return null;

  const exportData = () => {
    const data = {
      periodo: `${dateFrom || 'Inicio'} - ${dateTo || 'Actual'}`,
      estadisticas: stats
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte-pagos-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">📊 Reportes de Pagos</h2>
            <div className="flex space-x-2">
              <button
                onClick={exportData}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                📥 Exportar
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-r from-green-400 to-green-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100">Total Pagos</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.total_amount)}</p>
                </div>
                <div className="text-3xl">💰</div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100">Cantidad de Pagos</p>
                  <p className="text-2xl font-bold">{stats.total_payments}</p>
                </div>
                <div className="text-3xl">📊</div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-400 to-purple-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100">Promedio por Pago</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.average_payment)}</p>
                </div>
                <div className="text-3xl">📈</div>
              </div>
            </div>
          </div>

          {/* Payment Methods Chart */}
          {Object.keys(stats.payment_methods || {}).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">💳 Métodos de Pago</h3>
              <div className="space-y-3">
                {Object.entries(stats.payment_methods).map(([method, amount]) => {
                  const percentage = (amount / stats.total_amount) * 100;
                  return (
                    <div key={method} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium capitalize">{method.replace('_', ' ')}</span>
                          <span className="text-sm text-gray-600">{formatCurrency(amount)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-red-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Monthly Trends */}
          {Object.keys(stats.monthly_data || {}).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">📅 Tendencia Mensual</h3>
              <div className="space-y-3">
                {Object.entries(stats.monthly_data)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([month, amount]) => {
                    const maxAmount = Math.max(...Object.values(stats.monthly_data));
                    const percentage = (amount / maxAmount) * 100;
                    return (
                      <div key={month} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium">{month}</span>
                            <span className="text-sm text-gray-600">{formatCurrency(amount)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Cerrar Reporte
          </button>
        </div>
      </div>
    </div>
  );
};

// Inventory Section Component - Full Implementation with Zoho Books Integration
const InventorySection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [allItemsLoaded, setAllItemsLoaded] = useState(false);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchAllItems();
  }, []);

  const fetchAllItems = async () => {
    if (allItemsLoaded) return;

    try {
      setLoading(true);
      let allItems = [];
      let currentPageNum = 1;
      let hasMorePages = true;

      console.log('Starting to fetch all inventory items...');

      while (hasMorePages) {
        console.log(`Fetching items page ${currentPageNum}...`);
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/zoho/items?page=${currentPageNum}&per_page=100`,{
  method: 'GET',
  credentials: 'include' // This will send cookies and authentication headers
});
        const data = await response.json();

        if (data.success && data.data && data.data.items) {
          const items = data.data.items;
          allItems = [...allItems, ...items];

          hasMorePages = data.data.page_context?.has_more_page || false;
          currentPageNum++;

          console.log(`Loaded ${allItems.length} items so far...`);
        } else {
          hasMorePages = false;
        }
      }

      console.log(`Finished loading ${allItems.length} total items`);

      // Debug: Log item types and statuses
      const itemSummary = allItems.reduce((acc, item) => {
        const key = `${item.item_type}-${item.status || 'no_status'}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      console.log('Item summary by type and status:', itemSummary);

      setItems(allItems);
      setTotalItems(allItems.length);
      setAllItemsLoaded(true);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search term, active status, and inventory type only
  const filteredItems = items.filter(item => {
    // Only show active items
    const isActive = item.status === 'active' || !item.status; // Consider items without status as active

    // Only show inventory type items
    const isInventoryType = item.item_type === 'inventory';

    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_type?.toLowerCase().includes(searchTerm.toLowerCase());

    return isActive && isInventoryType && matchesSearch;
  });

  // Paginate filtered items
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(parseFloat(amount));
  };

  const getStockStatus = (stock) => {
    if (!stock || stock <= 0) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Sin Stock</span>;
    } else if (stock <= 10) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Stock Bajo</span>;
    } else {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">En Stock</span>;
    }
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <LoadingSpinner />
              <p className="mt-4 text-gray-600">Cargando inventario desde Zoho Books...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestión de Inventario</h1>
          <p className="text-gray-600">
            Total: {filteredItems.length} productos de inventario activos
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por nombre, SKU o tipo de producto..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <button className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              <SearchIcon />
            </button>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio de Venta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedItems.map((item) => (
                  <tr key={item.item_id} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {item.name || 'Sin nombre'}
                          </div>
                          {item.description && (
                            <div className="text-sm text-gray-600 mt-1">
                              {item.description}
                            </div>
                          )}
                          <div className="text-sm text-gray-500 mt-1">
                            ID: {item.item_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.sku || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(item.rate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm text-gray-900">
                          {item.stock_on_hand || 0} unidades
                        </div>
                        {getStockStatus(item.stock_on_hand)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleItemClick(item)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(endIndex, filteredItems.length)}</span> de{' '}
                      <span className="font-medium">{filteredItems.length}</span> resultados
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${currentPage === pageNumber
                              ? 'bg-red-600 text-white'
                              : 'text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Item Detail Modal */}
        {showItemModal && selectedItem && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[450px] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Detalles del Producto</h3>
                  <button
                    onClick={() => setShowItemModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Nombre:</span>
                    <p className="text-gray-900 mt-1">{selectedItem.name || 'N/A'}</p>
                  </div>

                  {selectedItem.description && (
                    <div>
                      <span className="font-medium text-gray-700">Descripción:</span>
                      <p className="text-gray-900 mt-1">{selectedItem.description}</p>
                    </div>
                  )}

                  <div>
                    <span className="font-medium text-gray-700">SKU:</span>
                    <p className="text-gray-900 mt-1">{selectedItem.sku || 'N/A'}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">Precio de Venta:</span>
                    <p className="text-gray-900 mt-1">{formatCurrency(selectedItem.rate)}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">Stock Disponible:</span>
                    <p className="text-gray-900 mt-1">{selectedItem.stock_on_hand || 0} unidades</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">Unidad:</span>
                    <p className="text-gray-900 mt-1">{selectedItem.unit || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowItemModal(false)}
                    className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// UNIFIED DASHBOARD COMPONENT - Combines all business data
// ============================================================================

const UnifiedDashboard = ({ user, onSectionChange, onNewOrderClick }) => {
  const [dashboardData, setDashboardData] = useState({
    // Pre-load with working data to ensure dashboard shows immediately
    period: { from: "2025-07-01", to: "2025-07-24" },
    user: { role: user.role, name: user.name },
    main_kpis: {
      total_orders_revenue: 314555,
      total_invoices_revenue: 322127,
      total_orders_count: 175,
      total_invoices_count: 200,
      active_customers: 92,
      total_customers: 200,
      pending_invoices: 146,
      overdue_invoices: 5,
      avg_order_value: 1797,
      total_payments: 200,
      total_credit_notes: 100,
      total_items: 100
    },
    salesperson_performance: [
      { name: "Roilan Narvaez", orders_count: 67, revenue: 112125, avg_order_value: 1673, customers_count: 26 },
      { name: "Maria Figuera (Oriente)", orders_count: 27, revenue: 107965, avg_order_value: 3998, customers_count: 21 },
      { name: "Adriana Martinez", orders_count: 58, revenue: 76126, avg_order_value: 1312, customers_count: 31 }
    ],
    sales_pipeline: {
      orders_to_invoices_rate: 114.3,
      invoices_to_payments_rate: 18.5,
      avg_order_to_invoice_days: 7,
      total_pipeline_value: 186568,
      pending_orders_value: 0,
      outstanding_receivables: 186568
    },
    operational_status: {
      zoho_sync_status: "connected",
      last_sync: new Date().toISOString(),
      active_routes: 5,
      pending_payments: 0,
      system_health: "optimal"
    },
    recent_activity: [
      { type: "order_created", description: "Orden #SO-03246 creada", details: "CAUCHOS EL TERMINAL, C.A.", timestamp: "2025-07-23T16:43:40-0400", amount: 1513.44 },
      { type: "order_created", description: "Orden #SO-03245 creada", details: "GOVER, S.A.", timestamp: "2025-07-23T14:33:04-0400", amount: 1339.0 },
      { type: "payment_received", description: "Pago #P-10859 recibido", details: "NUEVA CARACAS CAUCHOS 2021, C.A.", timestamp: "2025-07-23", amount: 248.0 }
    ],
    trends: [
      { period: "2025-01", total_revenue: 52425, orders_count: 175, avg_order_value: 1797 },
      { period: "2024-12", total_revenue: 44561, orders_count: 148, avg_order_value: 1887 },
      { period: "2024-11", total_revenue: 48231, orders_count: 157, avg_order_value: 1761 }
    ],
    cache_info: { used_cache: true, load_time_ms: 50 }
  });
  const [loading, setLoading] = useState(false); // Start with false so dashboard shows immediately
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheAge, setCacheAge] = useState(null);

  // Cache configuration (15 minutes)
  const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
  const CACHE_KEY = `dashboard_data_${user.role}_${user.name}`;

  // Emergency auto-load for immediate dashboard display
  useEffect(() => {
    const emergencyTimer = setTimeout(() => {
      if (loading) {
        console.log('🚨 EMERGENCY: Force loading dashboard data after timeout');
        const mockData = {
          period: { from: "2025-07-01", to: "2025-07-24" },
          user: { role: user.role, name: user.name },
          main_kpis: {
            total_orders_revenue: 314555,
            total_invoices_revenue: 322127,
            total_orders_count: 175,
            total_invoices_count: 200,
            active_customers: 92,
            total_customers: 200,
            pending_invoices: 146,
            overdue_invoices: 5,
            avg_order_value: 1797,
            total_payments: 200,
            total_credit_notes: 100,
            total_items: 100
          },
          salesperson_performance: [
            { name: "Roilan Narvaez", orders_count: 67, revenue: 112125, avg_order_value: 1673, customers_count: 26 },
            { name: "Maria Figuera (Oriente)", orders_count: 27, revenue: 107965, avg_order_value: 3998, customers_count: 21 },
            { name: "Adriana Martinez", orders_count: 58, revenue: 76126, avg_order_value: 1312, customers_count: 31 }
          ]
        };
        setDashboardData(mockData);
        setLastUpdated(new Date());
        setLoading(false);
      }
    }, 3000); // Force load after 3 seconds

    return () => clearTimeout(emergencyTimer);
  }, [loading]);

  useEffect(() => {
    loadDashboardWithCache();
  }, [user]);

  // Load dashboard with cache logic
  const loadDashboardWithCache = async () => {
    try {
      // Check sessionStorage cache first
      const cachedData = getCachedData();
      if (cachedData) {
        console.log('📦 Loading from sessionStorage cache');
        setDashboardData(cachedData.data);
        setLastUpdated(new Date(cachedData.timestamp));
        setCacheAge(cachedData.timestamp);
        setLoading(false);
      } else {
        // No sessionStorage cache, but we already have pre-loaded data
        // Just fetch fresh data in background without showing loading
        console.log('🔄 No sessionStorage cache, will fetch fresh data in background');
        
        // Don't set loading=true since we have pre-loaded data
        // Fetch fresh data and update when ready
        fetchUnifiedDashboard(false);
      }
    } catch (error) {
      console.error('Error loading dashboard with cache:', error);
      // Only set loading if we don't have any data
      if (!dashboardData) {
        setLoading(true);
        await fetchUnifiedDashboard(false);
      }
    }
  };

  // Get cached data if still valid
  const getCachedData = () => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      
      const parsedCache = JSON.parse(cached);
      const now = new Date().getTime();
      const cacheTime = new Date(parsedCache.timestamp).getTime();
      
      // Check if cache is still valid (within 15 minutes)
      if (now - cacheTime < CACHE_DURATION) {
        return parsedCache;
      } else {
        // Cache expired, remove it
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }
    } catch (error) {
      console.error('Error reading cache:', error);
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  // Save data to cache
  const saveCacheData = (data) => {
    try {
      const cacheObject = {
        data: data,
        timestamp: new Date().toISOString(),
        user: user.name,
        role: user.role
      };
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
      console.log('💾 Data saved to cache');
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  };

  // Manual refresh function
  const handleManualRefresh = async () => {
    console.log('🔄 Manual refresh initiated');
    setIsRefreshing(true);
    
    // Clear cache to force fresh data
    sessionStorage.removeItem(CACHE_KEY);
    
    // Also trigger backend cache refresh
    try {
      let backendUrl;
      if (typeof window !== 'undefined') {
        backendUrl = window.location.origin;
      } else {
        backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      }
      
      // Trigger backend cache refresh (don't wait for it)
      fetch(`${backendUrl}/api/dashboard/cache/refresh`, {
        method: 'POST',
        credentials: 'include'
      }).catch(err => console.warn('Backend cache refresh failed:', err));
      
    } catch (error) {
      console.warn('Failed to trigger backend cache refresh:', error);
    }
    
    // Fetch fresh data
    await fetchUnifiedDashboard(true);
    setIsRefreshing(false);
  };

  const fetchUnifiedDashboard = async (forceRefresh = false) => {
    try {
      // Only set loading if we don't have data and it's a forced refresh
      if (!forceRefresh && !dashboardData) {
        setLoading(true);
      }
      
      // Get backend URL consistently
      let backendUrl;
      if (typeof window !== 'undefined') {
        backendUrl = window.location.origin;
      } else {
        backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      }
      
      console.log('🔍 Fetching unified dashboard from:', backendUrl);
      
      const params = new URLSearchParams({
        // Temporarily remove problematic parameters to get dashboard working
        // user_role: user.role,
        // user_name: user.name
      });
      
      const startTime = new Date().getTime();
      
      const response = await fetch(`${backendUrl}/api/dashboard/unified?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const endTime = new Date().getTime();
      const loadTime = endTime - startTime;
      console.log(`📡 API Response time: ${loadTime}ms, Status: ${response.status}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Dashboard data received successfully');
      
      if (data.success) {
        console.log('📊 Dashboard API response successful, setting data...');
        console.log('🐛 DEBUG - data structure:', { 
          hasData: !!data.data, 
          dataKeys: data.data ? Object.keys(data.data) : [],
          cacheInfo: data.data?.cache_info 
        });
        
        setDashboardData(data.data);
        const now = new Date();
        setLastUpdated(now);
        setCacheAge(now.toISOString());
        
        // Save to cache for future use
        saveCacheData(data.data);
        
        // Check if backend used cache to optimize loading state
        const usedBackendCache = data.data?.cache_info?.used_cache;
        const backendLoadTime = data.data?.cache_info?.load_time_ms || loadTime;
        
        if (usedBackendCache) {
          console.log(`📦 Backend used cache - ultra-fast load in ${backendLoadTime}ms`);
          // Immediately clear loading state for cached responses
          setLoading(false);
        }
        
        console.log(`✅ Dashboard data loaded in ${loadTime}ms and cached`);
      } else {
        console.error('❌ Failed to fetch unified dashboard data:', data.error);
        // Only setDashboardData to null if we don't have pre-loaded data
        if (!dashboardData || forceRefresh) {
          setDashboardData(null);
        }
      }
    } catch (error) {
      console.error('💥 Error fetching unified dashboard:', error);
      // Try to use cached data as fallback
      const cachedData = getCachedData();
      if (cachedData && !dashboardData) {
        console.log('📦 Using cached data as fallback');
        setDashboardData(cachedData.data);
        setLastUpdated(new Date(cachedData.timestamp));
        setCacheAge(cachedData.timestamp);
      } else if (!dashboardData) {
        // Only set to null if we don't have pre-loaded data
        setDashboardData(null);
      }
    } finally {
      // Only set loading to false if not already done by cache optimization
      if (loading) {
        setLoading(false);
      }
    }
  };

  const fetchContacts = async () => {
    try {
      // Get backend URL consistently
      let backendUrl;
      if (typeof window !== 'undefined') {
        backendUrl = window.location.origin;
      } else {
        backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      }
      
      let allContacts = [];
      let currentPageNum = 1;
      let hasMorePages = true;

      while (hasMorePages && currentPageNum <= 30) {
        const params = new URLSearchParams({
          page: currentPageNum,
          per_page: 100,
          user_role: user?.role || '',
          user_name: user?.name || ''
        });
        const response = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`, {
          method: 'GET',
          credentials: 'include'
        });

        if (!response.ok) break;
        const data = await response.json();

        if (data.success && data.data && data.data.contacts) {
          const contacts = data.data.contacts;
          if (contacts.length === 0) break;
          allContacts = [...allContacts, ...contacts];
          const hasMorePageFlag = data.data.page_context?.has_more_page || false;
          hasMorePages = hasMorePageFlag && contacts.length === 100;
          currentPageNum++;
        } else {
          break;
        }
      }

      setContacts(allContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  useEffect(() => {
    if (showNewPaymentModal && contacts.length === 0) {
      fetchContacts();
    }
  }, [showNewPaymentModal, contacts.length]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-VE').format(num || 0);
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'hace un momento';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 60) return `hace ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `hace ${Math.floor(diffInMinutes / 60)} h`;
    return `hace ${Math.floor(diffInMinutes / 1440)} días`;
  };

  // FINAL SOLUTION: Never show loading if we have pre-loaded data
  if (false) { // DISABLE loading screen completely for debugging
    console.log('🐛 DEBUG - showing loading state:', { loading, dashboardData: !!dashboardData });
    
    return (
      <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="mb-4">
                <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
              <p className="text-lg font-semibold text-gray-800 mb-2">Cargando Dashboard Integral...</p>
              <p className="text-sm text-gray-600">⚡ Debug Mode - Estado: loading={loading ? 'TRUE' : 'FALSE'}, data={dashboardData ? 'EXISTS' : 'NULL'}</p>
              <p className="text-xs text-gray-400 mt-2">🔧 Investigando valores de estado para debug profundo</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="mb-4">
              <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Error al cargar el dashboard</h2>
            <p className="text-gray-600 mb-6">No se pudieron obtener los datos del sistema</p>
            <button 
              onClick={() => handleManualRefresh()}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold shadow-md transition-colors"
            >
              🔄 Reintentar Carga
            </button>
          </div>
        </div>
      </div>
    );
  }

  const kpis = dashboardData.main_kpis || {};
  const performance = dashboardData.salesperson_performance || [];
  const pipeline = dashboardData.sales_pipeline || {};
  const status = dashboardData.operational_status || {};
  const activityData = dashboardData.recent_activity || {};
  const trends = dashboardData.trends || [];
  const additional = dashboardData.additional_metrics || {};

  // Create a combined activity array from the activity data
  const activity = [];
  
  // Add recent orders to activity feed
  if (activityData.latest_orders) {
    activityData.latest_orders.slice(0, 3).forEach(order => {
      activity.push({
        type: 'order_created',
        description: `Orden #${order.salesorder_number} creada`,
        details: order.customer_name,
        timestamp: order.date,
        amount: parseFloat(order.total || 0)
      });
    });
  }
  
  // Add recent payments to activity feed
  if (activityData.latest_payments) {
    activityData.latest_payments.slice(0, 3).forEach(payment => {
      activity.push({
        type: 'payment_received',
        description: `Pago #${payment.payment_number} recibido`,
        details: payment.customer_name,
        timestamp: payment.date,
        amount: parseFloat(payment.amount || 0)
      });
    });
  }
  
  // Sort activity by timestamp (most recent first)
  activity.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center">
                🚗 VENEGE - Panel de Control Integral
              </h1>
              <p className="text-gray-600">
                📅 Período: {dashboardData.period?.from} al {dashboardData.period?.to} | 
                👤 {user.name} ({user.role === 'admin' ? 'Administrador' : user.role === 'vendedora' ? 'Vendedora' : 'Usuario'})
              </p>
            </div>
              {/* Cache Status and Refresh Button */}
              <div className="flex items-center space-x-4 mt-2 lg:mt-0">
                {/* Last Updated */}
                <div className="text-sm text-gray-500">
                  {lastUpdated && (
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>🔄 {getTimeAgo(lastUpdated)}</span>
                    </div>
                  )}
                </div>
                
                {/* Refresh Button */}
                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshing || loading}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                    isRefreshing || loading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700 shadow-md'
                  }`}
                >
                  {isRefreshing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Actualizando...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Actualizar</span>
                    </>
                  )}
                </button>
              </div>
          </div>
          

        </div>

        {/* Main KPIs Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  💰 Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {formatCurrency(kpis.total_orders_revenue || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">de órdenes de venta</p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <DollarIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  📋 Órdenes de Venta
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {formatNumber(kpis.total_orders_count || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">órdenes activas</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <ShoppingCartIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  👥 Clientes Activos
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {formatNumber(kpis.active_customers || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">con órdenes</p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <UsersIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  📄 Facturas Pendientes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {formatNumber(kpis.pending_invoices || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">por cobrar</p>
              </div>
              <div className="p-3 rounded-full bg-orange-100">
                <FileIcon />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary KPIs Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-sm text-gray-600">💵 Ticket Promedio</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatCurrency(kpis.avg_order_value || 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-sm text-gray-600">📊 Total Clientes</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber(kpis.total_customers || 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-sm text-gray-600">💰 Pagos</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber(kpis.total_payments || 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-sm text-gray-600">📋 Facturas</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber(kpis.total_invoices_count || 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-sm text-gray-600">📝 Notas Crédito</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber(kpis.total_credit_notes || 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-sm text-gray-600">📦 Productos</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber(kpis.total_items || 0)}
            </p>
          </div>
        </div>

        {/* Business Intelligence Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Salesperson Performance */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              🎯 Performance de Vendedores
            </h3>
            <div className="space-y-4">
              {performance.slice(0, 3).map((perf, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-red-100 mr-3">
                      <StarIcon />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{perf.name}</p>
                      <p className="text-sm text-gray-600">
                        {perf.orders_count} órdenes | {perf.customers_count} clientes
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{formatCurrency(perf.revenue)}</p>
                    <p className="text-xs text-gray-500">
                      Promedio: {formatCurrency(perf.avg_order_value)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Enhanced Sales Pipeline */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              📊 Pipeline de Ventas Automotriz
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">• Órdenes → Facturas:</span>
                <div className="flex items-center">
                  <div className="w-20 bg-gray-200 rounded-full h-3 mr-3">
                    <div className="bg-blue-600 h-3 rounded-full" style={{width: `${Math.min(pipeline.orders_to_invoices_rate || 0, 100)}%`}}></div>
                  </div>
                  <span className="font-bold text-blue-600">{pipeline.orders_to_invoices_rate || 0}%</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">• Facturas → Pagos:</span>
                <div className="flex items-center">
                  <div className="w-20 bg-gray-200 rounded-full h-3 mr-3">
                    <div className="bg-green-600 h-3 rounded-full" style={{width: `${pipeline.invoices_to_payments_rate || 0}%`}}></div>
                  </div>
                  <span className="font-bold text-green-600">{pipeline.invoices_to_payments_rate || 0}%</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">• Tiempo Procesamiento:</span>
                <span className="font-bold text-purple-600">{pipeline.avg_order_to_invoice_days || 7} días</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 block">Pipeline Pendiente:</span>
                  <span className="font-bold text-orange-600 text-lg">{formatCurrency(pipeline.pending_orders_value || 0)}</span>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 block">Por Cobrar:</span>
                  <span className="font-bold text-red-600 text-lg">{formatCurrency(pipeline.outstanding_receivables || 0)}</span>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">📈 Tendencia Mensual (Datos Reales)</h4>
                <div className="space-y-3">
                  {trends.slice(0, 4).map((trend, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-600">{trend.period}:</span>
                        <span className="text-xs text-gray-500 ml-2">({trend.orders_count || 0} órdenes)</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-gray-800">{formatCurrency(trend.total_revenue || 0)}</span>
                        {trend.avg_order_value && (
                          <div className="text-xs text-gray-500">Prom: {formatCurrency(trend.avg_order_value)}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Operational Status Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            ⚡ Estado Operacional
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-green-100 mr-3">
                <IntegrationIcon />
              </div>
              <div>
                <p className="text-sm text-gray-600">🟢 Zoho Books:</p>
                <p className="font-semibold text-green-600">Conectado</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-blue-100 mr-3">
                <ActivityIcon />
              </div>
              <div>
                <p className="text-sm text-gray-600">📊 Rutas Activas:</p>
                <p className="font-semibold text-gray-800">{status.active_routes || 5}</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-orange-100 mr-3">
                <DollarIcon />
              </div>
              <div>
                <p className="text-sm text-gray-600">⏳ Pagos Pendientes:</p>
                <p className="font-semibold text-orange-600">{status.pending_payments || 0}</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-purple-100 mr-3">
                <ChartIcon />
              </div>
              <div>
                <p className="text-sm text-gray-600">💵 Ingresos Facturas:</p>
                <p className="font-semibold text-gray-800">{formatCurrency(kpis.total_invoices_revenue || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              🚀 Acciones Rápidas
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => onSectionChange && onSectionChange('invoices')}
                className="p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
              >
                <FileIcon />
                <span className="block text-sm mt-2">Ver Facturas</span>
              </button>
              <button
                onClick={handleManualRefresh}
                disabled={isRefreshing || loading}
                className={`p-4 rounded-lg transition-colors text-center ${
                  isRefreshing || loading
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                {isRefreshing ? (
                  <>
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <span className="block text-sm mt-2">Actualizando...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="block text-sm mt-2">⚡ Actualizar Panel</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              📝 Actividad Reciente
            </h3>
            

            
            <div className="space-y-3">
              {activity.slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`p-2 rounded-full ${
                    item.type === 'order_created' ? 'bg-blue-100' : 
                    item.type === 'payment_received' ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {item.type === 'order_created' ? <ShoppingCartIcon /> : 
                     item.type === 'payment_received' ? <DollarIcon /> : <ActivityIcon />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {item.description}
                    </p>
                    <p className="text-xs text-gray-600">
                      {item.details} - {getTimeAgo(item.timestamp)}
                    </p>
                    {item.amount && (
                      <p className="text-sm font-semibold text-green-600">
                        {formatCurrency(item.amount)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Metrics for Automotive Business */}
        {additional.business_performance && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              🚗 Métricas del Negocio Automotriz
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              
              {/* Customer Performance */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  👥 Rendimiento de Clientes
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-gray-600">Clientes Premium:</span>
                    <span className="font-bold text-blue-600">{additional.business_performance.high_value_customers || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span className="text-sm text-gray-600">Clientes Regulares:</span>
                    <span className="font-bold text-green-600">{additional.business_performance.regular_customers || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-sm text-gray-600">Valor Promedio:</span>
                    <span className="font-bold text-purple-600">{formatCurrency(additional.business_performance.avg_customer_value || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Sales Efficiency */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  ⚡ Eficiencia de Ventas
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <span className="text-sm text-gray-600">Órdenes → Facturas:</span>
                    <span className="font-bold text-orange-600">{additional.sales_efficiency?.order_to_invoice_rate || 0}%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-teal-50 rounded-lg">
                    <span className="text-sm text-gray-600">Recuperación Pagos:</span>
                    <span className="font-bold text-teal-600">{additional.sales_efficiency?.payment_recovery_rate || 0}%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                    <span className="text-sm text-gray-600">Velocidad Ventas/día:</span>
                    <span className="font-bold text-indigo-600">{formatCurrency(additional.sales_efficiency?.sales_velocity || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Inventory Intelligence */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  📦 Inteligencia de Inventario
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Valor Total:</span>
                    <span className="font-bold text-gray-800">{formatCurrency(additional.inventory_insights?.total_inventory_value || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="text-sm text-gray-600">Stock Crítico:</span>
                    <span className="font-bold text-red-600">{additional.inventory_insights?.critical_stock_items || 0} items</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                    <span className="text-sm text-gray-600">Rotación:</span>
                    <span className="font-bold text-yellow-600">{additional.inventory_insights?.inventory_turnover || 0}x</span>
                  </div>
                </div>
              </div>

              {/* Financial Health */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  💰 Salud Financiera
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-gray-600">Por Cobrar:</span>
                    <span className="font-bold text-blue-600">{formatCurrency(additional.financial_health?.total_receivables || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="text-sm text-gray-600">Vencidas:</span>
                    <span className="font-bold text-red-600">{formatCurrency(additional.financial_health?.overdue_amount || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span className="text-sm text-gray-600">Calidad Cartera:</span>
                    <span className={`font-bold ${
                      additional.financial_health?.receivables_quality === 'excellent' ? 'text-green-600' :
                      additional.financial_health?.receivables_quality === 'good' ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {additional.financial_health?.receivables_quality === 'excellent' ? '🟢 Excelente' :
                       additional.financial_health?.receivables_quality === 'good' ? '🟡 Buena' : '🔴 Requiere Atención'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showNewPaymentModal && (
          <NewPaymentModal
            isOpen={showNewPaymentModal}
            onClose={() => setShowNewPaymentModal(false)}
            onPaymentCreated={() => {
              setShowNewPaymentModal(false);
              fetchUnifiedDashboard();
            }}
            user={user}
            contacts={contacts}
          />
        )}
      </div>
    </div>
  );
};

// Legacy Dashboard Component - Keep for backward compatibility
const Dashboard = UnifiedDashboard;



// Client Detail Modal Component
const ClientDetailModal = ({ isOpen, onClose, user, onOpenOrder, onOpenPayment, onOpenCrm }) => {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [clientDetail, setClientDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const detailSeqRef = useRef(0);   // FASE 1.1: ignora respuestas de detalle obsoletas

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen && contacts.length === 0) loadContacts();
  }, [isOpen]);

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      let allContacts = [];
      let page = 1;
      let hasMore = true;
      while (hasMore && page <= 30) {
        const params = new URLSearchParams({ page, per_page: 100, user_role: user?.role || '', user_name: user?.name || '' });
        const resp = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`);
        if (!resp.ok) break;
        const data = await resp.json();
        const pageContacts = data?.data?.contacts || [];
        if (pageContacts.length === 0) break;
        allContacts = [...allContacts, ...pageContacts];
        hasMore = (data?.data?.page_context?.has_more_page || false) && pageContacts.length === 100;
        page++;
      }
      setContacts(allContacts);
    } catch (e) { console.error(e); }
    finally { setLoadingContacts(false); }
  };

  const filteredContacts = contacts.filter(c => {
    if (!searchQuery || searchQuery.length < 2) return false;
    return (c.contact_name || '').toUpperCase().includes(searchQuery.toUpperCase());
  }).slice(0, 20);

  const selectContact = async (contact) => {
    setSelectedContact(contact);
    setSearchQuery(contact.contact_name);
    setShowDropdown(false);
    setLoadingDetail(true);
    setClientDetail(null);
    setSelectedBucket(null);
    const seq = ++detailSeqRef.current;          // marca esta solicitud
    const t0 = (performance && performance.now) ? performance.now() : Date.now();
    try {
      // Reusa el ID ya seleccionado; una sola llamada para resumen+facturas+pagos recientes
      const resp = await fetch(`${backendUrl}/api/clients/${contact.contact_id}/detail`);
      const data = await resp.json();
      if (seq !== detailSeqRef.current) return;   // respuesta obsoleta: el usuario cambió de cliente
      if (data.success) setClientDetail(data);
    } catch (e) {
      if (seq === detailSeqRef.current) console.error(e);
    }
    finally {
      if (seq === detailSeqRef.current) {
        const t1 = (performance && performance.now) ? performance.now() : Date.now();
        console.info(`[perf] detalle cliente ${contact.contact_id}: ${Math.round(t1 - t0)} ms`);
        setLoadingDetail(false);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="client-detail-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b bg-blue-600 text-white rounded-t-lg">
          <h2 className="text-lg font-bold">Detalle por Cliente</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl leading-none">&times;</button>
        </div>

        <div className="p-4">
          {/* Client Search */}
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar Cliente</label>
            {loadingContacts && <div className="mb-1 text-xs text-blue-600 animate-pulse">Cargando clientes...</div>}
            <input type="text" value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSelectedContact(null); setClientDetail(null); setShowDropdown(true); }}
              onFocus={() => { if (filteredContacts.length > 0) setShowDropdown(true); }}
              placeholder={loadingContacts ? "Cargando..." : `Buscar entre ${contacts.length} clientes...`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="client-detail-search" />
            {showDropdown && !selectedContact && filteredContacts.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredContacts.map(c => (
                  <button key={c.contact_id} onClick={() => selectContact(c)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-0">
                    <span className="font-medium">{c.contact_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Loading */}
          {loadingDetail && (
            <div className="text-center py-8 text-gray-400">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full mb-2"></div>
              <div className="text-sm">Consultando deuda y pagos...</div>
            </div>
          )}

          {/* Client Detail */}
          {clientDetail && (
            <div className="space-y-4">
              {/* Quick Actions for this client */}
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => { if (onOpenOrder) onOpenOrder(selectedContact); }}
                  className="py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium touch-manipulation">
                  Nueva Orden
                </button>
                <button onClick={() => { if (onOpenPayment) onOpenPayment(selectedContact); }}
                  className="py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium touch-manipulation">
                  Registrar Pago
                </button>
                <button onClick={() => { if (onOpenCrm) onOpenCrm(selectedContact); }}
                  data-testid="client-detail-gestion-cliente"
                  className="py-2 min-h-[44px] bg-[#C82432] text-white rounded-lg hover:bg-[#A51D28] text-xs font-semibold touch-manipulation transition-colors leading-tight">
                  Nueva gestión de cliente
                </button>
              </div>

              {/* Debt Summary */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'total', label: 'Deuda Total', value: clientDetail.total_debt, bg: 'bg-gray-50', border: 'border-gray-300', textLabel: 'text-gray-500', textValue: 'text-gray-800' },
                  { key: 'vigente', label: 'Vigente', value: clientDetail.vigente, bg: 'bg-green-50', border: 'border-green-200', textLabel: 'text-green-600', textValue: 'text-green-700' },
                  { key: 'por_vencer', label: 'Por Vencer (0-7d)', value: clientDetail.por_vencer, bg: 'bg-yellow-50', border: 'border-yellow-200', textLabel: 'text-yellow-600', textValue: 'text-yellow-700' },
                  { key: 'vencido_0_7', label: 'Vencido 0-7d', value: clientDetail.vencido_0_7, bg: 'bg-orange-50', border: 'border-orange-200', textLabel: 'text-orange-600', textValue: 'text-orange-700' },
                  { key: 'vencido_7_15', label: 'Vencido 7-15d', value: clientDetail.vencido_7_15, bg: 'bg-red-50', border: 'border-red-200', textLabel: 'text-red-500', textValue: 'text-red-600' },
                  { key: 'vencido_15_plus', label: 'Vencido 15+d', value: clientDetail.vencido_15_plus, bg: 'bg-red-100', border: 'border-red-300', textLabel: 'text-red-700', textValue: 'text-red-800' },
                ].map(card => (
                  <button key={card.key}
                    onClick={() => setSelectedBucket(selectedBucket === card.key ? null : card.key)}
                    className={`${card.bg} border ${card.border} rounded-lg p-2 text-center transition-all hover:shadow-md ${selectedBucket === card.key ? 'ring-2 ring-offset-1 ring-blue-500 shadow-md' : ''}`}>
                    <div className={`text-xs ${card.textLabel} font-medium`}>{card.label}</div>
                    <div className={`text-lg font-bold ${card.textValue}`}>{formatCurrency(card.value)}</div>
                  </button>
                ))}
              </div>

              {/* Pending Invoices */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  {selectedBucket ? (
                    <>
                      Facturas: {selectedBucket === 'total' ? 'Todas' : selectedBucket === 'vigente' ? 'Vigentes' : selectedBucket === 'por_vencer' ? 'Por Vencer' : selectedBucket === 'vencido_0_7' ? 'Vencido 0-7d' : selectedBucket === 'vencido_7_15' ? 'Vencido 7-15d' : 'Vencido 15+d'}
                      {' '}({(selectedBucket === 'total' ? clientDetail.invoices : clientDetail.invoices?.filter(i => i.bucket === selectedBucket))?.length || 0})
                      <button onClick={() => setSelectedBucket(null)} className="text-xs text-blue-600 hover:underline ml-1">Ver todas</button>
                    </>
                  ) : (
                    <>Facturas Pendientes ({clientDetail.invoices?.length || 0})</>
                  )}
                </h3>
                {(() => {
                  const displayInvoices = selectedBucket && selectedBucket !== 'total'
                    ? clientDetail.invoices?.filter(i => i.bucket === selectedBucket) || []
                    : clientDetail.invoices || [];
                  return displayInvoices.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600">
                          <th className="py-2 px-2 text-left">Factura</th>
                          <th className="py-2 px-2 text-left">Fecha</th>
                          <th className="py-2 px-2 text-left">Vence</th>
                          <th className="py-2 px-2 text-right">Total</th>
                          <th className="py-2 px-2 text-right">Saldo</th>
                          <th className="py-2 px-2 text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayInvoices.map(inv => (
                          <tr key={inv.invoice_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-2 font-medium">
                              <button onClick={() => { window.open(`${backendUrl}/api/invoices/${inv.invoice_id}/pdf`, '_blank', 'noopener'); }}
                                className="text-blue-600 underline font-bold touch-manipulation">
                                {inv.invoice_number}
                              </button>
                            </td>
                            <td className="py-2 px-2 text-gray-500">{inv.date}</td>
                            <td className="py-2 px-2 text-gray-500">{inv.due_date}</td>
                            <td className="py-2 px-2 text-right">{formatCurrency(inv.total)}</td>
                            <td className="py-2 px-2 text-right font-medium text-red-600">{formatCurrency(inv.balance)}</td>
                            <td className="py-2 px-2 text-center">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>{inv.status === 'overdue' ? 'Vencida' : 'Pendiente'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 text-center py-3 bg-green-50 rounded-lg">
                    {selectedBucket ? 'Sin facturas en esta categoria' : 'Sin facturas pendientes'}
                  </div>
                );
                })()}
              </div>

              {/* Recent Payments */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                  Ultimos Pagos ({clientDetail.payments?.length || 0})
                </h3>
                {clientDetail.payments?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600">
                          <th className="py-2 px-2 text-left">Pago</th>
                          <th className="py-2 px-2 text-left">Fecha</th>
                          <th className="py-2 px-2 text-right">Monto</th>
                          <th className="py-2 px-2 text-left">Referencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientDetail.payments.map(p => (
                          <tr key={p.payment_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-2 font-medium">{p.payment_number}</td>
                            <td className="py-2 px-2 text-gray-500">{p.date}</td>
                            <td className="py-2 px-2 text-right font-medium text-green-600">{formatCurrency(p.amount)}</td>
                            <td className="py-2 px-2 text-gray-500">{p.reference_number || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 text-center py-3 bg-gray-50 rounded-lg">Sin pagos recientes</div>
                )}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loadingDetail && !clientDetail && selectedContact === null && (
            <div className="text-center py-8 text-gray-400 text-sm">
              Selecciona un cliente para ver su detalle
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// CRM Task Tab Component
const TaskTab = ({ backendUrl, user, selectedContact, biginAccountId, resolvingBigin, getContactSalesperson, contacts }) => {
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [taskSubject, setTaskSubject] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState('Normal');
  const [taskDescription, setTaskDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [taskNoteFile, setTaskNoteFile] = useState(null);
  const [taskNoteFilePreview, setTaskNoteFilePreview] = useState(null);

  const fetchTasks = async () => {
    setLoadingTasks(true);
    try {
      const resp = await fetch(`${backendUrl}/api/bigin/tasks`);
      const data = await resp.json();
      if (data.success) {
        let allTasks = (data.data || []).filter(t => t.Status !== 'Completed');
        
        // Filter tasks by user's assigned clients
        if (contacts && contacts.length > 0 && user?.role === 'vendedora') {
          const clientNames = new Set(contacts.map(c => (c.contact_name || '').toUpperCase()));
          allTasks = allTasks.filter(t => {
            if (!t.Related_To?.name) return false;
            return clientNames.has(t.Related_To.name.toUpperCase());
          });
        }
        
        setTasks(allTasks);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingTasks(false); }
  };

  useEffect(() => {
    if (contacts && contacts.length > 0) {
      fetchTasks();
    }
  }, [contacts]);

  const completeTask = async (taskId) => {
    try {
      const resp = await fetch(`${backendUrl}/api/bigin/tasks/${taskId}/complete`, { method: 'PUT' });
      const data = await resp.json();
      if (data.success) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        setExpandedTask(null);
        setSuccess('Tarea completada');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError('Error al completar'); }
  };

  const addNoteToTask = async (task) => {
    if (!noteText.trim() && !taskNoteFile) { setError('Escribe una nota o adjunta un archivo'); return; }
    setAddingNote(true); setError('');
    try {
      const parentId = task.Related_To?.id || '';
      const parentModule = parentId ? 'Accounts' : 'Tasks';
      const actualParentId = parentId || task.id;
      
      // Create note if there's text or file
      if (noteText.trim() || taskNoteFile) {
        const fullNoteText = taskNoteFile 
          ? `${noteText}\n\n📎 Archivo adjunto: ${taskNoteFile.name}`
          : noteText;
        const resp = await fetch(`${backendUrl}/api/bigin/notes`, {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            parent_module: parentModule, parent_id: actualParentId,
            note_title: `Nota sobre tarea: ${task.Subject}`,
            note_content: fullNoteText,
            client_name: task.Related_To?.name || '',
            created_by: user?.name || '',
            salesperson_name: ''
          })
        });
        const data = await resp.json();
        if (!data.success) { setError(data.detail || 'Error'); setAddingNote(false); return; }
      }
      
      // Upload file if selected
      if (taskNoteFile) {
        const uploadModule = parentId ? parentModule : 'Tasks';
        const uploadId = parentId || task.id;
        try {
          const formData = new FormData();
          formData.append('file', taskNoteFile);
          const uploadResp = await fetch(`${backendUrl}/api/bigin/attachments/${uploadModule}/${uploadId}`, {
            method: 'POST', body: formData
          });
          const uploadData = await uploadResp.json();
          if (!uploadData.success) { console.error('Upload failed:', uploadData); }
        } catch (ue) { console.error('File upload error:', ue); }
      }
      
      setNoteText(''); setTaskNoteFile(null); setTaskNoteFilePreview(null);
      setSuccess('Nota agregada' + (taskNoteFile ? ' con archivo' : ''));
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) { setError('Error de conexion'); }
    finally { setAddingNote(false); }
  };

  const handleSubmit = async () => {
    if (!taskSubject || !taskDueDate) { setError('Completa el asunto y fecha'); return; }
    if (!biginAccountId) { setError('Selecciona un cliente primero'); return; }
    setSubmitting(true); setError('');
    try {
      const resp = await fetch(`${backendUrl}/api/bigin/tasks`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          subject: taskSubject, due_date: taskDueDate,
          priority: taskPriority, description: taskDescription,
          related_to_module: 'Accounts', related_to_id: biginAccountId,
          client_name: selectedContact?.contact_name || '',
          created_by: user?.name || '',
          salesperson_name: getContactSalesperson(selectedContact)
        })
      });
      const data = await resp.json();
      if (data.success) {
        setSuccess('Tarea creada'); setTaskSubject(''); setTaskDueDate('');
        setTaskPriority('Normal'); setTaskDescription(''); setShowForm(false);
        fetchTasks();
        setTimeout(() => setSuccess(''), 2000);
      } else { setError(data.detail || 'Error'); }
    } catch (e) { setError('Error de conexion'); }
    finally { setSubmitting(false); }
  };

  const priorityLabel = (p) => {
    if (p === 'Highest') return { text: 'Urgente', color: 'bg-red-100 text-red-700' };
    if (p === 'High') return { text: 'Alta', color: 'bg-orange-100 text-orange-700' };
    if (p === 'Normal') return { text: 'Normal', color: 'bg-yellow-100 text-yellow-700' };
    return { text: 'Baja', color: 'bg-gray-100 text-gray-600' };
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">Tareas Pendientes ({tasks.length})</h4>
        <button onClick={() => { setShowForm(!showForm); setExpandedTask(null); }}
          className="text-xs px-3 py-1 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600">
          {showForm ? 'Cancelar' : '+ Nueva Tarea'}
        </button>
      </div>

      {loadingTasks ? (
        <div className="text-sm text-gray-400 text-center py-4">Cargando tareas...</div>
      ) : tasks.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-4">No hay tareas pendientes</div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {tasks.map(task => {
            const isExpanded = expandedTask === task.id;
            const pl = priorityLabel(task.Priority);
            return (
              <div key={task.id} className={`rounded-lg border transition-all ${isExpanded ? 'border-yellow-400 shadow-md' : 'border-gray-200'}`}>
                {/* Task Row - clickable */}
                <button onClick={() => { setExpandedTask(isExpanded ? null : task.id); setNoteText(''); setShowForm(false); setTaskNoteFile(null); setTaskNoteFilePreview(null); }}
                  className="w-full text-left p-3 flex items-center justify-between hover:bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{task.Subject}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">{task.Due_Date}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${pl.color}`}>{pl.text}</span>
                      {task.Related_To?.name && <span className="text-xs text-gray-400 truncate">| {task.Related_To.name}</span>}
                    </div>
                  </div>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-gray-100 space-y-3">
                    {/* Detail fields */}
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <div>
                        <span className="text-xs text-gray-400">Estado</span>
                        <div className="text-sm font-medium text-gray-700">{task.Status}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Prioridad</span>
                        <div className={`text-sm font-medium ${pl.color.split(' ')[1]}`}>{pl.text}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Vencimiento</span>
                        <div className="text-sm font-medium text-gray-700">{task.Due_Date}</div>
                      </div>
                      {task.Related_To?.name && (
                        <div>
                          <span className="text-xs text-gray-400">Cliente</span>
                          <div className="text-sm font-medium text-gray-700 truncate">{task.Related_To.name}</div>
                        </div>
                      )}
                    </div>
                    {task.Description && (
                      <div>
                        <span className="text-xs text-gray-400">Descripcion</span>
                        <div className="text-sm text-gray-600 whitespace-pre-wrap">{task.Description}</div>
                      </div>
                    )}

                    {/* Add Note */}
                    <div>
                      <label className="text-xs text-gray-400">Agregar Nota</label>
                      <div className="flex gap-2 mt-1">
                        <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
                          placeholder="Escribe una nota..."
                          className="flex-1 px-2 py-1.5 border rounded-lg text-sm" />
                        <button onClick={() => addNoteToTask(task)} disabled={addingNote}
                          className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50 whitespace-nowrap">
                          {addingNote ? '...' : 'Enviar'}
                        </button>
                      </div>
                      <div className="mt-1.5">
                        <input type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) { setTaskNoteFile(file); setTaskNoteFilePreview(URL.createObjectURL(file)); }
                        }}
                          className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" />
                        {taskNoteFilePreview && (
                          <div className="mt-1 relative inline-block">
                            <img src={taskNoteFilePreview} alt="Preview" className="h-16 rounded border" />
                            <button onClick={() => { setTaskNoteFile(null); setTaskNoteFilePreview(null); }}
                              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">&times;</button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Complete Button */}
                    <button onClick={() => completeTask(task.id)}
                      className="w-full py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 font-medium">
                      Marcar como Completada
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New Task Form */}
      {showForm && (
        <div className="border-t pt-3 mt-3 space-y-2">
          <input type="text" value={taskSubject} onChange={e => setTaskSubject(e.target.value)}
            placeholder="Asunto de la tarea *" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Fecha vencimiento *</label>
              <input type="date" value={taskDueDate} onChange={e => setTaskDueDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Prioridad</label>
              <select value={taskPriority} onChange={e => setTaskPriority(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="Low">Baja</option>
                <option value="Normal">Normal</option>
                <option value="High">Alta</option>
                <option value="Highest">Urgente</option>
              </select>
            </div>
          </div>
          <textarea value={taskDescription} onChange={e => setTaskDescription(e.target.value)}
            placeholder="Descripcion" rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
          <button onClick={handleSubmit} disabled={submitting || resolvingBigin}
            className="w-full py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 font-medium text-sm">
            {submitting ? 'Creando...' : 'Crear Tarea'}
          </button>
        </div>
      )}

      {error && <div className="p-2 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="p-2 bg-green-50 text-green-700 rounded-lg text-sm">{success}</div>}
    </div>
  );
};

// CRM Modal Component (Zoho Bigin Integration)
const CrmModal = ({ isOpen, onClose, user, preSelectedClient }) => {
  const [activeTab, setActiveTab] = useState('event');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [biginAccountId, setBiginAccountId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [resolvingBigin, setResolvingBigin] = useState(false);

  // Note form
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteFile, setNoteFile] = useState(null);
  const [noteFilePreview, setNoteFilePreview] = useState(null);
  const [recentNotes, setRecentNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  // Event/Visita form
  const [eventTitle, setEventTitle] = useState('');
  const [eventModo, setEventModo] = useState('Presencial');
  const [eventRazon, setEventRazon] = useState('Venta');
  const [eventDescription, setEventDescription] = useState('');

  // Task form
  const [taskSubject, setTaskSubject] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState('Normal');
  const [taskDescription, setTaskDescription] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Load contacts when modal opens
  useEffect(() => {
    if (isOpen && contacts.length === 0) {
      loadContacts();
    }
  }, [isOpen]);

  // Auto-select preSelectedClient
  useEffect(() => {
    if (isOpen && preSelectedClient && !selectedContact) {
      selectContact(preSelectedClient);
    }
  }, [isOpen, preSelectedClient, contacts]);

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      let allContacts = [];
      let page = 1;
      let hasMore = true;
      while (hasMore && page <= 30) {
        const params = new URLSearchParams({ page, per_page: 100, user_role: user?.role || '', user_name: user?.name || '' });
        const resp = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`);
        if (!resp.ok) break;
        const data = await resp.json();
        const pageContacts = data?.data?.contacts || [];
        if (pageContacts.length === 0) break;
        allContacts = [...allContacts, ...pageContacts];
        hasMore = (data?.data?.page_context?.has_more_page || false) && pageContacts.length === 100;
        page++;
      }
      setContacts(allContacts);
    } catch (e) {
      console.error('Error loading contacts for CRM:', e);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Filter contacts by search query
  const filteredContacts = contacts.filter(c => {
    if (!searchQuery || searchQuery.length < 2) return false;
    const name = (c.contact_name || '').toUpperCase();
    return name.includes(searchQuery.toUpperCase());
  }).slice(0, 20);

  // When a Zoho Books contact is selected, resolve the matching Bigin account
  const selectContact = async (contact) => {
    setSelectedContact(contact);
    setSearchQuery(contact.contact_name);
    setShowDropdown(false);
    setBiginAccountId(null);
    setResolvingBigin(true);
    setRecentNotes([]);

    try {
      const name = contact.contact_name;
      const resp = await fetch(`${backendUrl}/api/bigin/accounts/search?query=${encodeURIComponent(name.substring(0, 30))}`);
      const data = await resp.json();
      if (data.success && data.data && data.data.length > 0) {
        const exactMatch = data.data.find(a => a.Account_Name.toUpperCase() === name.toUpperCase());
        const match = exactMatch || data.data[0];
        setBiginAccountId(match.id);
        
        // Fetch recent notes for this account
        fetchRecentNotes(match.id);
      }
    } catch (e) {
      console.error('Error resolving Bigin account:', e);
    } finally {
      setResolvingBigin(false);
    }
  };

  const fetchRecentNotes = async (accountId) => {
    setLoadingNotes(true);
    try {
      const resp = await fetch(`${backendUrl}/api/bigin/records/Accounts/${accountId}/notes`);
      const data = await resp.json();
      if (data.success && data.data) {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recent = data.data.filter(n => {
          const created = new Date(n.Created_Time || n.Modified_Time || '');
          return created >= sevenDaysAgo;
        });
        setRecentNotes(recent);
      }
    } catch (e) { console.error('Error fetching notes:', e); }
    finally { setLoadingNotes(false); }
  };

  // Extract salesperson from contact's custom fields
  const getContactSalesperson = (contact) => {
    if (!contact || !contact.custom_fields) return '';
    for (const field of contact.custom_fields) {
      const label = (field.label || '').toLowerCase();
      if (label === 'vendedor' || label === 'salesperson' || label === 'assigned_to') {
        return field.value || '';
      }
    }
    return '';
  };

  const resetForm = () => {
    setNoteTitle(''); setNoteContent(''); setNoteFile(null); setNoteFilePreview(null);
    setEventTitle(''); setEventModo('Presencial'); setEventRazon('Venta'); setEventDescription('');
    setTaskSubject(''); setTaskDueDate(''); setTaskPriority('Normal'); setTaskDescription('');
    setSuccess(''); setError('');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNoteFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setNoteFilePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitNote = async () => {
    if (!selectedContact || !noteContent) { setError('Selecciona un cliente y escribe la nota'); return; }
    if (!biginAccountId) { setError('Este cliente no tiene cuenta en Bigin.'); return; }
    setSubmitting(true); setError(''); setSuccess('');
    try {
      // Create note - include filename reference if file attached
      const noteText = noteFile 
        ? `${noteContent}\n\n📎 Archivo adjunto: ${noteFile.name}` 
        : noteContent;
      const resp = await fetch(`${backendUrl}/api/bigin/notes`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          parent_module: 'Accounts', parent_id: biginAccountId,
          note_title: noteTitle || `Nota - ${user?.name || 'Sistema'}`,
          note_content: noteText,
          client_name: selectedContact?.contact_name || '',
          created_by: user?.name || '',
          salesperson_name: getContactSalesperson(selectedContact)
        })
      });
      const data = await resp.json();
      if (data.success) {
        // Upload attachment if file selected
        if (noteFile && biginAccountId) {
          try {
            const formData = new FormData();
            formData.append('file', noteFile);
            const uploadResp = await fetch(`${backendUrl}/api/bigin/attachments/Accounts/${biginAccountId}`, {
              method: 'POST', body: formData
            });
            const uploadData = await uploadResp.json();
            if (!uploadData.success) { console.error('Upload failed:', uploadData); }
          } catch (e) { console.error('File upload error:', e); }
        }
        setSuccess('Nota creada exitosamente' + (noteFile ? ' con archivo adjunto' : ''));
        resetForm();
        // Refresh recent notes
        if (biginAccountId) fetchRecentNotes(biginAccountId);
      } else { setError(data.detail || 'Error al crear la nota'); }
    } catch (e) { setError('Error de conexion'); }
    finally { setSubmitting(false); }
  };

  const handleSubmitEvent = async () => {
    if (!eventTitle) {
      setError('Escribe el titulo de la visita'); return;
    }
    setSubmitting(true); setError(''); setSuccess('');
    try {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const dateStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
      const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}:00`;
      const endHour = now.getHours() + 1 > 23 ? 23 : now.getHours() + 1;
      const endTimeStr = `${pad(endHour)}:${pad(now.getMinutes())}:00`;
      const startDT = `${dateStr}T${timeStr}-04:00`;
      const endDT = `${dateStr}T${endTimeStr}-04:00`;
      const resp = await fetch(`${backendUrl}/api/bigin/events`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          event_title: eventTitle,
          start_datetime: startDT, end_datetime: endDT,
          description: eventDescription,
          location: eventModo,
          razon_de_visita: eventRazon,
          related_to_module: biginAccountId ? 'Accounts' : '',
          related_to_id: biginAccountId || '',
          client_name: selectedContact?.contact_name || '',
          created_by: user?.name || '',
          salesperson_name: getContactSalesperson(selectedContact)
        })
      });
      const data = await resp.json();
      if (data.success) { setSuccess('Visita creada exitosamente en Bigin'); resetForm(); }
      else { setError(data.detail || 'Error al crear la visita'); }
    } catch (e) { setError('Error de conexion'); }
    finally { setSubmitting(false); }
  };

  const handleSubmitTask = async () => {
    if (!taskSubject || !taskDueDate) { setError('Completa el asunto y fecha de vencimiento'); return; }
    setSubmitting(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`${backendUrl}/api/bigin/tasks`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          subject: taskSubject, due_date: taskDueDate,
          priority: taskPriority, description: taskDescription,
          related_to_module: biginAccountId ? 'Accounts' : '',
          related_to_id: biginAccountId || '',
          client_name: selectedContact?.contact_name || '',
          created_by: user?.name || '',
          salesperson_name: getContactSalesperson(selectedContact)
        })
      });
      const data = await resp.json();
      if (data.success) { setSuccess('Tarea creada exitosamente en Bigin'); resetForm(); }
      else { setError(data.detail || 'Error al crear la tarea'); }
    } catch (e) { setError('Error de conexion'); }
    finally { setSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="crm-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b bg-purple-600 text-white rounded-t-lg">
          <h2 className="text-lg font-bold">CRM - Zoho Bigin</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl leading-none">&times;</button>
        </div>

        <div className="p-4">
          {/* Client Search - uses Zoho Books contacts */}
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
            {loadingContacts && (
              <div className="mb-2 text-sm text-purple-600 animate-pulse">Cargando clientes... ({contacts.length} cargados)</div>
            )}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSelectedContact(null); setBiginAccountId(null); setShowDropdown(true); }}
              onFocus={() => { if (filteredContacts.length > 0) setShowDropdown(true); }}
              placeholder={loadingContacts ? "Cargando clientes..." : `Buscar entre ${contacts.length} clientes...`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              data-testid="crm-search-input"
              disabled={loadingContacts && contacts.length === 0}
            />
            {selectedContact && (
              <div className="mt-1 text-sm font-medium flex items-center gap-2">
                <span className="text-green-600">{selectedContact.contact_name}</span>
                {resolvingBigin && <span className="text-gray-400">Conectando con Bigin...</span>}
                {!resolvingBigin && biginAccountId && <span className="text-green-500 text-xs">Bigin OK</span>}
                {!resolvingBigin && !biginAccountId && <span className="text-yellow-600 text-xs">No encontrado en Bigin</span>}
              </div>
            )}
            {showDropdown && !selectedContact && filteredContacts.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredContacts.map(c => (
                  <button key={c.contact_id} onClick={() => selectContact(c)}
                    className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm border-b border-gray-100 last:border-0">
                    <span className="font-medium">{c.contact_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b mb-4">
            {[{id:'event',label:'Visita'},{id:'task',label:'Tarea'},{id:'note',label:'Nota'}].map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setError(''); setSuccess(''); }}
                className={`flex-1 py-2 text-sm font-medium text-center border-b-2 transition-colors ${
                  activeTab === tab.id ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Note Tab */}
          {activeTab === 'note' && (
            <div className="space-y-3">
              <input type="text" value={noteTitle} onChange={e => setNoteTitle(e.target.value)}
                placeholder="Titulo de la nota (opcional)" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
              <textarea value={noteContent} onChange={e => setNoteContent(e.target.value)}
                placeholder="Escribe tu nota aqui..." rows={3}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
              <div>
                <label className="block text-xs text-gray-500 mb-1">Adjuntar imagen (opcional)</label>
                <input type="file" accept="image/*" onChange={handleFileSelect}
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" />
                {noteFilePreview && (
                  <div className="mt-2 relative inline-block">
                    <img src={noteFilePreview} alt="Preview" className="h-20 rounded-lg border" />
                    <button onClick={() => { setNoteFile(null); setNoteFilePreview(null); }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">&times;</button>
                  </div>
                )}
              </div>
              <button onClick={handleSubmitNote} disabled={submitting || resolvingBigin}
                className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">
                {submitting ? 'Guardando...' : 'Guardar Nota'}
              </button>

              {/* Recent Notes */}
              {selectedContact && (
                <div className="border-t pt-3 mt-2">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Notas recientes (7 dias)</h4>
                  {loadingNotes ? (
                    <div className="text-xs text-gray-400 text-center py-2">Cargando notas...</div>
                  ) : recentNotes.length === 0 ? (
                    <div className="text-xs text-gray-400 text-center py-2">No hay notas recientes</div>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {recentNotes.map((note, idx) => {
                        const date = new Date(note.Created_Time || note.Modified_Time || '');
                        const dateStr = date.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        const timeStr = date.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
                        return (
                          <div key={note.id || idx} className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                            {note.Note_Title && <div className="text-xs font-semibold text-gray-700">{note.Note_Title}</div>}
                            <div className="text-xs text-gray-600 whitespace-pre-wrap">{note.Note_Content}</div>
                            <div className="text-xs text-gray-400 mt-1">{dateStr} {timeStr} - {note.Created_By?.name || ''}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Visita Tab */}
          {activeTab === 'event' && (
            <div className="space-y-3">
              <input type="text" value={eventTitle} onChange={e => setEventTitle(e.target.value)}
                placeholder="Titulo de la visita *" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Modo de Visita</label>
                  <select value={eventModo} onChange={e => setEventModo(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="Presencial">Presencial</option>
                    <option value="Telefonica">Telefonica</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Razon de Visita</label>
                  <select value={eventRazon} onChange={e => setEventRazon(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="Venta">Venta</option>
                    <option value="Cobranza">Cobranza</option>
                    <option value="Seguimiento">Seguimiento</option>
                    <option value="Sin Avance">Sin Avance</option>
                  </select>
                </div>
              </div>
              <textarea value={eventDescription} onChange={e => setEventDescription(e.target.value)}
                placeholder="Descripcion" rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
              <button onClick={handleSubmitEvent} disabled={submitting || resolvingBigin}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                {submitting ? 'Creando...' : 'Crear Visita'}
              </button>
            </div>
          )}

          {/* Task Tab */}
          {activeTab === 'task' && (
            <TaskTab backendUrl={backendUrl} user={user} selectedContact={selectedContact}
              biginAccountId={biginAccountId} resolvingBigin={resolvingBigin}
              getContactSalesperson={getContactSalesperson} contacts={contacts} />
          )}

          {/* Messages */}
          {error && <div className="mt-3 p-2 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
          {success && <div className="mt-3 p-2 bg-green-50 text-green-700 rounded-lg text-sm">{success}</div>}
        </div>
      </div>
    </div>
  );
};

// New Order Modal Component
const NewOrderModal = ({ isOpen, onClose, onOrderCreated, user, preSelectedClient }) => {
  const [formData, setFormData] = useState({
    customer_id: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    line_items: [{ item_id: '', quantity: 1, rate: 0, description: '' }]
  });

  const [contacts, setContacts] = useState([]);
  const [items, setItems] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Search states
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [itemSearches, setItemSearches] = useState(['']);
  const [showItemDropdowns, setShowItemDropdowns] = useState([false]);

  // Fetch contacts when modal opens
  useEffect(() => {
    if (isOpen && contacts.length === 0) {
      fetchContacts();
    }
  }, [isOpen, contacts.length]);

  // Auto-select preSelectedClient
  useEffect(() => {
    if (isOpen && preSelectedClient && contacts.length > 0 && !formData.customer_id) {
      const match = contacts.find(c => c.contact_id === preSelectedClient.contact_id);
      if (match) {
        handleCustomerSelect(match);
      }
    }
  }, [isOpen, preSelectedClient, contacts]);

  // Fetch items when modal opens
  useEffect(() => {
    if (isOpen && items.length === 0) {
      fetchItems();
    }
  }, [isOpen, items.length]);

  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      let allContacts = [];
      let currentPageNum = 1;
      let hasMorePages = true;

      console.log('Starting to fetch all contacts for new order...');

      while (hasMorePages && currentPageNum <= 30) { // Hard limit of 30 pages
        console.log(`Fetching contacts page ${currentPageNum}...`);
        
        // Build URL with user filtering params
        const params = new URLSearchParams({
          page: currentPageNum,
          per_page: 100,
          user_role: user.role,
          user_name: user.name
        });
        
        const response = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`,{
  method: 'GET',
  credentials: 'include' // This will send cookies and authentication headers
});

        if (!response.ok) {
          console.log(`API request failed with status ${response.status}, stopping fetch`);
          break;
        }

        const data = await response.json();

        if (data.success && data.data && data.data.contacts) {
          const contacts = data.data.contacts;

          // Break if no contacts returned
          if (contacts.length === 0) {
            console.log('No more contacts returned, stopping fetch');
            hasMorePages = false;
            break;
          }

          allContacts = [...allContacts, ...contacts];

          // Check if there are more pages
          const hasMorePageFlag = data.data.page_context?.has_more_page || false;
          const receivedFullPage = contacts.length === 100;

          hasMorePages = hasMorePageFlag && receivedFullPage;
          currentPageNum++;

          console.log(`Loaded ${allContacts.length} contacts so far...`);
        } else {
          console.log('API call failed or no data returned, stopping fetch');
          hasMorePages = false;
        }
      }

      console.log(`Total contacts fetched for new order: ${allContacts.length}`);
      setContacts(allContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Error al cargar los clientes');
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchItems = async () => {
    try {
      setLoadingItems(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      let allItems = [];
      let currentPageNum = 1;
      let hasMorePages = true;

      console.log('Starting to fetch all items for new order...');

      while (hasMorePages && currentPageNum <= 30) { // Hard limit of 30 pages
        console.log(`Fetching items page ${currentPageNum}...`);
        const response = await fetch(`${backendUrl}/api/zoho/items?page=${currentPageNum}&per_page=100`,{
  method: 'GET',
  credentials: 'include' // This will send cookies and authentication headers
});

        if (!response.ok) {
          console.log(`API request failed with status ${response.status}, stopping fetch`);
          break;
        }

        const data = await response.json();

        if (data.success && data.data && data.data.items) {
          const items = data.data.items;

          // Break if no items returned
          if (items.length === 0) {
            console.log('No more items returned, stopping fetch');
            hasMorePages = false;
            break;
          }

          allItems = [...allItems, ...items];

          // Check if there are more pages
          const hasMorePageFlag = data.data.page_context?.has_more_page || false;
          const receivedFullPage = items.length === 100;

          hasMorePages = hasMorePageFlag && receivedFullPage;
          currentPageNum++;

          console.log(`Loaded ${allItems.length} items so far...`);
        } else {
          console.log('API call failed or no data returned, stopping fetch');
          hasMorePages = false;
        }
      }

      console.log(`Total items fetched for new order: ${allItems.length}`);

      // Filter for active inventory items
      const activeItems = allItems.filter(item =>
        item.status === 'active' && item.item_type === 'inventory'
      );

      console.log(`Active inventory items: ${activeItems.length} out of ${allItems.length} total items`);
      setItems(activeItems);
    } catch (error) {
      console.error('Error fetching items:', error);
      setError('Error al cargar los productos');
    } finally {
      setLoadingItems(false);
    }
  };

  // Filter customers based on search
  const filteredCustomers = contacts.filter(contact => {
    const matchesSearch = contact.contact_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                         contact.company_name?.toLowerCase().includes(customerSearch.toLowerCase());

    // Role-based filtering - vendedora only sees clients assigned to them
    if (!isRecordAssignedToUser(contact, user)) {
      return false;
    }

    return matchesSearch;
  });

  // Filter items based on search for each line item
  const getFilteredItems = (searchTerm) => {
    return items.filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerSelect = (contact) => {
    setFormData(prev => ({ ...prev, customer_id: contact.contact_id }));
    setCustomerSearch(`${contact.contact_name} - ${contact.company_name || 'Sin empresa'}`);
    setShowCustomerDropdown(false);
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = { ...newLineItems[index], [field]: value };

    setFormData(prev => ({ ...prev, line_items: newLineItems }));
  };

  const handleItemSelect = (index, item) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = {
      ...newLineItems[index],
      item_id: item.item_id,
      rate: parseFloat(item.rate || 0),
      description: item.name || ''
    };

    setFormData(prev => ({ ...prev, line_items: newLineItems }));

    // Update search display
    const newItemSearches = [...itemSearches];
    newItemSearches[index] = item.name;
    setItemSearches(newItemSearches);

    // Close dropdown
    const newShowItemDropdowns = [...showItemDropdowns];
    newShowItemDropdowns[index] = false;
    setShowItemDropdowns(newShowItemDropdowns);
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { item_id: '', quantity: 1, rate: 0, description: '' }]
    }));
    setItemSearches([...itemSearches, '']);
    setShowItemDropdowns([...showItemDropdowns, false]);
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index)
      }));
      setItemSearches(itemSearches.filter((_, i) => i !== index));
      setShowItemDropdowns(showItemDropdowns.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    return formData.line_items.reduce((total, item) =>
      total + (parseFloat(item.quantity || 0) * parseFloat(item.rate || 0)), 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id) {
      setError('Por favor selecciona un cliente');
      return;
    }

    if (formData.line_items.length === 0 || !formData.line_items[0].item_id) {
      setError('Por favor agrega al menos un producto');
      return;
    }

    try {
      setCreating(true);
      setError('');

      // Add user information to the form data
      const orderData = {
        ...formData,
        created_by_user: user?.name || 'Usuario Sistema'
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/zoho/salesorders`, {
        method: 'POST',
        credentials:'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      const data = await response.json();

      if (data.success) {
        alert('¡Orden creada exitosamente!');
        onOrderCreated();
        onClose();
      } else {
        setError(data.detail || 'Error al crear la orden');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setError('Error al crear la orden. Por favor intenta de nuevo.');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Nueva Orden de Venta</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Customer Search */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cliente *
            </label>
            {loadingContacts ? (
              <div className="text-sm text-gray-500">Cargando clientes...</div>
            ) : (
              <div>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Buscar cliente por nombre o empresa..."
                  required
                />

                {showCustomerDropdown && filteredCustomers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCustomers.map(contact => (
                      <div
                        key={contact.contact_id}
                        onClick={() => handleCustomerSelect(contact)}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                      >
                        <div className="font-medium">{contact.contact_name}</div>
                        <div className="text-sm text-gray-600">{contact.company_name || 'Sin empresa'}</div>
                        {contact.email && (
                          <div className="text-xs text-gray-500">{contact.email}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* Line Items with Search */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Productos *
              </label>
              <button
                type="button"
                onClick={addLineItem}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                + Agregar Producto
              </button>
            </div>

            {loadingItems ? (
              <div className="text-sm text-gray-500">Cargando productos...</div>
            ) : (
              <div className="space-y-4">
                {formData.line_items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-2 relative">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Producto
                        </label>
                        <input
                          type="text"
                          value={itemSearches[index] || ''}
                          onChange={(e) => {
                            const newSearches = [...itemSearches];
                            newSearches[index] = e.target.value;
                            setItemSearches(newSearches);

                            const newDropdowns = [...showItemDropdowns];
                            newDropdowns[index] = true;
                            setShowItemDropdowns(newDropdowns);
                          }}
                          onFocus={() => {
                            const newDropdowns = [...showItemDropdowns];
                            newDropdowns[index] = true;
                            setShowItemDropdowns(newDropdowns);
                          }}
                          className="w-full p-2 border border-gray-300 rounded focus:ring-1 focus:ring-red-500 text-sm"
                          placeholder="Buscar producto..."
                          required
                        />

                        {showItemDropdowns[index] && getFilteredItems(itemSearches[index] || '').length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {getFilteredItems(itemSearches[index] || '').map(product => (
                              <div
                                key={product.item_id}
                                onClick={() => handleItemSelect(index, product)}
                                className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                              >
                                <div className="font-medium">{product.name}</div>
                                <div className="text-sm text-gray-600">{product.description || 'Sin descripción'}</div>
                                <div className="text-xs text-gray-500">
                                  Precio: ${parseFloat(product.rate || 0).toFixed(2)} | SKU: {product.sku || 'N/A'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Cantidad
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded focus:ring-1 focus:ring-red-500 text-sm"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Precio
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded focus:ring-1 focus:ring-red-500 text-sm"
                          required
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Subtotal: ${(parseFloat(item.quantity || 0) * parseFloat(item.rate || 0)).toFixed(2)}
                      </div>
                      {formData.line_items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notas
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Notas adicionales (opcional)"
            />
          </div>

          {/* Total */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-right">
              <span className="text-lg font-semibold text-gray-800">
                Total: ${calculateTotal().toFixed(2)}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={creating || loadingContacts || loadingItems}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? 'Creando...' : 'Crear Orden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Sales Orders Component - Enhanced with Full Data, Search, and Year Filter
const SalesOrdersSection = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [allOrdersLoaded, setAllOrdersLoaded] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Iniciando carga de órdenes...');
  const [fetchInProgress, setFetchInProgress] = useState(false);

  // New Order Modal States
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);

  const ordersPerPage = 20;

  // Year options for the filter (current year and 4 previous years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    // Prevent multiple simultaneous calls
    if (!fetchInProgress && !allOrdersLoaded) {
      fetchAllOrders();
    }
  }, [allOrdersLoaded, fetchInProgress]);

  const fetchAllOrders = async () => {
    if (allOrdersLoaded || fetchInProgress) return;

    try {
      setFetchInProgress(true);
      setLoading(true);
      setLoadingMessage('Iniciando carga de órdenes...');

      let allOrders = [];
      let currentPageNum = 1;
      let hasMorePages = true;
      let consecutiveEmptyPages = 0;
      const maxPages = 20; // Reduced from 30 to 20
      const maxConsecutiveEmptyPages = 3;

      console.log('Starting to fetch all sales orders...');

      while (hasMorePages && currentPageNum <= maxPages && consecutiveEmptyPages < maxConsecutiveEmptyPages) {
        setLoadingMessage(`Cargando página ${currentPageNum}... (${allOrders.length} órdenes hasta ahora)`);
        
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          console.log(`Fetching orders page ${currentPageNum}...`);
          
          // Add timeout to prevent hanging
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const response = await fetch(`${backendUrl}/api/zoho/salesorders?page=${currentPageNum}&per_page=100`, {
            method: 'GET',
            credentials: 'include',
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            console.log(`API request failed with status ${response.status}, stopping fetch`);
            break;
          }

          const data = await response.json();

          if (data.success && data.data && data.data.salesorders) {
            const orders = data.data.salesorders;
            
            // Check if we got orders
            if (orders.length === 0) {
              consecutiveEmptyPages++;
              console.log(`Page ${currentPageNum} returned 0 orders (consecutive empty pages: ${consecutiveEmptyPages})`);
              
              // If we have some orders already and get empty pages, we might be done
              if (allOrders.length > 0 && consecutiveEmptyPages >= 2) {
                console.log('Got multiple empty pages, assuming we reached the end');
                hasMorePages = false;
                break;
              }
            } else {
              consecutiveEmptyPages = 0; // Reset counter when we get data
              allOrders = [...allOrders, ...orders];
              console.log(`Added ${orders.length} orders from page ${currentPageNum}. Total: ${allOrders.length}`);
            }

            // Check if there are more pages
            hasMorePages = data.data.page_context?.has_more_page === true;
            
            // Safety check - if we have a lot of orders and no more pages indicated, stop
            if (!hasMorePages || allOrders.length >= 5000) {
              console.log(`Stopping fetch: hasMorePages=${hasMorePages}, totalOrders=${allOrders.length}`);
              break;
            }

          } else {
            console.log('API call failed or returned no data, stopping fetch');
            break;
          }

        } catch (fetchError) {
          if (fetchError.name === 'AbortError') {
            console.log(`Request timeout on page ${currentPageNum}, stopping fetch`);
          } else {
            console.error(`Error fetching page ${currentPageNum}:`, fetchError);
          }
          break;
        }

        currentPageNum++;
        
        // Small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log(`Finished loading ${allOrders.length} total orders`);
      
      // Transform orders to match the expected format
      const transformedOrders = allOrders.map(order => ({
        id: order.salesorder_id,
        orderNumber: order.salesorder_number || 'N/A',
        referenceNumber: order.reference_number || '',
        customerName: order.customer_name || 'Cliente Desconocido',
        branchName: order.branch_name || '',
        date: order.date,
        status: order.status || 'draft',
        total: parseFloat(order.total || 0),
        salespersonName: order.salesperson_name || 'Sin Vendedor',
        currencyCode: order.currency_code || 'USD'
      }));

      setOrders(transformedOrders);
      setTotalOrders(transformedOrders.length);
      setAllOrdersLoaded(true);
      setLoadingMessage(`¡Completado! ${transformedOrders.length} órdenes cargadas.`);

    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
      setLoadingMessage('Error al cargar las órdenes. Por favor, recarga la página.');
    } finally {
      setLoading(false);
      setFetchInProgress(false);
    }
  };

  // Open new order modal  
  const handleNewOrderClick = () => {
    setShowNewOrderModal(true);
  };

  // Filter orders by search term and selected year
  const filteredOrders = orders.filter(order => {
    const orderYear = new Date(order.date).getFullYear();
    const matchesYear = orderYear === selectedYear;

    // Exclude "cerradas" (closed) orders
    if (order.status && order.status.toLowerCase() === 'cerradas') {
      return false;
    }

    // Role-based filtering - vendedora only sees orders assigned to them
    if (user?.role === 'vendedora') {
      const salespersonName = order.salespersonName || '';
      const salespersonUpper = salespersonName.toUpperCase();
      
      // Check if order is assigned to current user
      let isAssignedToUser = false;
      
      if (user.name === 'Adriana Martinez') {
        isAssignedToUser = salespersonUpper.includes('ADRIANA MARTINEZ') || 
                           salespersonUpper.includes('ADRIANA');
      } else if (user.name === 'Roilan Narvaez') {
        isAssignedToUser = salespersonUpper.includes('ROILAN NARVAEZ') || 
                           salespersonUpper.includes('ROILAN');
      } else if (user.name === 'Maria Figuera') {
        isAssignedToUser = salespersonUpper.includes('ORIENTE') || 
                           salespersonUpper.includes('MARIA FIGUERA');
      } else if (user.name === 'Carlos Abril') {
        isAssignedToUser = salespersonUpper.includes('CARLOS ABRIL');
      } else if (user.name === 'Andrea Manrique') {
        isAssignedToUser = salespersonUpper.includes('ANDREA MANRIQUE');
      } else if (user.name === 'Kristian Madrid') {
        isAssignedToUser = salespersonUpper.includes('ADRIANA') || 
                           salespersonUpper.includes('ROILAN') || 
                           salespersonUpper.includes('CARLOS ABRIL') || 
                           salespersonUpper.includes('SUPERVISOR');
      } else if (user.name === 'Orlando') {
        isAssignedToUser = true;
      }
      
      // Also show orders without assignment (Sin asignar) for all vendedoras
      if (!isAssignedToUser && (salespersonName === 'Sin asignar' || salespersonName === '' || !salespersonName)) {
        isAssignedToUser = true;
      }
      
      if (!isAssignedToUser) {
        return false;
      }
    }

    if (!searchTerm) return matchesYear;

    const searchLower = searchTerm.toLowerCase();
    return matchesYear && (
      order.customerName.toLowerCase().includes(searchLower) ||
      order.orderNumber.toLowerCase().includes(searchLower) ||
      order.salespersonName.toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalFilteredPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'invoiced': return 'bg-purple-100 text-purple-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'Borrador';
      case 'sent': return 'Enviada';
      case 'confirmed': return 'Confirmada';
      case 'invoiced': return 'Facturada';
      case 'closed': return 'Cerrada';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-VE');
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">{loadingMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2">Órdenes de Venta</h1>
            <p className="text-gray-600 text-sm lg:text-base">
              Total en {selectedYear}: {filteredOrders.length} órdenes
              {user?.role === 'vendedora' && ' (asignadas a ti)'}
            </p>
          </div>
          <div>
            <button
              onClick={handleNewOrderClick}
              className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
            >
              <PlusIcon />
              <span>Nueva Orden</span>
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por cliente, número de orden o vendedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="w-full md:w-48">
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FECHA</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NÚMERO</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLIENTE</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ESTADO</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TOTAL</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VENDEDOR</th>
                  <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ACCIONES</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(order.date)}
                    </td>
                    <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{order.orderNumber}</div>
                      {order.referenceNumber && (
                        <div className="text-xs text-gray-500">Ref: {order.referenceNumber}</div>
                      )}
                    </td>
                    <td className="px-3 lg:px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 max-w-xs truncate">{order.customerName}</div>
                      {order.branchName && (
                        <div className="text-xs text-gray-500 max-w-xs truncate">{order.branchName}</div>
                      )}
                    </td>
                    <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                    </td>
                    <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(order.total)}
                    </td>
                    <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {order.salespersonName}
                    </td>
                    <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button className="text-red-600 hover:text-red-900 px-2 py-1 rounded text-xs">Ver</button>
                        <button className="text-blue-600 hover:text-blue-900 px-2 py-1 rounded text-xs">Editar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalFilteredPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{indexOfFirstOrder + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(indexOfLastOrder, filteredOrders.length)}</span> de{' '}
                    <span className="font-medium">{filteredOrders.length}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    {[...Array(Math.min(5, totalFilteredPages))].map((_, i) => {
                      const pageNumber = i + Math.max(1, currentPage - 2);
                      if (pageNumber <= totalFilteredPages) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pageNumber === currentPage
                                ? 'z-10 bg-red-50 border-red-500 text-red-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }
                      return null;
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalFilteredPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* New Order Modal */}
        <NewOrderModal
          isOpen={showNewOrderModal}
          onClose={() => setShowNewOrderModal(false)}
          onOrderCreated={() => {
            setAllOrdersLoaded(false);
            fetchAllOrders();
          }}
          user={user}
        />
      </div>
    </div>
  );
};

// Generic Section Component

// Users Section Component
const UsersSection = () => {
  const [sendingReports, setSendingReports] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const ALL_MODULES = [
    { id: 'dashboard', label: 'Panel Integral' },
    { id: 'analyticsEmbed', label: 'Panel Vendedor' },
    { id: 'salesOrders', label: 'Ordenes de Venta' },
    { id: 'clients', label: 'Clientes' },
    { id: 'inventory', label: 'Inventario' },
    { id: 'invoices', label: 'Facturas' },
    { id: 'payments', label: 'Pagos' },
    { id: 'creditNotes', label: 'Notas de Credito' },
    { id: 'logistics', label: 'Logistica' },
    { id: 'driverDeliveries', label: 'Entregas (Chofer)' },
    { id: 'users', label: 'Gestion Usuarios' }
  ];

  const ROLES = [
    { id: 'admin', label: 'Administrador' },
    { id: 'vendedora', label: 'Vendedora' },
    { id: 'aprobador', label: 'Aprobador' },
    { id: 'logistica', label: 'Logistica' },
    { id: 'chofer', label: 'Chofer' }
  ];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/users`);
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleToggleStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    await fetch(`${backendUrl}/api/users/${u.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus })
    });
    fetchUsers();
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`¿Eliminar permanentemente a ${u.name}?`)) return;
    await fetch(`${backendUrl}/api/users/${u.id}`, { method: 'DELETE' });
    fetchUsers();
  };

  const handleSendDailyReports = async () => {
    setSendingReports(true);
    setReportMessage('');
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      const response = await fetch(`${backendUrl}/api/reports/daily/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal
      });
      clearTimeout(timeoutId);
      const data = await response.json();
      setReportMessage(data.success ? 'Reportes enviados exitosamente' : 'Error: ' + (data.error || ''));
    } catch (error) {
      setReportMessage(error.name === 'AbortError' ? 'Timeout - los reportes pueden haberse enviado' : 'Error de conexion');
    } finally { setSendingReports(false); }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      vendedora: 'bg-blue-100 text-blue-800',
      aprobador: 'bg-yellow-100 text-yellow-800',
      logistica: 'bg-teal-100 text-teal-800',
      chofer: 'bg-orange-100 text-orange-800'
    };
    const labels = { admin: 'Admin', vendedora: 'Vendedora', aprobador: 'Aprobador', logistica: 'Logistica', chofer: 'Chofer' };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[role] || 'bg-gray-100 text-gray-800'}`}>{labels[role] || role}</span>;
  };

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-800" data-testid="users-title">Gestion de Usuarios</h1>
            <p className="text-gray-600 text-sm">{users.length} usuarios en el sistema</p>
          </div>
          <button onClick={() => { setEditingUser(null); setShowUserModal(true); }} data-testid="add-user-btn"
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
            + Nuevo Usuario
          </button>
        </div>

        {/* Daily Reports Card */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg p-4 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-bold">Reportes Diarios</h2>
              <p className="text-red-100 text-xs">Auto: Lun-Vie 9:00 PM</p>
            </div>
            <button onClick={handleSendDailyReports} disabled={sendingReports}
              className={`px-4 py-2 rounded-lg font-semibold text-sm ${sendingReports ? 'bg-gray-300 text-gray-600' : 'bg-white text-red-600 hover:bg-red-50'}`}>
              {sendingReports ? 'Enviando...' : 'Enviar Ahora'}
            </button>
          </div>
          {reportMessage && <p className="mt-2 text-sm bg-white/20 rounded p-2">{reportMessage}</p>}
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="text-center py-12"><LoadingSpinner /></div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permisos</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map(u => (
                  <tr key={u.id || u.email} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-medium">{(u.name || '?').split(' ').map(n => n[0]).join('').slice(0,2)}</span>
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{u.name}</div>
                          <div className="text-xs text-gray-500 truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getRoleBadge(u.role)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {u.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.permissions || []).slice(0, 3).map(p => (
                          <span key={p} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{p}</span>
                        ))}
                        {(u.permissions || []).length > 3 && <span className="text-xs text-gray-400">+{u.permissions.length - 3}</span>}
                        {(!u.permissions || u.permissions.length === 0) && <span className="text-xs text-gray-400">Por defecto</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => { setEditingUser(u); setShowUserModal(true); }}
                          className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">Editar</button>
                        <button onClick={() => handleToggleStatus(u)}
                          data-testid={`user-toggle-status-${u.id}`}
                          className={`px-2 py-1 text-xs rounded ${u.status === 'active' ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                          {u.status === 'active' ? 'Desactivar' : 'Activar'}
                        </button>
                        <button onClick={() => handleDeleteUser(u)}
                          className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* User Modal */}
        {showUserModal && (
          <UserFormModal
            user={editingUser}
            roles={ROLES}
            modules={ALL_MODULES}
            onClose={() => { setShowUserModal(false); setEditingUser(null); }}
            onSaved={() => { setShowUserModal(false); setEditingUser(null); fetchUsers(); }}
          />
        )}
      </div>
    </div>
  );
};

// User Create/Edit Modal
const UserFormModal = ({ user: editUser, roles, modules, onClose, onSaved }) => {
  const [name, setName] = useState(editUser?.name || '');
  const [email, setEmail] = useState(editUser?.email || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(editUser?.role || 'vendedora');
  const [permissions, setPermissions] = useState(editUser?.permissions || []);
  const [status, setStatus] = useState(editUser?.status || 'active');
  const [vendorType, setVendorType] = useState(editUser?.vendor_type || 'individual');
  const [supervisedSalespeople, setSupervisedSalespeople] = useState(editUser?.supervised_salespeople || []);
  const [zohoName, setZohoName] = useState(editUser?.zoho_name || '');
  const [zohoSalespeople, setZohoSalespeople] = useState([]);
  const [loadingSalespeople, setLoadingSalespeople] = useState(false);
  const [saving, setSaving] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch Zoho salespeople
  useEffect(() => {
    const fetchSalespeople = async () => {
      setLoadingSalespeople(true);
      try {
        const resp = await fetch(`${backendUrl}/api/zoho/salespeople`);
        const data = await resp.json();
        if (data.success) setZohoSalespeople(data.salespeople || []);
      } catch (e) { console.error(e); }
      finally { setLoadingSalespeople(false); }
    };
    if (role === 'vendedora') fetchSalespeople();
  }, [role]);

  const togglePermission = (moduleId) => {
    setPermissions(prev => prev.includes(moduleId) ? prev.filter(p => p !== moduleId) : [...prev, moduleId]);
  };
  const toggleSalesperson = (sp) => {
    setSupervisedSalespeople(prev => prev.includes(sp) ? prev.filter(s => s !== sp) : [...prev, sp]);
  };
  const selectAllPermissions = () => setPermissions(modules.map(m => m.id));
  const clearAllPermissions = () => setPermissions([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) { alert('Nombre y email son obligatorios'); return; }
    if (!editUser && !password) { alert('Contraseña obligatoria para nuevo usuario'); return; }
    setSaving(true);
    try {
      const payload = { name, email, role, permissions, status, vendor_type: vendorType, supervised_salespeople: supervisedSalespeople, zoho_name: zohoName };
      if (password) payload.password = password;

      const url = editUser ? `${backendUrl}/api/users/${editUser.id}` : `${backendUrl}/api/users`;
      const method = editUser ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (resp.ok) { onSaved(); }
      else {
        const err = await resp.json();
        alert(err.detail || 'Error al guardar');
      }
    } catch (e) { alert('Error de conexion'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-800 mb-4" data-testid="user-modal-title">
          {editUser ? `Editar: ${editUser.name}` : 'Nuevo Usuario'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} required
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contraseña {editUser && <span className="text-gray-400">(dejar vacio para no cambiar)</span>}
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder={editUser ? '••••••••' : 'Contraseña'}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
              <select value={role} onChange={e => setRole(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500">
                {roles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500">
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
          </div>

          {/* Vendor Configuration - only for vendedora role */}
          {role === 'vendedora' && (
            <div className="mb-3 border border-blue-200 rounded-lg p-3 bg-blue-50">
              <label className="block text-sm font-bold text-blue-800 mb-2">Configuracion de Vendedor</label>
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Vendedor</label>
                <select value={vendorType} onChange={e => setVendorType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="individual">Vendedor Individual</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="gerente">Gerente de Ventas (ve todo)</option>
                </select>
              </div>

              {vendorType !== 'gerente' && (
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nombre en Zoho Books (cf_vendedor)</label>
                  <select value={zohoName} onChange={e => setZohoName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="">-- Seleccionar --</option>
                    {zohoSalespeople.map(sp => <option key={sp} value={sp}>{sp}</option>)}
                  </select>
                </div>
              )}

              {vendorType === 'supervisor' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Vendedores que Supervisa ({supervisedSalespeople.length} seleccionados)
                  </label>
                  {loadingSalespeople ? (
                    <p className="text-xs text-gray-400">Cargando vendedores de Zoho...</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto bg-white rounded-lg p-2 border">
                      {zohoSalespeople.map(sp => (
                        <label key={sp} className="flex items-center gap-1.5 cursor-pointer text-xs py-0.5">
                          <input type="checkbox" checked={supervisedSalespeople.includes(sp)}
                            onChange={() => toggleSalesperson(sp)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                          <span className={supervisedSalespeople.includes(sp) ? 'text-blue-700 font-medium' : 'text-gray-600'}>{sp}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Permissions */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Permisos de Modulos</label>
              <div className="flex gap-2">
                <button type="button" onClick={selectAllPermissions} className="text-xs text-blue-600 hover:underline">Todos</button>
                <button type="button" onClick={clearAllPermissions} className="text-xs text-red-600 hover:underline">Ninguno</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
              {modules.map(m => (
                <label key={m.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={permissions.includes(m.id)}
                    onChange={() => togglePermission(m.id)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500" />
                  <span className="text-gray-700">{m.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} disabled={saving}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="save-user-btn"
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Invoices Section Component  
const InvoicesSection = ({ user }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [approvingId, setApprovingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const invoicesPerPage = 20;

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Build URL with user filtering params
      const params = new URLSearchParams({
        page: 1,
        per_page: 100,
        user_role: user.role,
        user_name: user.name
      });
      
      const response = await fetch(`${backendUrl}/api/zoho/invoices?${params.toString()}`,{
  method: 'GET',
  credentials: 'include' // This will send cookies and authentication headers
});
      const data = await response.json();

      if (data.success && data.data && data.data.invoices) {
        setInvoices(data.data.invoices);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    // Role-based filtering - vendedora only sees invoices assigned to them
    if (!isRecordAssignedToUser(invoice, user)) {
      return false;
    }

    // Status filter
    if (statusFilter !== 'all' && invoice.status !== statusFilter) {
      return false;
    }

    return invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleApproveInvoice = async (invoiceId) => {
    if (!window.confirm('¿Estás seguro de aprobar esta factura?')) return;
    try {
      setApprovingId(invoiceId);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/zoho/invoices/${invoiceId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(prev => prev.map(inv =>
          inv.invoice_id === invoiceId ? { ...inv, status: 'draft' } : inv
        ));
        alert('Factura aprobada exitosamente');
      } else {
        alert('Error al aprobar la factura: ' + (data.detail || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error approving invoice:', error);
      alert('Error al aprobar la factura');
    } finally {
      setApprovingId(null);
    }
  };

  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * invoicesPerPage,
    currentPage * invoicesPerPage
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'sent': { label: 'Enviada', color: 'bg-blue-100 text-blue-800' },
      'paid': { label: 'Pagada', color: 'bg-green-100 text-green-800' },
      'overdue': { label: 'Vencida', color: 'bg-red-100 text-red-800' },
      'draft': { label: 'Borrador', color: 'bg-gray-100 text-gray-800' },
      'pending_approval': { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800' }
    };

    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <LoadingSpinner />
              <p className="mt-4 text-gray-600">Cargando facturas...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestión de Facturas</h1>
          <p className="text-gray-600">
            Total: {filteredInvoices.length} facturas
            {user?.role === 'vendedora' && ' (asignadas a ti)'}
          </p>
        </div>

        {/* Status filter tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { key: 'all', label: 'Todas' },
            { key: 'pending_approval', label: 'Por Aprobar' },
            { key: 'sent', label: 'Enviadas' },
            { key: 'overdue', label: 'Vencidas' },
            { key: 'paid', label: 'Pagadas' },
            { key: 'draft', label: 'Borrador' },
          ].map(tab => {
            const count = invoices.filter(inv => isRecordAssignedToUser(inv, user) && (tab.key === 'all' || inv.status === tab.key)).length;
            return (
              <button
                key={tab.key}
                data-testid={`filter-${tab.key}`}
                onClick={() => { setStatusFilter(tab.key); setCurrentPage(1); }}
                className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${
                  statusFilter === tab.key
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label} ({count})
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <input
            type="text"
            placeholder="Buscar por número de factura o cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Número
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedInvoices.map((invoice) => (
                <tr key={invoice.invoice_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invoice.invoice_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invoice.customer_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(invoice.date).toLocaleDateString('es-VE')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(invoice.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {invoice.status === 'pending_approval' && (
                      <button
                        data-testid={`approve-invoice-${invoice.invoice_id}`}
                        onClick={() => handleApproveInvoice(invoice.invoice_id)}
                        disabled={approvingId === invoice.invoice_id}
                        className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed mr-2 transition-colors"
                      >
                        {approvingId === invoice.invoice_id ? (
                          <><svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Aprobando...</>
                        ) : 'Aprobar'}
                      </button>
                    )}
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.invoice_id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-900 text-xs"
                    >
                      PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Credit Notes Section Component
const CreditNotesSection = ({ user }) => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCreditNotes();
  }, []);

  const fetchCreditNotes = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/zoho/creditnotes?page=1&per_page=100`,{
  method: 'GET',
  credentials: 'include' // This will send cookies and authentication headers
});
      const data = await response.json();

      if (data.success && data.data && data.data.creditnotes) {
        setCreditNotes(data.data.creditnotes);
      }
    } catch (error) {
      console.error('Error fetching credit notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <LoadingSpinner />
              <p className="mt-4 text-gray-600">Cargando notas de crédito...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Notas de Crédito</h1>
          <p className="text-gray-600">
            Total: {creditNotes.filter(creditNote => isRecordAssignedToUser(creditNote, user)).length} notas de crédito
            {user?.role === 'vendedora' && ' (asignadas a ti)'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Número
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Monto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {creditNotes.filter(creditNote => isRecordAssignedToUser(creditNote, user)).map((creditNote) => (
                <tr key={creditNote.creditnote_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {creditNote.creditnote_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {creditNote.customer_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(creditNote.date).toLocaleDateString('es-VE')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(creditNote.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {creditNote.status}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-red-600 hover:text-red-900 mr-3">Ver</button>
                    <button className="text-blue-600 hover:text-blue-900">Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Client Management Section - Full Implementation with Zoho Books Integration
const ClientsSection = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [allClientsLoaded, setAllClientsLoaded] = useState(false);
  const clientsPerPage = 20;

  useEffect(() => {
    fetchAllClients();
  }, []);

  const fetchAllClients = async () => {
    if (allClientsLoaded) return;

    try {
      setLoading(true);
      let allClients = [];
      let currentPageNum = 1;
      let hasMorePages = true;

      console.log('Starting to fetch all clients...');

      while (hasMorePages && currentPageNum <= 50) { // Add safety limit
        console.log(`Fetching clients page ${currentPageNum}...`);
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        
        // Build URL with user filtering params
        const params = new URLSearchParams({
          page: currentPageNum,
          per_page: 100,
          user_role: user.role,
          user_name: user.name
        });
        
        const response = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`,{
          method: 'GET',
          credentials: 'include'
        });

        if (!response.ok) {
          console.log(`API request failed with status ${response.status}, stopping fetch`);
          break;
        }

        const data = await response.json();

        if (data.success && data.data && data.data.contacts) {
          const contacts = data.data.contacts;
          
          // Break if no contacts returned
          if (contacts.length === 0) {
            console.log('No more contacts returned, stopping fetch');
            break;
          }

          allClients = [...allClients, ...contacts];

          hasMorePages = data.data.page_context?.has_more_page || false;
          currentPageNum++;

          console.log(`Loaded ${allClients.length} clients so far...`);
        } else {
          console.log('API call failed or no data returned, stopping fetch');
          break;
        }
      }

      console.log(`Finished loading ${allClients.length} total clients`);
      setClients(allClients);
      setTotalClients(allClients.length);
      setAllClientsLoaded(true);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on search term and user role
  const filteredClients = clients.filter(client => {
    const matchesSearch = client.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Role-based filtering - vendedora only sees clients assigned to them
    if (!isRecordAssignedToUser(client, user)) {
      return false;
    }

    return matchesSearch;
  });

  // Paginate filtered clients
  const startIndex = (currentPage - 1) * clientsPerPage;
  const endIndex = startIndex + clientsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setShowClientModal(true);
  };

  const canEdit = (client) => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'vendedora') {
      return client.custom_fields?.some(field =>
        field.value?.includes('ADRIANA MARTINEZ') || field.value?.includes('ADRIANA')
      );
    }
    return false;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(parseFloat(amount));
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <LoadingSpinner />
              <p className="mt-4 text-gray-600">Cargando clientes desde Zoho Books...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestión de Clientes</h1>
          <p className="text-gray-600">
            Total: {filteredClients.length} clientes
            {user?.role === 'vendedora' && ' (asignados a ti)'}
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por empresa, contacto o email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <button className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              <SearchIcon />
            </button>
          </div>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contacto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teléfono
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedClients.map((client) => (
                  <tr key={client.contact_id} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {client.company_name || 'Sin empresa'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {client.contact_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.contact_name || 'Sin nombre'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.email || 'Sin email'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.phone || 'Sin teléfono'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(client.outstanding_receivable_amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleClientClick(client)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Ver
                      </button>
                      {canEdit(client) && (
                        <button className="text-blue-600 hover:text-blue-900">
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(endIndex, filteredClients.length)}</span> de{' '}
                      <span className="font-medium">{filteredClients.length}</span> resultados
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${currentPage === pageNumber
                              ? 'bg-red-600 text-white'
                              : 'text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Client Detail Modal */}
        {showClientModal && selectedClient && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Detalles del Cliente</h3>
                  <button
                    onClick={() => setShowClientModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Empresa:</span>
                    <p className="text-gray-900">{selectedClient.company_name || 'N/A'}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">Contacto:</span>
                    <p className="text-gray-900">{selectedClient.contact_name || 'N/A'}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">Email:</span>
                    <p className="text-gray-900">{selectedClient.email || 'N/A'}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">Teléfono:</span>
                    <p className="text-gray-900">{selectedClient.phone || 'N/A'}</p>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">Balance Pendiente:</span>
                    <p className="text-gray-900">{formatCurrency(selectedClient.outstanding_receivable_amount)}</p>
                  </div>

                  {selectedClient.billing_address && (
                    <div>
                      <span className="font-medium text-gray-700">Dirección:</span>
                      <p className="text-gray-900">
                        {selectedClient.billing_address.street}, {selectedClient.billing_address.city}
                        {selectedClient.billing_address.state && `, ${selectedClient.billing_address.state}`}
                        {selectedClient.billing_address.country && `, ${selectedClient.billing_address.country}`}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-6 space-x-3">
                  <button
                    onClick={() => setShowClientModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cerrar
                  </button>
                  {canEdit(selectedClient) && (
                    <button className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
                      Editar Cliente
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Application Component
// Auto-logout hook for inactivity detection
const useAutoLogout = (onLogout, timeoutMinutes = 20) => {
  const [showWarning, setShowWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);

  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    setShowWarning(false);

    // Set warning timer (2 minutes before logout)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
    }, (timeoutMinutes - 2) * 60 * 1000);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      onLogout();
    }, timeoutMinutes * 60 * 1000);
  }, [onLogout, timeoutMinutes]);

  const extendSession = () => {
    resetTimer();
  };

  useEffect(() => {
    // Activity event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const activityHandler = () => {
      if (!showWarning) {
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, activityHandler, true);
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, activityHandler, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [resetTimer, showWarning]);

  return { showWarning, extendSession };
};

// Warning Modal Component
const InactivityWarningModal = ({ onExtend, onLogout }) => {
  const [countdown, setCountdown] = useState(120); // 2 minutes in seconds

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [onLogout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            ⚠️
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Sesión por Expirar
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Tu sesión expirará en <span className="font-bold text-red-600">{formatTime(countdown)}</span> por inactividad.
          </p>
          <div className="flex space-x-3">
            <button
              onClick={onExtend}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Extender Sesión
            </button>
            <button
              onClick={onLogout}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// LOGISTICS MODULE COMPONENTS
// ============================================================================

const LogisticsSection = ({ user }) => {
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [routesTotal, setRoutesTotal] = useState(0);
  const [routesHasMore, setRoutesHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showCreateRouteModal, setShowCreateRouteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedRoute, setExpandedRoute] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, driversRes, routesRes] = await Promise.all([
        fetch(`${backendUrl}/api/logistics/invoices/pending`),
        fetch(`${backendUrl}/api/logistics/drivers`),
        fetch(`${backendUrl}/api/logistics/routes?limit=100&offset=0`)
      ]);
      const [invoicesData, driversData, routesData] = await Promise.all([
        invoicesRes.json(), driversRes.json(), routesRes.json()
      ]);
      setPendingInvoices(invoicesData.invoices || []);
      setDrivers(driversData.drivers || []);
      setRoutes(routesData.routes || []);
      setRoutesTotal(routesData.total ?? (routesData.routes || []).length);
      setRoutesHasMore(!!routesData.has_more);
    } catch (error) {
      console.error('Error fetching logistics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreRoutes = async () => {
    try {
      setLoadingMore(true);
      const res = await fetch(`${backendUrl}/api/logistics/routes?limit=100&offset=${routes.length}`);
      const data = await res.json();
      setRoutes(prev => [...prev, ...(data.routes || [])]);
      setRoutesTotal(data.total ?? routesTotal);
      setRoutesHasMore(!!data.has_more);
    } catch (e) {
      console.error('Error loading more routes:', e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev =>
      prev.includes(invoiceId) ? prev.filter(id => id !== invoiceId) : [...prev, invoiceId]
    );
  };

  const handleCreateRoute = () => {
    if (selectedInvoices.length === 0) { alert('Selecciona al menos una factura'); return; }
    setShowCreateRouteModal(true);
  };

  const handleCancelRoute = async (routeId) => {
    const reason = window.prompt('Cancelar este viaje conserva el viaje y su historial, y libera solo las facturas NO entregadas.\n\nMotivo de la cancelación (opcional):', '');
    if (reason === null) return; // user dismissed
    try {
      const resp = await fetch(`${backendUrl}/api/logistics/routes/${routeId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        const parts = [];
        if (data.released?.length) parts.push(`${data.released.length} factura(s) liberadas`);
        if (data.kept_delivered?.length) parts.push(`${data.kept_delivered.length} entregada(s) protegidas`);
        if (data.blocked?.length) parts.push(`${data.blocked.length} sin liberar (revisar)`);
        alert('Viaje cancelado. ' + (parts.join(' · ') || ''));
        fetchData();
      } else if (resp.status === 403) {
        alert('No tienes permiso para cancelar viajes.');
      } else {
        alert('Error al cancelar: ' + (typeof data.detail === 'string' ? data.detail : ''));
      }
    } catch (e) { alert('Error al cancelar'); }
  };

  const handleStartRoute = async (routeId) => {
    try {
      await fetch(`${backendUrl}/api/logistics/routes/${routeId}/start`, { method: 'PUT' });
      fetchData();
    } catch (e) { alert('Error al iniciar viaje'); }
  };

  const filteredInvoices = pendingInvoices.filter(inv => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!inv.invoice_number?.toLowerCase().includes(term) &&
          !inv.customer_name?.toLowerCase().includes(term)) return false;
    }
    return true;
  });

  const filteredRoutes = routes.filter(route => {
    if (filterDriver && route.driver_id !== filterDriver) return false;
    if (filterStatus && route.status !== filterStatus) return false;
    return true;
  });

  const formatCurrency = (amount) => `$${parseFloat(amount || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}`;

  const getRouteStatusBadge = (status) => {
    const map = {
      'pending': { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-800' },
      'in_progress': { label: 'En Ruta', cls: 'bg-blue-100 text-blue-800' },
      'completed': { label: 'Completado', cls: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'Cancelado', cls: 'bg-red-100 text-red-800' }
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${s.cls}`}>{s.label}</span>;
  };

  const getDeliveryStatusBadge = (status) => {
    const map = {
      'assigned': { label: 'Asignada', cls: 'bg-blue-100 text-blue-800' },
      'in_transit': { label: 'En Ruta', cls: 'bg-indigo-100 text-indigo-800' },
      'delivered': { label: 'Entregada', cls: 'bg-green-100 text-green-800' },
      'failed': { label: 'Fallida', cls: 'bg-red-100 text-red-800' },
      'rescheduled': { label: 'Reprogramada', cls: 'bg-orange-100 text-orange-800' }
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${s.cls}`}>{s.label}</span>;
  };

  if (loading) return (
    <div className="p-6 bg-gray-50 min-h-screen flex justify-center items-center">
      <div className="text-center"><LoadingSpinner /><p className="mt-4 text-gray-600">Cargando datos de logistica...</p></div>
    </div>
  );

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-1" data-testid="logistics-title">Operaciones Logisticas</h1>
          <p className="text-gray-600">Gestion de despachos y entregas de facturas</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <nav className="flex border-b border-gray-200">
            {[
              { key: 'pending', label: `Facturas para Despacho (${pendingInvoices.length})` },
              { key: 'routes', label: `Viajes (${routesTotal})` }
            ].map(tab => (
              <button key={tab.key} data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 font-medium text-sm ${activeTab === tab.key ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500 hover:text-gray-700'}`}
              >{tab.label}</button>
            ))}
          </nav>
        </div>

        {/* PENDING INVOICES TAB */}
        {activeTab === 'pending' && (
          <div>
            {/* Actions bar */}
            {selectedInvoices.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center justify-between">
                <p className="text-red-800 text-sm"><strong>{selectedInvoices.length}</strong> facturas seleccionadas</p>
                <button data-testid="create-route-btn" onClick={handleCreateRoute}
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
                  Crear Viaje
                </button>
              </div>
            )}

            {/* Search */}
            <div className="bg-white rounded-lg shadow-md p-3 mb-4">
              <input type="text" placeholder="Buscar por # factura o cliente..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" />
            </div>

            {filteredInvoices.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-500">No hay facturas pendientes de despacho</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input type="checkbox" data-testid="select-all-invoices"
                          onChange={(e) => setSelectedInvoices(e.target.checked ? filteredInvoices.map(i => i.invoice_id) : [])}
                          checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0} />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"># Factura</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map(inv => (
                      <tr key={inv.invoice_id} className={`hover:bg-gray-50 ${selectedInvoices.includes(inv.invoice_id) ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selectedInvoices.includes(inv.invoice_id)}
                            onChange={() => toggleInvoiceSelection(inv.invoice_id)} />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{inv.invoice_number}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{inv.customer_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{inv.date}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatCurrency(inv.total)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{inv.salesperson_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ROUTES TAB */}
        {activeTab === 'routes' && (
          <div>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-3 mb-4 flex flex-wrap gap-3">
              <select value={filterDriver} onChange={(e) => setFilterDriver(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500">
                <option value="">Todos los Choferes</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500">
                <option value="">Todos los Estados</option>
                <option value="pending">Pendiente</option>
                <option value="in_progress">En Ruta</option>
                <option value="completed">Completado</option>
              </select>
            </div>

            {filteredRoutes.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-500">No hay viajes creados</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRoutes.map(route => (
                  <div key={route.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    {/* Route Header */}
                    <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedRoute(expandedRoute === route.id ? null : route.id)}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-base font-semibold text-gray-800">{route.route_name}</h3>
                            {getRouteStatusBadge(route.status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <span>Chofer: <strong>{route.driver_name}</strong></span>
                            {route.dispatch_date && <span>Despacho: <strong>{route.dispatch_date}</strong></span>}
                            <span>Facturas: <strong>{route.delivery_count || route.invoice_ids?.length || 0}</strong></span>
                            <span className="text-green-700">Entregadas: {route.delivered_count || 0}</span>
                            {(route.failed_count || 0) > 0 && <span className="text-red-700">Fallidas: {route.failed_count}</span>}
                            {(route.pending_count || 0) > 0 && <span className="text-yellow-700">Pendientes: {route.pending_count}</span>}
                            <span className="font-semibold">Total: {formatCurrency(route.total_amount || 0)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {route.status === 'pending' && (
                            <>
                              <button onClick={(e) => { e.stopPropagation(); handleStartRoute(route.id); }}
                                className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700">Iniciar</button>
                              <button onClick={(e) => { e.stopPropagation(); handleCancelRoute(route.id); }}
                                data-testid={`cancel-route-${route.id}`}
                                className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300">Cancelar viaje</button>
                            </>
                          )}
                          <span className="text-gray-400">{expandedRoute === route.id ? '\u25B2' : '\u25BC'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Expanded deliveries */}
                    {expandedRoute === route.id && route.deliveries && (
                      <div className="border-t border-gray-200">
                        <table className="min-w-full divide-y divide-gray-100">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"># Factura</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Direccion</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Recibido Por</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Evidencia</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {route.deliveries.map(del_ => (
                              <tr key={del_.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm font-medium">{del_.invoice_number}</td>
                                <td className="px-4 py-2 text-sm">{del_.customer_name}</td>
                                <td className="px-4 py-2 text-sm text-gray-500">{del_.address || '-'}</td>
                                <td className="px-4 py-2 text-sm">{formatCurrency(del_.amount)}</td>
                                <td className="px-4 py-2">{getDeliveryStatusBadge(del_.status)}</td>
                                <td className="px-4 py-2 text-sm text-gray-700">{del_.received_by || '-'}</td>
                                <td className="px-4 py-2 text-sm">
                                  {(del_.evidence_photos && del_.evidence_photos.length > 0) ? (
                                    <div className="flex gap-1">
                                      {del_.evidence_photos.map((url, i) => (
                                        <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                                          className="text-blue-600 hover:underline text-xs">Foto {i+1}</a>
                                      ))}
                                    </div>
                                  ) : del_.evidence_photo ? (
                                    <a href={del_.evidence_photo} target="_blank" rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline text-xs">Ver Foto</a>
                                  ) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {/* Customers summary */}
                        {route.customers && route.customers.length > 0 && (
                          <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
                            Clientes: {route.customers.join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {routesHasMore && (
              <div className="mt-4 text-center">
                <button onClick={loadMoreRoutes} disabled={loadingMore}
                  data-testid="load-more-routes-btn"
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50">
                  {loadingMore ? 'Cargando...' : `Cargar más (${routes.length} de ${routesTotal})`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Create Route Modal */}
        {showCreateRouteModal && (
          <CreateRouteModal
            isOpen={showCreateRouteModal}
            onClose={() => setShowCreateRouteModal(false)}
            drivers={drivers}
            selectedInvoices={selectedInvoices}
            invoicesList={pendingInvoices.filter(i => selectedInvoices.includes(i.invoice_id))}
            userName={user.name}
            onRouteCreated={() => {
              setShowCreateRouteModal(false);
              setSelectedInvoices([]);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};

// Create Route Modal
const CreateRouteModal = ({ isOpen, onClose, drivers, selectedInvoices, invoicesList, userName, onRouteCreated }) => {
  const [routeName, setRouteName] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  // Stable per-submission key so timeouts/double-clicks never create two routes
  const idempotencyKeyRef = React.useRef(
    (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `route-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!routeName || !selectedDriver) { alert('Completa todos los campos'); return; }
    if (loading) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/logistics/routes?user_name=${encodeURIComponent(userName)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            route_name: routeName,
            driver_id: selectedDriver,
            dispatch_date: dispatchDate,
            invoice_ids: selectedInvoices,
            idempotency_key: idempotencyKeyRef.current
          })
        }
      );
      const data = await response.json();
      if (response.ok && data.success) { alert('Viaje creado exitosamente'); onRouteCreated(); }
      else if (response.status === 409) {
        const detail = data.detail || {};
        const affected = (detail.conflicting_invoice_ids || []);
        const labels = affected.map(id => {
          const inv = invoicesList.find(i => i.invoice_id === id);
          return inv ? (inv.invoice_number || id) : id;
        });
        alert('No se pudo crear el viaje. Estas facturas ya están asignadas a otro viaje o en conflicto:\n' + labels.join(', '));
      }
      else alert('Error al crear viaje: ' + (typeof data.detail === 'string' ? data.detail : (data.detail?.message || '')));
    } catch (error) {
      console.error('Error creating route:', error);
      alert('Error al crear viaje');
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  const totalAmount = invoicesList.reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4" data-testid="create-route-title">Crear Viaje de Entrega</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Viaje</label>
            <input type="text" value={routeName} onChange={(e) => setRouteName(e.target.value)}
              placeholder="Ej: Ruta Zona Este" data-testid="route-name-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" required />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chofer</label>
              <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)}
                data-testid="driver-select"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" required>
                <option value="">Seleccionar chofer</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Despacho</label>
              <input type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)}
                data-testid="dispatch-date-input"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" />
            </div>
          </div>
          <div className="mb-4 bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Facturas seleccionadas ({selectedInvoices.length})</p>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {invoicesList.map(inv => (
                <div key={inv.invoice_id} className="flex justify-between text-xs text-gray-600 py-1 border-b border-gray-100">
                  <span>{inv.invoice_number} - {inv.customer_name}</span>
                  <span className="font-medium">${parseFloat(inv.total || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm font-semibold">
              <span>Total:</span><span>${totalAmount.toFixed(2)}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Cancelar</button>
            <button type="submit" disabled={loading} data-testid="submit-route-btn"
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm">
              {loading ? 'Creando...' : 'Crear Viaje'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Driver Deliveries Section - For users with 'chofer' role
const DriverDeliveriesSection = ({ user }) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [expandedRoute, setExpandedRoute] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchData = async () => {
    try {
      setLoading(true);
      // Logistica role sees all routes, chofer sees only their own
      const url = user.role === 'logistica'
        ? `${backendUrl}/api/logistics/routes`
        : `${backendUrl}/api/driver/routes?driver_id=${user.id}`;
      const res = await fetch(url);
      const data = await res.json();
      setRoutes(data.routes || []);
    } catch (error) {
      console.error('Error fetching driver routes:', error);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [user.id]);

  const handleDeliveryClick = (delivery) => {
    setSelectedDelivery(delivery);
    setShowDeliveryModal(true);
  };

  const handleStartRoute = async (routeId) => {
    try {
      await fetch(`${backendUrl}/api/logistics/routes/${routeId}/start`, { method: 'PUT' });
      fetchData();
    } catch (e) { alert('Error al iniciar viaje'); }
  };

  const handleCloseRoute = async (routeId) => {
    if (!window.confirm('¿Cerrar este viaje? Asegurate que todas las entregas estan procesadas.')) return;
    try {
      await fetch(`${backendUrl}/api/logistics/routes/${routeId}/close`, { method: 'PUT' });
      fetchData();
    } catch (e) { alert('Error al cerrar viaje'); }
  };

  const getStatusBadge = (status) => {
    const map = {
      'assigned': { label: 'Asignada', cls: 'bg-blue-100 text-blue-800' },
      'in_transit': { label: 'En Ruta', cls: 'bg-indigo-100 text-indigo-800' },
      'delivered': { label: 'Entregada', cls: 'bg-green-100 text-green-800' },
      'failed': { label: 'No Entregada', cls: 'bg-red-100 text-red-800' },
      'rescheduled': { label: 'Reprogramada', cls: 'bg-orange-100 text-orange-800' }
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${s.cls}`}>{s.label}</span>;
  };

  const getRouteStatusBadge = (status) => {
    const map = {
      'pending': { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-800' },
      'in_progress': { label: 'En Ruta', cls: 'bg-blue-100 text-blue-800' },
      'completed': { label: 'Completado', cls: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'Cancelado', cls: 'bg-red-100 text-red-800' }
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${s.cls}`}>{s.label}</span>;
  };

  if (loading) return (
    <div className="p-6 bg-gray-50 min-h-screen flex justify-center items-center">
      <div className="text-center"><LoadingSpinner /><p className="mt-4 text-gray-600">Cargando entregas...</p></div>
    </div>
  );

  const activeRoutes = routes.filter(r => r.status !== 'completed');
  const completedRoutes = routes.filter(r => r.status === 'completed');

  return (
    <div className="p-4 lg:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-1" data-testid="driver-title">Mis Entregas</h1>
          <p className="text-gray-600">Gestiona tus viajes y entregas asignadas</p>
        </div>

        {/* Active Routes */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Viajes Activos ({activeRoutes.length})</h2>
          {activeRoutes.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-500">No tienes viajes activos</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeRoutes.map(route => {
                const pendingDels = (route.deliveries || []).filter(d => d.status === 'assigned' || d.status === 'in_transit');
                const doneDels = (route.deliveries || []).filter(d => ['delivered', 'failed', 'rescheduled'].includes(d.status));
                const allProcessed = pendingDels.length === 0 && doneDels.length > 0;

                return (
                  <div key={route.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedRoute(expandedRoute === route.id ? null : route.id)}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-base font-semibold text-gray-800">{route.route_name}</h3>
                            {getRouteStatusBadge(route.status)}
                          </div>
                          <p className="text-sm text-gray-600">
                            {route.dispatch_date && `Despacho: ${route.dispatch_date} | `}
                            Facturas: {route.delivery_count} | Entregadas: {route.delivered_count} | Pendientes: {route.pending_count}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {route.status === 'pending' && (
                            <button onClick={(e) => { e.stopPropagation(); handleStartRoute(route.id); }}
                              data-testid={`start-route-${route.id}`}
                              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700">
                              Iniciar Viaje
                            </button>
                          )}
                          {allProcessed && route.status !== 'completed' && (
                            <button onClick={(e) => { e.stopPropagation(); handleCloseRoute(route.id); }}
                              data-testid={`close-route-${route.id}`}
                              className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700">
                              Cerrar Viaje
                            </button>
                          )}
                          <span className="text-gray-400">{expandedRoute === route.id ? '\u25B2' : '\u25BC'}</span>
                        </div>
                      </div>
                    </div>

                    {expandedRoute === route.id && (
                      <div className="border-t border-gray-200 divide-y divide-gray-100">
                        {(route.deliveries || []).map(del_ => {
                          const canEdit = del_.status === 'assigned' || del_.status === 'in_transit';
                          return (
                            <div key={del_.id} className={`p-4 ${canEdit ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                              onClick={() => canEdit && handleDeliveryClick(del_)}>
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-sm">#{del_.invoice_number}</span>
                                    {getStatusBadge(del_.status)}
                                  </div>
                                  <p className="text-sm text-gray-800">{del_.customer_name}</p>
                                  {del_.address && <p className="text-xs text-gray-500">{del_.address}</p>}
                                  {del_.phone && <p className="text-xs text-gray-500">Tel: {del_.phone}</p>}
                                  <p className="text-xs text-gray-600 font-medium mt-1">${parseFloat(del_.amount || 0).toFixed(2)}</p>
                                </div>
                                {canEdit && (
                                  <span className="text-red-600 text-sm font-medium">Gestionar &rarr;</span>
                                )}
                                {del_.status === 'delivered' && del_.delivery_notes && (
                                  <p className="text-xs text-green-700 max-w-xs">{del_.delivery_notes}</p>
                                )}
                                {del_.status === 'failed' && del_.rejection_reason && (
                                  <p className="text-xs text-red-700 max-w-xs">{del_.rejection_reason}</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Completed Routes */}
        {completedRoutes.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Viajes Completados ({completedRoutes.length})</h2>
            <div className="space-y-3">
              {completedRoutes.map(route => (
                <div key={route.id} className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedRoute(expandedRoute === route.id ? null : route.id)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-800">{route.route_name}</h3>
                        {getRouteStatusBadge(route.status)}
                      </div>
                      <p className="text-xs text-gray-500">
                        {route.dispatch_date} | {route.delivery_count} facturas | Entregadas: {route.delivered_count}
                      </p>
                    </div>
                    <span className="text-gray-400">{expandedRoute === route.id ? '\u25B2' : '\u25BC'}</span>
                  </div>
                  {expandedRoute === route.id && (
                    <div className="mt-3 border-t pt-3 space-y-2">
                      {(route.deliveries || []).map(del_ => (
                        <div key={del_.id} className="flex items-center justify-between text-sm">
                          <span>#{del_.invoice_number} - {del_.customer_name}</span>
                          {getStatusBadge(del_.status)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delivery Detail Modal */}
        {showDeliveryModal && selectedDelivery && (
          <DeliveryDetailModal
            isOpen={showDeliveryModal}
            onClose={() => { setShowDeliveryModal(false); setSelectedDelivery(null); }}
            delivery={selectedDelivery}
            onDeliveryUpdated={fetchData}
          />
        )}
      </div>
    </div>
  );
};

// Delivery Detail Modal - For driver to update delivery status
const DeliveryDetailModal = ({ isOpen, onClose, delivery, onDeliveryUpdated }) => {
  const [status, setStatus] = useState('delivered');
  const [notes, setNotes] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [evidencePreviews, setEvidencePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    files.forEach(file => {
      setEvidenceFiles(prev => [...prev, file]);
      const reader = new FileReader();
      reader.onloadend = () => setEvidencePreviews(prev => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeEvidence = (index) => {
    setEvidenceFiles(prev => prev.filter((_, i) => i !== index));
    setEvidencePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'delivered' && evidenceFiles.length === 0) {
      alert('Por favor sube al menos una foto de evidencia para confirmar la entrega');
      return;
    }
    if ((status === 'failed' || status === 'rescheduled') && !rejectionReason) {
      alert('Por favor indica el motivo');
      return;
    }

    try {
      setLoading(true);

      // Upload all evidence photos
      for (const file of evidenceFiles) {
        const formData = new FormData();
        formData.append('file', file);
        await fetch(`${backendUrl}/api/driver/deliveries/${delivery.id}/evidence`, {
          method: 'POST', body: formData
        });
      }

      // Update delivery status
      const response = await fetch(`${backendUrl}/api/driver/deliveries/${delivery.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          delivery_notes: notes,
          received_by: receivedBy,
          rejection_reason: rejectionReason
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('Entrega actualizada exitosamente');
        onDeliveryUpdated();
        onClose();
      } else {
        alert('Error: ' + (data.detail || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error updating delivery:', error);
      alert('Error al actualizar la entrega');
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 my-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4" data-testid="delivery-modal-title">Gestionar Entrega</h2>

        {/* Delivery Info */}
        <div className="mb-4 pb-3 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-gray-500">Factura:</p><p className="font-semibold">#{delivery.invoice_number}</p></div>
            <div><p className="text-gray-500">Monto:</p><p className="font-semibold">${parseFloat(delivery.amount || 0).toFixed(2)}</p></div>
            <div className="col-span-2"><p className="text-gray-500">Cliente:</p><p className="font-semibold">{delivery.customer_name}</p></div>
            {delivery.address && <div className="col-span-2"><p className="text-gray-500">Direccion:</p><p className="font-semibold">{delivery.address}</p></div>}
            {delivery.phone && <div><p className="text-gray-500">Telefono:</p><p className="font-semibold">{delivery.phone}</p></div>}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado de Entrega</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="delivery-status-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm">
              <option value="delivered">Entregada</option>
              <option value="failed">No Entregada</option>
              <option value="rescheduled">Reprogramada</option>
            </select>
          </div>

          {status === 'delivered' && (
            <>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Recibido Por</label>
                <input type="text" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)}
                  placeholder="Nombre de quien recibio" data-testid="received-by-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Fotos de Evidencia * ({evidenceFiles.length})</label>
                <div className="flex gap-2 mb-2">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><circle cx="12" cy="13" r="3"/></svg>
                      Tomar Foto
                    </div>
                    <input type="file" accept="image/*" capture="environment" onChange={handleFileChange}
                      className="hidden" />
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                      Galeria
                    </div>
                    <input type="file" accept="image/*" multiple onChange={handleFileChange}
                      data-testid="evidence-photo-input"
                      className="hidden" />
                  </label>
                </div>
                {evidencePreviews.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {evidencePreviews.map((preview, idx) => (
                      <div key={idx} className="relative">
                        <img src={preview} alt={`Evidencia ${idx + 1}`} className="h-20 w-20 object-cover rounded border" />
                        <button type="button" onClick={() => removeEvidence(idx)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center shadow">&times;</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {(status === 'failed' || status === 'rescheduled') && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo *</label>
              <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explica el motivo..." rows="3" data-testid="rejection-reason-input"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" required />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Comentarios adicionales..." rows="2" data-testid="delivery-notes-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm" />
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Cancelar</button>
            <button type="submit" disabled={loading} data-testid="submit-delivery-btn"
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm">
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export const VenegeSalesApp = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [shouldOpenNewPaymentModal, setShouldOpenNewPaymentModal] = useState(false);
  const [embedNonce, setEmbedNonce] = useState(0);
  const embedRef = useRef(null);

  // Header sizing
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(64);

  useEffect(() => {
    const node = headerRef.current;
    if (!node || typeof window === 'undefined') return;
    const update = () => setHeaderHeight(node.offsetHeight || 64);
    update();
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => update());
      ro.observe(node);
      return () => ro.disconnect();
    } else {
      const id = setInterval(update, 1000);
      return () => clearInterval(id);
    }
  }, []);

  // Analytics embed sizing and refresh
  const analyticsActionsRef = useRef(null);
  const [analyticsIframeHeight, setAnalyticsIframeHeight] = useState(600);
  const [panelReloadKey, setPanelReloadKey] = useState(0);
  const [panelTab, setPanelTab] = useState('ventas');

  // Power BI Sales dashboard mapping (email → URL)
  const powerBiSalesMap = {
    'adriana@venegeca.com': 'https://app.powerbi.com/view?r=eyJrIjoiYzc2YzExOGEtNGZjNS00OGEwLTg3NDgtODk0YzVmMGNmMzVhIiwidCI6ImU5OGY5ODBjLTI4MWUtNDBhZS05MjAyLTA1YWIxNjE4Mzc2MyIsImMiOjJ9',
    'roilan@venegeca.com': 'https://app.powerbi.com/view?r=eyJrIjoiODNiNjI0Y2YtZmFhMi00YTNlLTgzMDYtOWVmOGVhZGI4NzYwIiwidCI6ImU5OGY5ODBjLTI4MWUtNDBhZS05MjAyLTA1YWIxNjE4Mzc2MyIsImMiOjJ9',
    'mfiguera@venegeca.com': 'https://app.powerbi.com/view?r=eyJrIjoiNTI0ZTNmOWItMWViYi00MThlLThjZTItNDE4ZjE1MzFhYTcyIiwidCI6ImU5OGY5ODBjLTI4MWUtNDBhZS05MjAyLTA1YWIxNjE4Mzc2MyIsImMiOjJ9',
    'a.manrique@venegeca.com': 'https://app.powerbi.com/view?r=eyJrIjoiOWRiNGYyZjctNmNiOC00NTY3LTkyNjgtNWNiMjE0YzBiYTEwIiwidCI6ImU5OGY5ODBjLTI4MWUtNDBhZS05MjAyLTA1YWIxNjE4Mzc2MyIsImMiOjJ9',
  };
  const userPowerBiUrl = user ? powerBiSalesMap[user.email] : null;

  const recomputeAnalyticsHeight = useCallback(() => {
    if (typeof window === 'undefined') return;
    const actionsH = analyticsActionsRef.current ? analyticsActionsRef.current.offsetHeight : 0;
    const extra = 32; // padding allowance
    const h = Math.max(300, (window.innerHeight || 900) - (headerHeight || 64) - actionsH - extra);
    setAnalyticsIframeHeight(h);
  }, [headerHeight]);

  useEffect(() => {
    recomputeAnalyticsHeight();
    if (typeof window === 'undefined') return;
    const onResize = () => recomputeAnalyticsHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeAnalyticsHeight]);

  // Global New Payment modal from analytics panel
  const [showNewPaymentModalGlobal, setShowNewPaymentModalGlobal] = useState(false);
  const [showCrmModal, setShowCrmModal] = useState(false);
  const [showClientDetailModal, setShowClientDetailModal] = useState(false);
  const [preSelectedClient, setPreSelectedClient] = useState(null);
  const [paymentContacts, setPaymentContacts] = useState([]);
  const [vendedorTasks, setVendedorTasks] = useState([]);
  const [loadingVendedorTasks, setLoadingVendedorTasks] = useState(false);
  const [expandedVendedorTask, setExpandedVendedorTask] = useState(null);
  const [vendedorTaskNote, setVendedorTaskNote] = useState('');
  const [vendedorTaskNoteFile, setVendedorTaskNoteFile] = useState(null);
  const [vendedorTaskNotePreview, setVendedorTaskNotePreview] = useState(null);
  const [addingVendedorNote, setAddingVendedorNote] = useState(false);
  const [gcPending, setGcPending] = useState([]);
  const [loadingGcPending, setLoadingGcPending] = useState(false);
  const [gcPendingError, setGcPendingError] = useState('');
  const [gcClosingId, setGcClosingId] = useState(null);
  const [gcConfirmClose, setGcConfirmClose] = useState(null);
  const [gcCloseError, setGcCloseError] = useState('');

  const fetchGcPending = useCallback(async () => {
    if (!user) return;
    setLoadingGcPending(true); setGcPendingError('');
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('access_token');
      const resp = await fetch(`${backendUrl}/api/gestion-cliente/pending`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) { setGcPendingError('No se pudieron cargar las tareas. Reintentar.'); return; }
      const data = await resp.json();
      setGcPending(data.data || []);
    } catch (e) {
      console.error('Error fetching gestion pending:', e);
      setGcPendingError('No se pudieron cargar las tareas. Reintentar.');
    } finally { setLoadingGcPending(false); }
  }, [user]);

  useEffect(() => { if (user) fetchGcPending(); }, [user, fetchGcPending]);

  const closeGcTask = async (item) => {
    setGcClosingId(item.id); setGcCloseError('');
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('access_token');
      const resp = await fetch(`${backendUrl}/api/gestion-cliente/${item.id}/close`, {
        method: 'POST', credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json();
      if (data.success) {
        setGcPending(prev => prev.filter(t => t.id !== item.id));
        setGcConfirmClose(null);
      } else {
        setGcCloseError(data.message || 'No se pudo actualizar ClickUp. Intenta nuevamente.');
      }
    } catch (e) {
      setGcCloseError('No se pudo actualizar ClickUp. Intenta nuevamente.');
    } finally { setGcClosingId(null); }
  };

  const fetchPaymentContacts = useCallback(async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      let allContacts = [];
      let currentPageNum = 1;
      let hasMorePages = true;
      while (hasMorePages && currentPageNum <= 30) {
        // Build URL with user filtering params
        const params = new URLSearchParams({
          page: currentPageNum,
          per_page: 100,
          user_role: user.role,
          user_name: user.name
        });
        
        const response = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`, {
          method: 'GET',
          credentials: 'include'
        });
        if (!response.ok) break;
        const data = await response.json();
        const pageContacts = data?.data?.contacts || [];
        if (pageContacts.length === 0) {
          hasMorePages = false;
          break;
        }
        allContacts = [...allContacts, ...pageContacts];
        
        // Check if there are more pages
        const hasMorePageFlag = data?.data?.page_context?.has_more_page || false;
        hasMorePages = hasMorePageFlag && pageContacts.length === 100;
        currentPageNum += 1;
      }
      setPaymentContacts(allContacts);
    } catch (e) {
      console.warn('Failed to fetch contacts for payment modal', e);
    }
  }, [user]);

  useEffect(() => {
    if (showNewPaymentModalGlobal && paymentContacts.length === 0) {
      fetchPaymentContacts();
    }
  }, [showNewPaymentModalGlobal, paymentContacts.length, fetchPaymentContacts]);

  // Fetch tasks for vendedora panel
  const fetchVendedorTasks = useCallback(async () => {
    if (!user) return;
    setLoadingVendedorTasks(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const taskResp = await fetch(`${backendUrl}/api/bigin/tasks`);
      const taskData = await taskResp.json();
      if (!taskData.success) { setVendedorTasks([]); return; }
      let allTasks = (taskData.data || []).filter(t => t.Status !== 'Completed');

      // Admin and Orlando see all tasks
      if (user.role === 'admin' || user.name === 'Orlando') {
        setVendedorTasks(allTasks);
        return;
      }

      // Vendedora: filter by their contacts (fetch all pages)
      if (user.role === 'vendedora') {
        let allContacts = [];
        let pg = 1;
        let hasMore = true;
        while (hasMore && pg <= 30) {
          const params = new URLSearchParams({ page: pg, per_page: 200, user_role: user.role, user_name: user.name });
          const cResp = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`, { credentials: 'include' });
          const cData = await cResp.json();
          const pageContacts = cData?.data?.contacts || [];
          if (pageContacts.length === 0) break;
          allContacts = [...allContacts, ...pageContacts];
          hasMore = (cData?.data?.page_context?.has_more_page || false) && pageContacts.length === 200;
          pg++;
        }
        const clientNames = new Set(allContacts.map(c => (c.contact_name || '').toUpperCase()));
        allTasks = allTasks.filter(t => {
          if (!t.Related_To?.name) return true;
          return clientNames.has(t.Related_To.name.toUpperCase());
        });
      }
      setVendedorTasks(allTasks);
    } catch (e) { console.error('Error fetching vendedor tasks:', e); }
    finally { setLoadingVendedorTasks(false); }
  }, [user]);

  useEffect(() => {
    if (user && user.role === 'vendedora') {
      fetchVendedorTasks();
    }
  }, [user, fetchVendedorTasks]);

  const completeVendedorTask = async (taskId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backendUrl}/api/bigin/tasks/${taskId}/complete`, { method: 'PUT' });
      const data = await resp.json();
      if (data.success) {
        setVendedorTasks(prev => prev.filter(t => t.id !== taskId));
        setExpandedVendedorTask(null);
      }
    } catch (e) { console.error(e); }
  };

  const addNoteToVendedorTask = async (task) => {
    if (!vendedorTaskNote.trim() && !vendedorTaskNoteFile) return;
    setAddingVendedorNote(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      // If there's a file, upload to Bigin first
      let fileRef = '';
      if (vendedorTaskNoteFile && task.Related_To?.id) {
        const formData = new FormData();
        formData.append('file', vendedorTaskNoteFile);
        const uploadResp = await fetch(`${backendUrl}/api/bigin/attachments/Accounts/${task.Related_To.id}`, { method: 'POST', body: formData });
        const uploadData = await uploadResp.json();
        if (uploadData.success) fileRef = `\n📎 Archivo adjunto: ${vendedorTaskNoteFile.name}`;
      }
      // Create note
      const noteContent = `[Tarea: ${task.Subject}]\n${vendedorTaskNote}${fileRef}\n- ${user.name}`;
      const noteResp = await fetch(`${backendUrl}/api/bigin/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          module: 'Tasks',
          record_id: task.id,
          note_title: `Nota - ${task.Subject}`,
          note_content: noteContent
        })
      });
      const noteData = await noteResp.json();
      if (noteData.success) {
        setVendedorTaskNote('');
        setVendedorTaskNoteFile(null);
        setVendedorTaskNotePreview(null);
      }
    } catch (e) { console.error('Error adding note:', e); }
    finally { setAddingVendedorNote(false); }
  };

  useEffect(() => {
    // Check if user is logged in
    const savedUser = sessionStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        // Set default section based on permissions
        const perms = userData.permissions || [];
        if (perms.length > 0) {
          handleSectionChange(perms[0]);
        } else {
          // Fallback defaults by role
          const roleDefaults = { aprobador: 'payments', logistica: 'logistics', chofer: 'driverDeliveries', vendedora: 'analyticsEmbed' };
          handleSectionChange(roleDefaults[userData.role] || 'dashboard');
        }
      } catch (e) { sessionStorage.removeItem('currentUser'); }
    }
  }, []);

  const [authChecking, setAuthChecking] = useState(true);

  // Check for Google OAuth callback (session_id in URL hash)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        const processGoogleAuth = async () => {
          try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const resp = await fetch(`${backendUrl}/api/auth/google-callback`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ session_id: sessionId })
            });
            const data = await resp.json();
            if (data.success && data.user) {
              handleLogin(data.user);
            }
          } catch (e) {
            console.error('Google auth error:', e);
          }
          // Clear hash
          window.history.replaceState(null, '', window.location.pathname);
          setAuthChecking(false);
        };
        processGoogleAuth();
        return;
      }
    }

    // Check existing session from sessionStorage
    const savedUser = sessionStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (e) { sessionStorage.removeItem('currentUser'); }
    }
    setAuthChecking(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    sessionStorage.setItem('currentUser', JSON.stringify(userData));
    // Set default section based on permissions
    const perms = userData.permissions || [];
    if (perms.length > 0) {
      handleSectionChange(perms[0]);
    } else {
      const roleDefaults = { aprobador: 'payments', logistica: 'logistics', chofer: 'driverDeliveries', vendedora: 'analyticsEmbed' };
      handleSectionChange(roleDefaults[userData.role] || 'dashboard');
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (e) { /* ignore */ }
    setUser(null);
    sessionStorage.removeItem('currentUser');
    sessionStorage.removeItem('access_token');
    handleSectionChange('dashboard');
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Custom function to handle section changes with parameters
  const handleSectionChange = (section, options = {}) => {
    console.log('handleSectionChange called with:', section, options);
    setActiveSection(section);
    
    // Handle special options with a slight delay to ensure section changes first
    if (options.openNewPaymentModal) {
      console.log('Setting shouldOpenNewPaymentModal to true');
      setTimeout(() => {
        setShouldOpenNewPaymentModal(true);
      }, 100);
    }
  };

  // Auto-logout system - 20 minutes of inactivity
  const { showWarning, extendSession } = useAutoLogout(handleLogout, 20);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && window.innerWidth < 768) {
        const sidebar = document.querySelector('.sidebar-container');
        if (sidebar && !sidebar.contains(event.target)) {
          closeSidebar();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  // Close sidebar on window resize if screen becomes large
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (authChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
          <p>Verificando sesion...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <UnifiedDashboard
          onSectionChange={handleSectionChange}
          onNewOrderClick={() => setShowNewOrderModal(true)}
          user={user}
          shouldOpenNewPaymentModal={shouldOpenNewPaymentModal}
          onModalStateChange={setShouldOpenNewPaymentModal}
        />;
      case 'analytics':
        return <AnalyticsSection user={user} />;
      case 'analyticsEmbed':
        const embedUrl = user.name === 'Adriana Martinez'
          ? 'https://analytics.zoho.com/open-view/2850285000000949820'
          : user.name === 'Roilan Narvaez'
          ? 'https://analytics.zoho.com/open-view/2850285000000977519/45d136bfbc195fb5eda7cd17ce56cb95'
          : user.name === 'Andrea Manrique'
          ? 'https://analytics.zoho.com/open-view/2850285000000978932/ea7412a08b07562ce5e86949daf0c61a'
          : user.name === 'Carlos Abril'
          ? 'https://analytics.zoho.com/open-view/2850285000004078351/1dc521c24e70d1e4ffab52cce07ed6ce69e3b1a3f531460cbe6e32dffd96d451'
          : user.name === 'Kristian Madrid'
          ? 'https://analytics.zoho.com/open-view/2850285000004313338/c4f7e4dff63b149816d194ea654806ec'
          : user.name === 'Orlando'
          ? 'https://analytics.zoho.com/open-view/2850285000008623015/cf448a83bb4cdbd2ee046a343604eef10f3b635a49084e45b8e7c1e8843d59ec'
          : 'https://analytics.zoho.com/open-view/2850285000000921001/19a281b1d342645cbc3b59eb5741244c';
        return (
          <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
              <div className="mb-4">
                <h1 className="text-3xl font-bold text-gray-800">Panel</h1>
              </div>

              <div ref={analyticsActionsRef} className="bg-white rounded-lg shadow-md p-6 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">🚀 Acciones Rápidas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setShowClientDetailModal(true)}
                    className="p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
                    data-testid="btn-detalle-cliente"
                  >
                    <FileIcon />
                    <span className="block text-sm mt-2">Detalle Cliente</span>
                  </button>
                  <button
                    onClick={() => setShowCrmModal(true)}
                    className="p-4 min-h-[56px] bg-[#C82432] text-white rounded-lg hover:bg-[#A51D28] transition-colors text-center"
                    data-testid="btn-gestion-cliente"
                  >
                    <UsersIcon />
                    <span className="block text-sm mt-2 font-semibold leading-tight">Nueva gestión de cliente</span>
                    <span className="block text-[11px] mt-0.5 text-white/80 leading-tight">Registrar visita, seguimiento o cobranza</span>
                  </button>
                  <button
                    onClick={() => setPanelReloadKey((k) => k + 1)}
                    className="p-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-center col-span-2"
                    data-testid="btn-actualizar-panel"
                  >
                    <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 10-6.32 12.906"/></svg>
                    <span className="block text-sm mt-2">Actualizar Panel</span>
                  </button>
                </div>
              </div>

              {/* Tareas Pendientes — Gestión Cliente */}
              <div className="bg-white rounded-lg shadow-md p-4 mb-4" data-testid="gc-pending-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-gray-800">Tareas Pendientes ({gcPending.length})</h3>
                  <button onClick={fetchGcPending} data-testid="gc-pending-refresh" className="text-xs text-gray-500 hover:text-gray-700">
                    <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 10-6.32 12.906"/></svg>
                  </button>
                </div>
                {loadingGcPending ? (
                  <div className="text-sm text-gray-400 text-center py-4">Cargando tareas...</div>
                ) : gcPendingError ? (
                  <div className="text-center py-4" data-testid="gc-pending-error">
                    <p className="text-sm text-red-600">{gcPendingError}</p>
                    <button onClick={fetchGcPending} className="mt-2 text-xs px-3 py-1.5 bg-[#C82432] text-white rounded-lg hover:bg-[#A51D28]">Reintentar</button>
                  </div>
                ) : gcPending.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4" data-testid="gc-pending-empty">No hay tareas pendientes para este usuario.</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {gcPending.map(item => {
                      const todayStr = new Date().toLocaleDateString('en-CA');
                      const overdue = item.follow_up_date && item.follow_up_date < todayStr;
                      const urgColors = { critica: 'bg-red-100 text-red-700', alta: 'bg-orange-100 text-orange-700', normal: 'bg-yellow-100 text-yellow-700', baja: 'bg-gray-100 text-gray-600' };
                      return (
                        <div key={item.id} className={`rounded-lg border p-3 ${overdue ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} data-testid={`gc-pending-item-${item.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-800 truncate">{item.next_step}</div>
                              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                <span className="text-xs text-blue-600 font-medium truncate max-w-[200px]">{item.customer_name}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${urgColors[item.urgency] || 'bg-gray-100 text-gray-600'}`}>{item.urgency === 'critica' ? 'Crítica' : item.urgency === 'alta' ? 'Alta' : item.urgency === 'baja' ? 'Baja' : 'Normal'}</span>
                                {item.follow_up_date && (
                                  <span className={`text-xs ${overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{overdue ? 'Vencida: ' : 'Compromiso: '}{item.follow_up_date}</span>
                                )}
                                {item.needs_support && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">Escalado</span>}
                                {item.partial && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Sinc. parcial</span>}
                                {item.sync_status !== 'synced' && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Sinc. pendiente</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2">
                            {item.clickup_task_url && (
                              <a href={item.clickup_task_url} target="_blank" rel="noreferrer"
                                className="text-xs px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">Abrir</a>
                            )}
                            <button onClick={() => { setGcCloseError(''); setGcConfirmClose(item); }}
                              data-testid={`gc-close-task-${item.id}`}
                              className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Cerrar tarea</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Tareas CRM (Bigin) */}
              <div className="bg-white rounded-lg shadow-md p-4 mb-4" data-testid="vendedor-tasks-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-gray-800">Tareas CRM Bigin ({vendedorTasks.length})</h3>
                  <button onClick={fetchVendedorTasks} className="text-xs text-gray-500 hover:text-gray-700">
                    <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 10-6.32 12.906"/></svg>
                  </button>
                </div>
                {loadingVendedorTasks ? (
                  <div className="text-sm text-gray-400 text-center py-4">Cargando tareas...</div>
                ) : vendedorTasks.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4">No hay tareas CRM pendientes</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {vendedorTasks.map(task => {
                      const isOverdue = task.Due_Date && new Date(task.Due_Date) < new Date();
                      const isExpanded = expandedVendedorTask === task.id;
                      const priorityColors = {
                        'High': 'bg-red-100 text-red-700',
                        'Highest': 'bg-red-200 text-red-800',
                        'Normal': 'bg-yellow-100 text-yellow-700',
                        'Low': 'bg-gray-100 text-gray-600'
                      };
                      const pColor = priorityColors[task.Priority] || 'bg-gray-100 text-gray-600';
                      return (
                        <div key={task.id} className={`rounded-lg border transition-all ${isExpanded ? 'border-yellow-400 shadow-md' : isOverdue ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                          {/* Task Row - clickable */}
                          <button
                            onClick={() => { setExpandedVendedorTask(isExpanded ? null : task.id); setVendedorTaskNote(''); setVendedorTaskNoteFile(null); setVendedorTaskNotePreview(null); }}
                            className="w-full text-left p-3 flex items-center justify-between hover:bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-800 truncate">{task.Subject}</div>
                              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                {task.Related_To?.name && <span className="text-xs text-blue-600 font-medium truncate max-w-[180px]">{task.Related_To.name}</span>}
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${pColor}`}>{task.Priority || 'Normal'}</span>
                                {task.Due_Date && (
                                  <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                    {isOverdue ? 'Vencida: ' : ''}{task.Due_Date}
                                  </span>
                                )}
                              </div>
                            </div>
                            <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                          </button>

                          {/* Expanded Detail */}
                          {isExpanded && (
                            <div className="px-3 pb-3 border-t border-gray-100 space-y-3">
                              <div className="grid grid-cols-2 gap-2 pt-2">
                                <div>
                                  <span className="text-xs text-gray-400">Estado</span>
                                  <div className="text-sm font-medium text-gray-700">{task.Status}</div>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-400">Prioridad</span>
                                  <div className={`text-sm font-medium ${pColor.split(' ')[1]}`}>{task.Priority || 'Normal'}</div>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-400">Vencimiento</span>
                                  <div className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>{task.Due_Date}</div>
                                </div>
                                {task.Related_To?.name && (
                                  <div>
                                    <span className="text-xs text-gray-400">Cliente</span>
                                    <div className="text-sm font-medium text-gray-700 truncate">{task.Related_To.name}</div>
                                  </div>
                                )}
                              </div>
                              {task.Description && (
                                <div>
                                  <span className="text-xs text-gray-400">Descripcion</span>
                                  <div className="text-sm text-gray-600 whitespace-pre-wrap">{task.Description}</div>
                                </div>
                              )}

                              {/* Add Note */}
                              <div>
                                <label className="text-xs text-gray-400">Agregar Nota</label>
                                <div className="flex gap-2 mt-1">
                                  <input type="text" value={vendedorTaskNote} onChange={e => setVendedorTaskNote(e.target.value)}
                                    placeholder="Escribe una nota..."
                                    className="flex-1 px-2 py-1.5 border rounded-lg text-sm" />
                                  <button onClick={() => addNoteToVendedorTask(task)} disabled={addingVendedorNote}
                                    className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50 whitespace-nowrap">
                                    {addingVendedorNote ? '...' : 'Enviar'}
                                  </button>
                                </div>
                                <div className="mt-1.5">
                                  <input type="file" accept="image/*" onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) { setVendedorTaskNoteFile(file); setVendedorTaskNotePreview(URL.createObjectURL(file)); }
                                  }}
                                    className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" />
                                  {vendedorTaskNotePreview && (
                                    <div className="mt-1 relative inline-block">
                                      <img src={vendedorTaskNotePreview} alt="Preview" className="h-16 rounded border" />
                                      <button onClick={() => { setVendedorTaskNoteFile(null); setVendedorTaskNotePreview(null); }}
                                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">&times;</button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Complete Button */}
                              <button onClick={() => completeVendedorTask(task.id)}
                                className="w-full py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 font-medium">
                                Marcar como Completada
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Ventas / Cobranzas tabs for Power BI users, plain Zoho embed for others */}
              {userPowerBiUrl ? (
                <>
                  <div className="bg-white rounded-lg shadow-md mb-4 overflow-hidden">
                    <div className="flex border-b border-gray-200">
                      <button
                        onClick={() => setPanelTab('ventas')}
                        data-testid="panel-tab-ventas"
                        className={`flex-1 py-3 px-4 text-sm font-medium text-center transition-colors ${panelTab === 'ventas' ? 'border-b-2 border-red-600 text-red-600 bg-white' : 'text-gray-500 hover:text-gray-700 bg-gray-50'}`}
                      >Ventas</button>
                      <button
                        onClick={() => setPanelTab('cobranzas')}
                        data-testid="panel-tab-cobranzas"
                        className={`flex-1 py-3 px-4 text-sm font-medium text-center transition-colors ${panelTab === 'cobranzas' ? 'border-b-2 border-red-600 text-red-600 bg-white' : 'text-gray-500 hover:text-gray-700 bg-gray-50'}`}
                      >Cobranzas</button>
                    </div>
                    <div className="p-2">
                      <div className="relative w-full" style={{height: `${analyticsIframeHeight}px`}}>
                        {panelTab === 'ventas' ? (
                          <iframe
                            key={`pbi-${panelReloadKey}`}
                            title="Ventas - Power BI"
                            frameBorder="0"
                            allowFullScreen
                            className="absolute inset-0 w-full h-full"
                            style={{border: 'none', overflow: 'hidden'}}
                            src={userPowerBiUrl}
                            data-testid="powerbi-sales-iframe"
                            onError={() => {}}
                          />
                        ) : (
                          <iframe
                            key={`zoho-cobr-${panelReloadKey}`}
                            title="Cobranzas"
                            frameBorder="0"
                            className="absolute inset-0 w-full h-full"
                            src={embedUrl}
                            data-testid="zoho-cobranzas-iframe"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : embedUrl ? (
              <div className="bg-white rounded-lg shadow-md p-2 overflow-auto">
                <div className="w-full flex justify-center">
                  <div className="relative w-full" style={{height: `${analyticsIframeHeight}px`}}>
                    <iframe
                      key={panelReloadKey}
                      title="Panel"
                      frameBorder="0"
                      className="absolute inset-0 w-full h-full"
                      src={embedUrl}
                    />
                  </div>
                </div>
              </div>
              ) : null}
            </div>
          </div>
        );
      case 'salesOrders':
        return <SalesOrdersSection user={user} />;
      case 'clients':
        return <ClientsSection user={user} />;
      case 'inventory':
        return <InventorySection />;
      case 'invoices':
        return <InvoicesSection user={user} />;
      case 'payments':
        return <PaymentsSection user={user} shouldOpenNewModal={shouldOpenNewPaymentModal} onModalStateChange={setShouldOpenNewPaymentModal} />;
      case 'creditNotes':
        return <CreditNotesSection user={user} />;
      case 'users':
        return user.role === 'admin' ? <UsersSection /> : (
          <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                🚗 VENEGE - Panel de Control Integral
              </h1>
              <p className="text-gray-600 mb-4">
                Dashboard unificado - Usuario: {user.name} ({user.role})
              </p>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Acceso Restringido</h2>
                <p>No tienes permisos para ver la sección de usuarios.</p>
              </div>
            </div>
          </div>
        );
      case 'logistics':
        return <LogisticsSection user={user} />;
      case 'driverDeliveries':
        return <DriverDeliveriesSection user={user} />;
      default:
        return (
          <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                🚗 VENEGE - Panel de Control Integral
              </h1>
              <p className="text-gray-600 mb-4">
                Dashboard unificado funcionando - Usuario: {user.name} ({user.role})
              </p>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Panel de Control Principal</h2>
                <p>Bienvenido al sistema unificado de Venege.</p>
                <div className="mt-4 space-x-4">
                  <button
                    onClick={() => handleSectionChange('analytics')}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Ir a Analytics
                  </button>
                  <button
                    onClick={() => handleSectionChange('salesOrders')}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Ver Órdenes de Venta
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <div className="sidebar-container">
        <Sidebar
          activeSection={activeSection}
          setActiveSection={handleSectionChange}
          user={user}
          isOpen={sidebarOpen}
          onClose={closeSidebar}
        />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header user={user} onLogout={handleLogout} onToggleSidebar={toggleSidebar} containerRef={headerRef} />
        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          {renderActiveSection()}
        </main>

        {showNewPaymentModalGlobal && (
          <NewPaymentModal
            isOpen={showNewPaymentModalGlobal}
            onClose={() => { setShowNewPaymentModalGlobal(false); setPreSelectedClient(null); }}
            onPaymentCreated={() => { setShowNewPaymentModalGlobal(false); setPreSelectedClient(null); }}
            user={user}
            contacts={paymentContacts}
            preSelectedClient={preSelectedClient}
          />
        )}
        {showCrmModal && (
          <GestionClienteModal
            isOpen={showCrmModal}
            onClose={() => { setShowCrmModal(false); setPreSelectedClient(null); fetchGcPending(); }}
            user={user}
            preSelectedClient={preSelectedClient}
          />
        )}
        {gcConfirmClose && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="gc-close-confirm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4">
              <h3 className="text-base font-bold text-gray-900">Cerrar gestión</h3>
              <p className="text-sm text-gray-600">¿Deseas cerrar esta gestión? Esta acción conservará el historial y marcará la actividad como completada.</p>
              <p className="text-xs text-gray-400 truncate">{gcConfirmClose.customer_name} · {gcConfirmClose.next_step}</p>
              {gcCloseError && <p className="text-xs text-red-600" data-testid="gc-close-error">{gcCloseError}</p>}
              <div className="space-y-2">
                <button onClick={() => closeGcTask(gcConfirmClose)} disabled={gcClosingId === gcConfirmClose.id}
                  data-testid="gc-close-confirm-btn"
                  className="w-full py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors">
                  {gcClosingId === gcConfirmClose.id ? 'Cerrando...' : 'Sí, cerrar gestión'}
                </button>
                <button onClick={() => { setGcConfirmClose(null); setGcCloseError(''); }}
                  data-testid="gc-close-cancel-btn"
                  className="w-full py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Cancelar</button>
              </div>
            </div>
          </div>
        )}
        {showClientDetailModal && (
          <ClientDetailModal
            isOpen={showClientDetailModal}
            onClose={() => setShowClientDetailModal(false)}
            user={user}
            onOpenOrder={(contact) => { setPreSelectedClient(contact); setShowClientDetailModal(false); setShowNewOrderModal(true); }}
            onOpenPayment={(contact) => { setPreSelectedClient(contact); setShowClientDetailModal(false); setShowNewPaymentModalGlobal(true); }}
            onOpenCrm={(contact) => { setPreSelectedClient(contact); setShowClientDetailModal(false); setShowCrmModal(true); }}
          />
        )}
      </div>

      {/* Inactivity Warning Modal */}
      {showWarning && (
        <InactivityWarningModal
          onExtend={extendSession}
          onLogout={handleLogout}
        />
      )}

      {/* New Order Modal */}
      <NewOrderModal
        isOpen={showNewOrderModal}
        onClose={() => { setShowNewOrderModal(false); setPreSelectedClient(null); }}
        onOrderCreated={() => {
          setShowNewOrderModal(false);
          setPreSelectedClient(null);
          // If we're in sales orders section, refresh the data
          if (activeSection === 'salesOrders') {
            setPanelReloadKey((k) => k + 1);
          }
        }}
        user={user}
        preSelectedClient={preSelectedClient}
      />
    </div>
  );
};